// Offertes.js
// vOFF-054
// - Ter akkoord-knop wordt uitgegrijsd als relatie geen akkoord via WhatsApp/E-mail toestaat
// - WhatsApp opent op mobiel directer via de WhatsApp-app-link
// - E-mailmelding toont nu duidelijker het serverresultaat
// - Digitale akkoordmodule gekoppeld aan Offertes/Opdrachten
// - Knop "Ter akkoord" in voorbeeldpopup
// - Voorkeur WhatsApp/E-mail vanuit relatie wordt gebruikt
// - FIX: selectedInvoiceRow vóór gebruik geïnitialiseerd
// - Akkoordmelding wordt ook gewist bij Opslaan en Verwijderen
// - Offerteregels kunnen in de popup worden versleept/verplaatst
//
// Layout + UX afgestemd op Organisaties/Contactpersonen/Notities:
// - Links: zoek + knoppen
// - Midden: overzicht (met kolomfilters per kolom)
// - Rechts: details geselecteerde offerte
//
// Belangrijk:
// - Voor adres op offerte: correspondentieadres van de organisatie (ophalen via /api/organisations/:id)
//
// Verwachtte API's (server.js):
// - GET    /api/offers?q=...
// - GET    /api/offers/:id
// - POST   /api/offers
// - PUT    /api/offers/:id
// - DELETE /api/offers/:id
// - GET    /api/organisations
// - GET    /api/organisations/:id
// - GET    /api/contacts?organisation_id=...

import React, { useEffect, useMemo, useRef, useState } from "react";
import useVoiceCommands from "../components/useVoiceCommands";

// ===== OFFERTEREGELS POPUP =====
function makeEmptyOfferLine() {
  return {
    line_type: "Artikel",
    qty: "",
    description: "",
    price: "",
    vat: "21",
  };
}

const buildUrl = (u) =>
  u.startsWith("http") ? u : `${u.startsWith("/") ? u : `/${u}`}`;

// ---------------- helpers ----------------
function getParam(name) {
  try {
    const url = new URL(window.location.href);
    return url.searchParams.get(name) || "";
  } catch {
    return "";
  }
}

function norm(s) {
  return String(s || "")
    .toLowerCase()
    .trim()
    .replace(/[，、]/g, ",")
    .replace(/[“”]/g, '"')
    .replace(/[‘’]/g, "'")
    .replace(/[;:!?]/g, "")
    .replace(/\s+/g, " ");
}

function safeTrim(s) {
  return String(s || "").trim();
}

function isEmptyWord(t) {
  const x = norm(t);
  return x === "geen" || x === "blanco" || x === "leeg" || x === "null";
}

async function tryFetchJson(urls, options = {}) {
  const token =
    sessionStorage.getItem("token") ||
    localStorage.getItem("token") ||
    "";

  const tenant =
    sessionStorage.getItem("tenant") ||
    localStorage.getItem("tenant") ||
    "demo";

  let parsedUser = {};
  try {
    parsedUser = JSON.parse(
      sessionStorage.getItem("user") ||
      localStorage.getItem("user") ||
      "{}"
    );
  } catch {}

  const role = parsedUser?.role || "user";

  const companyCodeIds =
    sessionStorage.getItem("staffiq_company_code_ids") ||
    localStorage.getItem("staffiq_company_code_ids") ||
    "";

  let lastErr;
  for (const url of urls) {
    try {
      const headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        "x-tenant": tenant,
        "x-role": role,
        "x-staffiq-user-id": parsedUser?.id || undefined,
        "x-staffiq-user-email": parsedUser?.email || undefined,
        "x-staffiq-user-name": parsedUser?.display_name || parsedUser?.name || undefined,
        "x-company-code-ids": companyCodeIds || undefined,
      };

      Object.keys(headers).forEach(
        (k) => headers[k] === undefined && delete headers[k]
      );

      const res = await fetch(buildUrl(url), { ...options, headers });
      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}${msg ? ` - ${msg}` : ""}`);
      }
      const ct = res.headers.get("content-type") || "";
      if (ct.includes("application/json")) return await res.json();
      return await res.text();
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr || new Error("Failed to fetch");
}

function normalizePhoneForCommunication(raw, defaultCountryCode = "+31") {
  let s = String(raw || "").trim();

  if (!s || s === "-") return "";

  s = s
    .replace(/[^\d+]/g, "")
    .replace(/^00/, "+");

  if (!s) return "";

  if (s.startsWith("+")) return s;

  if (s.startsWith("0")) {
    return `${defaultCountryCode}${s.slice(1)}`;
  }

  if (s.startsWith("31")) {
    return `+${s}`;
  }

  return `${defaultCountryCode}${s}`;
}

async function loadCommunicationSettingsSafe() {
  try {
    const data = await tryFetchJson(["/api/communication-settings"]);

    return {
      email_enabled: data?.email_enabled !== false,
      whatsapp_enabled: data?.whatsapp_enabled !== false,
      sms_enabled: data?.sms_enabled === true,
      default_country_code: data?.default_country_code || "+31",
      default_sms_text: data?.default_sms_text || "",
      default_whatsapp_text: data?.default_whatsapp_text || "",
    };
  } catch {
    return {
      email_enabled: true,
      whatsapp_enabled: true,
      sms_enabled: false,
      default_country_code: "+31",
      default_sms_text: "",
      default_whatsapp_text: "",
    };
  }
}

function stopGridClick(e) {
  try {
    e.preventDefault();
    e.stopPropagation();
  } catch {}
}

function askCommunicationChoice(raw) {
  return new Promise((resolve) => {
    const overlay = document.createElement("div");

    overlay.style.position = "fixed";
    overlay.style.left = "0";
    overlay.style.top = "0";
    overlay.style.right = "0";
    overlay.style.bottom = "0";
    overlay.style.background = "rgba(0,0,0,0.35)";
    overlay.style.zIndex = "999999";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.padding = "18px";
    overlay.style.boxSizing = "border-box";

    const box = document.createElement("div");

    box.style.width = "min(520px, 100%)";
    box.style.background = "#fff";
    box.style.borderRadius = "12px";
    box.style.boxShadow = "0 18px 50px rgba(0,0,0,0.35)";
    box.style.padding = "18px";
    box.style.boxSizing = "border-box";
    box.style.fontFamily = "Arial, Helvetica, sans-serif";
    box.style.color = "#111";

    const title = document.createElement("div");
    title.style.fontWeight = "700";
    title.style.fontSize = "18px";
    title.style.marginBottom = "12px";
    title.textContent = `Wat wil je doen met ${raw}?`;

    const help = document.createElement("div");
    help.style.fontSize = "16px";
    help.style.lineHeight = "1.8";
    help.style.marginBottom = "12px";
    help.innerHTML = "1 = Bellen<br/>2 = SMS<br/>3 = WhatsApp";

    const input = document.createElement("input");
    input.type = "text";
    input.value = "3";
    input.inputMode = "numeric";
    input.autocomplete = "off";
    input.style.width = "100%";
    input.style.fontSize = "20px";
    input.style.padding = "10px 12px";
    input.style.border = "2px solid #1f4e79";
    input.style.borderRadius = "8px";
    input.style.boxSizing = "border-box";
    input.style.marginBottom = "14px";

    const buttons = document.createElement("div");
    buttons.style.display = "flex";
    buttons.style.gap = "10px";
    buttons.style.justifyContent = "flex-end";

    const okButton = document.createElement("button");
    okButton.type = "button";
    okButton.textContent = "OK";
    okButton.style.padding = "10px 18px";
    okButton.style.border = "none";
    okButton.style.borderRadius = "8px";
    okButton.style.background = "#1f4e79";
    okButton.style.color = "#fff";
    okButton.style.fontSize = "16px";
    okButton.style.cursor = "pointer";

    const cancelButton = document.createElement("button");
    cancelButton.type = "button";
    cancelButton.textContent = "Annuleren";
    cancelButton.style.padding = "10px 18px";
    cancelButton.style.border = "1px solid #d0d0d0";
    cancelButton.style.borderRadius = "8px";
    cancelButton.style.background = "#fff";
    cancelButton.style.color = "#111";
    cancelButton.style.fontSize = "16px";
    cancelButton.style.cursor = "pointer";

    let done = false;

    function cleanup(value) {
      if (done) return;
      done = true;

      try {
        document.body.removeChild(overlay);
      } catch {}

      resolve(value);
    }

    okButton.onclick = () => cleanup(input.value);
    cancelButton.onclick = () => cleanup(null);

    input.onkeydown = (ev) => {
      if (ev.key === "Enter") {
        ev.preventDefault();
        cleanup(input.value);
      }

      if (ev.key === "Escape") {
        ev.preventDefault();
        cleanup(null);
      }
    };

    overlay.onclick = (ev) => {
      if (ev.target === overlay) {
        cleanup(null);
      }
    };

    buttons.appendChild(cancelButton);
    buttons.appendChild(okButton);

    box.appendChild(title);
    box.appendChild(help);
    box.appendChild(input);
    box.appendChild(buttons);

    overlay.appendChild(box);
    document.body.appendChild(overlay);

    setTimeout(() => {
      try {
        input.focus();
        input.select();
      } catch {}
    }, 80);
  });
}

async function handleEmailGridClick(e, email) {
  stopGridClick(e);

  const mail = String(email || "").trim();

  if (!mail || mail === "-") return;

  const settings = await loadCommunicationSettingsSafe();

  if (settings.email_enabled === false) {
    alert("E-mail is niet actief in Communicatie-instellingen.");
    return;
  }

  window.location.href = `mailto:${encodeURIComponent(mail)}`;
}

function handlePhoneGridClick(e, phone) {
  stopGridClick(e);

  const tel = String(phone || "").trim();

  if (!tel || tel === "-") return;

  window.location.href = `tel:${tel}`;
}

async function handleMobileGridClick(e, mobile) {
  stopGridClick(e);

  const raw = String(mobile || "").trim();

  if (!raw || raw === "-") return;

  const settings = await loadCommunicationSettingsSafe();
  const normalized = normalizePhoneForCommunication(raw, settings.default_country_code || "+31");

  if (!normalized) return;

  const choice = await askCommunicationChoice(raw);

  const c = String(choice || "").trim().toLowerCase();

  if (!c) return;

  if (c === "1" || c === "bellen" || c === "bel") {
    window.location.href = `tel:${raw}`;
    return;
  }

  if (c === "2" || c === "sms") {
    if (settings.sms_enabled !== true) {
      alert("SMS is niet actief in Communicatie-instellingen.");
      return;
    }

    const text = window.prompt(
      `SMS naar ${raw}`,
      settings.default_sms_text || ""
    );

    if (text === null) return;

    const message = String(text || "").trim();

    if (!message) {
      alert("SMS tekst ontbreekt.");
      return;
    }

    try {
      await tryFetchJson(["/api/communications/sms"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: raw,
          text: message,
        }),
      });

      alert(`SMS verstuurd naar ${raw}`);
    } catch (err) {
      alert(err?.message || "SMS verzenden mislukt.");
    }

    return;
  }

  if (c === "3" || c === "whatsapp" || c === "app") {
    if (settings.whatsapp_enabled === false) {
      alert("WhatsApp is niet actief in Communicatie-instellingen.");
      return;
    }

    const whatsappNumber = normalized.replace(/[^\d]/g, "");
    const text = String(settings.default_whatsapp_text || "").trim();
    const suffix = text ? `?text=${encodeURIComponent(text)}` : "";

    window.open(`https://wa.me/${whatsappNumber}${suffix}`, "_blank", "noopener,noreferrer");
  }
}

function upsertById(arr, item) {
  const id = item?.id;
  if (id === null || id === undefined) return arr;
  const idx = arr.findIndex((x) => String(x.id) === String(id));
  if (idx === -1) return [item, ...arr];
  const copy = arr.slice();
  copy[idx] = { ...copy[idx], ...item };
  return copy;
}

function removeById(arr, id) {
  return arr.filter((x) => String(x.id) !== String(id));
}

function euro(n) {
  const x = Number(n || 0);
  if (!Number.isFinite(x)) return "€ 0,00";
  try {
    return x.toLocaleString("nl-NL", { style: "currency", currency: "EUR" });
  } catch {
    return `€ ${x.toFixed(2)}`;
  }
}

function isoDate(s) {
  const t = String(s || "").slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : "";
}

function formatNlDate(s) {
  const iso = isoDate(s);
  if (!iso) return "";
  const [yyyy, mm, dd] = iso.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

function addDaysIso(iso, days) {
  const base = isoDate(iso);
  const n = Number(days || 0);
  if (!base || !Number.isFinite(n)) return "";

  const [y, m, d] = base.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + Math.trunc(n));

  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");

  return `${yy}-${mm}-${dd}`;
}

function normalizeSpokenOfferNumber(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "";

  const map = {
    nul: "0",
    null: "0",
    zero: "0",
    een: "1",
    "één": "1",
    twee: "2",
    drie: "3",
    vier: "4",
    vijf: "5",
    zes: "6",
    zeven: "7",
    acht: "8",
    negen: "9",
  };

  const parts = s
    .replace(/-/g, " ")
    .replace(/\./g, " ")
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean);

  if (!parts.length) return "";

  let out = "";
  let ok = true;

  for (const p of parts) {
    if (/^\d+$/.test(p)) {
      out += p;
    } else if (map[p]) {
      out += map[p];
    } else {
      ok = false;
      break;
    }
  }

  if (!ok) {
    out = s.replace(/[^\d]/g, "");
  }

  if (!out) return "";

  return out.padStart(4, "0");
}

function parseDutchDateToISO(raw) {
  const s0 = safeTrim(raw);
  if (!s0) return "";

  const digits = s0.replace(/[^\d]/g, "");

  if (digits.length === 8) {
    const dd = parseInt(digits.slice(0, 2), 10);
    const mm = parseInt(digits.slice(2, 4), 10);
    const yyyy = parseInt(digits.slice(4, 8), 10);
    if (yyyy >= 1900 && yyyy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      const iso = `${String(yyyy).padStart(4, "0")}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
      const d = new Date(`${iso}T00:00:00`);
      if (!Number.isNaN(d.getTime())) return iso;
    }
  }

  if (digits.length === 6) {
    const dd = parseInt(digits.slice(0, 2), 10);
    const mm = parseInt(digits.slice(2, 4), 10);
    const yy = parseInt(digits.slice(4, 6), 10);
    const yyyy = 2000 + yy;
    if (yyyy >= 1900 && yyyy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      const iso = `${String(yyyy).padStart(4, "0")}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
      const d = new Date(`${iso}T00:00:00`);
      if (!Number.isNaN(d.getTime())) return iso;
    }
  }

  const s = norm(s0).replace(/[\.\,]/g, " ");
  const tokens = s.split(" ").filter(Boolean);

  const monthMap = {
    jan: 1,
    januari: 1,
    feb: 2,
    februari: 2,
    mrt: 3,
    maart: 3,
    apr: 4,
    april: 4,
    mei: 5,
    jun: 6,
    juni: 6,
    jul: 7,
    juli: 7,
    aug: 8,
    augustus: 8,
    sep: 9,
    sept: 9,
    september: 9,
    okt: 10,
    oktober: 10,
    nov: 11,
    november: 11,
    dec: 12,
    december: 12,
  };

  const splitMore = [];
  for (const t of tokens) {
    const parts = t.split(/[-/]/g).filter(Boolean);
    splitMore.push(...parts);
  }
  const tt = splitMore.filter(Boolean);

  if (tt.length >= 2) {
    const d1 = parseInt(tt[0], 10);
    const mWord = tt[1];
    const m1 = monthMap[mWord];

    if (Number.isFinite(d1) && m1) {
      let y1 = null;
      if (tt.length >= 3) {
        const yTry = parseInt(tt[2], 10);
        if (Number.isFinite(yTry)) y1 = yTry;
      }
      if (!y1) y1 = new Date().getFullYear();
      if (y1 < 100) y1 = 2000 + y1;

      if (y1 >= 1900 && y1 <= 2100 && d1 >= 1 && d1 <= 31) {
        const iso = `${String(y1).padStart(4, "0")}-${String(m1).padStart(2, "0")}-${String(d1).padStart(2, "0")}`;
        const d = new Date(`${iso}T00:00:00`);
        if (!Number.isNaN(d.getTime())) return iso;
      }
    }
  }

  if (tt.length >= 3) {
    const dd = parseInt(tt[0], 10);
    const mm = parseInt(tt[1], 10);
    let yyyy = parseInt(tt[2], 10);

    if (Number.isFinite(yyyy) && yyyy < 100) yyyy = 2000 + yyyy;

    if (yyyy >= 1900 && yyyy <= 2100 && mm >= 1 && mm <= 12 && dd >= 1 && dd <= 31) {
      const iso = `${String(yyyy).padStart(4, "0")}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
      const d = new Date(`${iso}T00:00:00`);
      if (!Number.isNaN(d.getTime())) return iso;
    }
  }

  return "";
}

function speechDigitsPreserveZeros(raw) {
  const s = String(raw || "").trim().toLowerCase();
  if (!s) return "";

  const map = {
    "nul": "0",
    "null": "0",
    "zero": "0",
    "een": "1",
    "één": "1",
    "twee": "2",
    "drie": "3",
    "vier": "4",
    "vijf": "5",
    "zes": "6",
    "zeven": "7",
    "acht": "8",
    "negen": "9",
  };

  const cleaned = s
    .replace(/-/g, " ")
    .replace(/\./g, " ")
    .replace(/,/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  const parts = cleaned.split(" ").filter(Boolean);
  if (!parts.length) return "";

  let out = "";
  let allKnown = true;

  for (const p of parts) {
    if (/^\d+$/.test(p)) {
      out += p;
    } else if (map[p]) {
      out += map[p];
    } else {
      allKnown = false;
      break;
    }
  }

  if (allKnown && out) return out;

  // fallback: alleen cijfers uit de tekst halen
  const digits = cleaned.replace(/[^\d]/g, "");
  return digits;
}

function decimal2OrEmptyString(s) {
  const raw = String(s || "").trim();
  if (!raw) return "";
  const cleaned = raw.replace(/,/g, ".").replace(/[^\d.]/g, "");
  const parts = cleaned.split(".");
  const a = parts[0] || "";
  const b = (parts[1] || "").slice(0, 2);
  if (parts.length === 1) return a;
  return `${a}.${b}`;
}

function punctToChar(t) {
  const x = norm(t);
  if (x === "punt") return ".";
  if (x === "komma") return ",";
  if (x === "dubbele punt" || x === "dubbelpunt") return ":";
  if (x === "puntkomma") return ";";
  if (x === "vraagteken") return "?";
  if (x === "uitroepteken") return "!";
  if (x === "nieuwe regel" || x === "enter") return "\n";
  return null;
}

function normalizeDbStatus(status) {
  const s = String(status || "").trim().toUpperCase();

  if (!s) return "OFFERTE";

  if (s === "DRAFT" || s === "CONCEPT" || s === "OFFERTE") return "OFFERTE";
  if (s === "OPDRACHT") return "OPDRACHT";
  if (s === "FACTUUR") return "FACTUUR";

  return "OFFERTE";
}

function statusLabel(status) {
  const s = normalizeDbStatus(status);

  switch (s) {
    case "OFFERTE":
      return "Offerte";
    case "OPDRACHT":
      return "Opdracht";
    case "FACTUUR":
      return "Factuur";
    default:
      return "Offerte";
  }
}

function boolPaidLabel(openAmount) {
  return openAmount <= 0.00001 ? "betaald" : "open";
}

function normalizeVatRate(v) {
  const s = String(v ?? "").toLowerCase().trim();

  if (["hoog", "high", "21", "21%", "h"].includes(s)) return "hoog";
  if (["laag", "low", "9", "9%", "l"].includes(s)) return "laag";
  if (["0", "0%", "nul", "zero"].includes(s)) return "0";
  if (["verlegd", "reverse", "btw verlegd"].includes(s)) return "verlegd";

  const n = Number(s.replace(",", ".").replace("%", ""));
  if (Number.isFinite(n)) {
    if (n === 21 || n === 0.21) return "hoog";
    if (n === 9 || n === 0.09) return "laag";
    if (n === 0) return "0";
  }

  return "hoog";
}

function vatPercentForRate(rate) {
  const r = normalizeVatRate(rate);
  if (r === "hoog") return 21;
  if (r === "laag") return 9;
  return 0;
}

function vatLabelForRate(rate) {
  const r = normalizeVatRate(rate);
  if (r === "hoog") return "21% btw";
  if (r === "laag") return "9% btw";
  if (r === "0") return "0% btw";
  if (r === "verlegd") return "Btw verlegd";
  return "21% btw";
}

function pickOrgDefaultVatRate(org) {
  if (!org) return "hoog";

  const raw =
    org.vat_rate ??
    org.default_vat_rate ??
    org.default_vat_type ??
    org.vat_rate_default ??
    org.vat_type ??
    org.btw_type ??
    org.btw_soort ??
    org.invoice_vat_rate ??
    "hoog";

  return normalizeVatRate(raw);
}

function pickOrgVatNumber(org) {
  if (!org) return "";
  return (
    org.vat_number ||
    org.vat_no ||
    org.btw_number ||
    org.btw_nummer ||
    org.tax_number ||
    ""
  );
}

function pickOrgGrekeningPercent(org) {
  if (!org) return 0;
  const raw =
    org.g_account_percentage ??
    org.grekening_percentage ??
    org.g_rekening_percentage ??
    org.g_percentage ??
    org.g_account_percent ??
    0;

  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.max(0, Math.min(100, n));
}

function numberFromAny(raw, fallback = 0) {
  if (raw === null || raw === undefined || raw === "") return fallback;

  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw : fallback;
  }

  let s = String(raw).trim();
  if (!s) return fallback;

  s = s.replace(/[^0-9,.-]/g, "");

  // Nederlands: 1.234,56 => 1234.56
  if (s.includes(",") && s.includes(".")) {
    s = s.replace(/\./g, "").replace(",", ".");
  } else if (s.includes(",")) {
    s = s.replace(",", ".");
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : fallback;
}

function lineQty(line) {
  return numberFromAny(
    line?.quantity ??
    line?.qty ??
    line?.aantal ??
    line?.amount_qty ??
    line?.line_qty ??
    0
  );
}

function lineUnitPrice(line) {
  return numberFromAny(
    line?.unit_price ??
    line?.price ??
    line?.unitPrice ??
    line?.unitprice ??
    line?.prijs ??
    line?.bedrag ??
    line?.amount ??
    0
  );
}

function lineVatRate(line) {
  return normalizeVatRate(line?.vat_rate ?? line?.vat ?? line?.btw ?? "hoog");
}

function lineDescription(line) {
  return String(line?.description ?? line?.omschrijving ?? line?.name ?? "").trim();
}

const OFFER_LINE_TYPES = ["Artikel", "Tekst", "Kop", "Subtotaal", "Totaal"];

function normalizeOfferLineType(raw) {
  const s = String(raw || "")
    .toLowerCase()
    .trim();

  if (s === "artikel" || s === "article" || s === "regel" || s === "item") return "Artikel";
  if (s === "tekst" || s === "text") return "Tekst";
  if (s === "kop" || s === "header" || s === "heading") return "Kop";
  if (s === "subtotaal" || s === "subtotal") return "Subtotaal";
  if (s === "totaal" || s === "total") return "Totaal";

  return "Artikel";
}

function backendOfferLineType(displayType) {
  const type = normalizeOfferLineType(displayType);

  if (type === "Artikel") return "item";
  if (type === "Tekst") return "text";
  if (type === "Kop") return "header";
  if (type === "Subtotaal") return "subtotal";
  if (type === "Totaal") return "total";

  return "item";
}

function lineTypeOf(line) {
  return normalizeOfferLineType(
    line?.lineType ??
    line?.type ??
    line?.regeltype ??
    line?.line_type ??
    "Artikel"
  );
}

function isAmountLine(line) {
  return lineTypeOf(line) === "Artikel";
}

function lineTotalExVat(line) {
  if (!isAmountLine(line)) return 0;

  const qty = lineQty(line);
  const unit = lineUnitPrice(line);

  if (!Number.isFinite(qty) || !Number.isFinite(unit)) return 0;

  return qty * unit;
}

function subtotalUntilLine(lines, targetIndex) {
  const arr = Array.isArray(lines) ? lines : [];
  const max = Number.isFinite(Number(targetIndex))
    ? Math.min(arr.length - 1, Number(targetIndex))
    : arr.length - 1;

  let subtotal = 0;

  for (let i = 0; i <= max; i += 1) {
    subtotal += lineTotalExVat(arr[i]);
  }

  return subtotal;
}

function calcOfferTotalsUntilLine(lines, targetIndex) {
  const arr = Array.isArray(lines) ? lines : [];
  const max = Number.isFinite(Number(targetIndex))
    ? Math.min(arr.length - 1, Number(targetIndex))
    : arr.length - 1;

  if (max < 0) {
    return calcOfferTotals([]);
  }

  return calcOfferTotals(arr.slice(0, max + 1));
}

function needsFixedOfferTextLayout(text) {
  const value = String(text || "");
  return /	| {2,}|□|☐|→|\|/.test(value);
}

function fixedOfferTextStyle(text) {
  // vOFF-038: geen apart monospace-lettertype meer gebruiken.
  // We bewaren spaties en regeleinden met whiteSpace: "pre-wrap",
  // maar houden overal hetzelfde offertelettertype aan.
  return {
    tabSize: 4,
  };
}

function calcOfferTotals(lines) {
  const vatMap = new Map();
  let subtotal = 0;

  const cleanLines = (Array.isArray(lines) ? lines : []).filter((ln) => {
    return isAmountLine(ln) && lineQty(ln) > 0 && lineDescription(ln);
  });

  cleanLines.forEach((ln) => {
    const qty = lineQty(ln);
    const unit = lineUnitPrice(ln);
    const rate = lineVatRate(ln);
    const base = qty * unit;
    const pct = vatPercentForRate(rate);
    const vatAmount = base * (pct / 100);

    subtotal += base;

    if (!vatMap.has(rate)) {
      vatMap.set(rate, {
        rate,
        label: vatLabelForRate(rate),
        percent: pct,
        base: 0,
        vat: 0,
      });
    }

    const row = vatMap.get(rate);
    row.base += base;
    row.vat += vatAmount;
  });

  const vatRows = Array.from(vatMap.values());
  const vatTotal = vatRows.reduce((sum, row) => sum + row.vat, 0);
  const total = subtotal + vatTotal;

  const hasNormalVat = vatRows.some((x) => x.rate === "hoog" || x.rate === "laag");
  const allVerlegd = vatRows.length > 0 && vatRows.every((x) => x.rate === "verlegd");
  const allZero = vatRows.length > 0 && vatRows.every((x) => x.rate === "0");

  return {
    subtotal,
    vatRows,
    vatTotal,
    vat_total: vatTotal,
    total,
    hasNormalVat,
    allVerlegd,
    allZero,
    hideVatColumnOnInvoice: false,
  };
}

function splitMultiline(text) {
  return String(text || "")
    .split(/\r?\n/)
    .map((s) => s.trim())
    .filter(Boolean);
}

// ---------------- component ----------------
const emptyForm = {
  id: null,
  organisation_id: "",
  contact_id: "",
  template_id: "",
  invoice_number: "",
  series_code: "",
  invoice_date: "",
  due_date: "",
  status: "DRAFT",
  notes: "",

  subtotal: 0,
  vat_total: 0,
  total: 0,

  created_at: "",
  updated_at: "",
};

function todayLocalIso() {
  return new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
    .toISOString()
    .slice(0, 10);
}

export default function Offertes() {
  const rawUser =
    sessionStorage.getItem("user") ||
    localStorage.getItem("user") ||
    "";

  let role = "user";

  try {
    role = JSON.parse(rawUser || "{}")?.role || "user";
  } catch {}

  const isReadonly = String(role || "").toLowerCase() === "readonly";

  const VERSION = "vOFF-054";

  // data
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [invoiceSettings, setInvoiceSettings] = useState({});
  const [offerTemplates, setOfferTemplates] = useState([]);
  const [projectItems, setProjectItems] = useState([]);
  const [selectedProjectLineId, setSelectedProjectLineId] = useState("__manual__");
  const [includeChildProjects, setIncludeChildProjects] = useState(false);
  function formatMoney(val) {
    return Number(val || 0).toLocaleString("nl-NL", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  // selectie + form
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyForm);

// Lock logica:
// - OFFERTE mag nog bewerkt worden
// - OPDRACHT en FACTUUR zijn voor gewone users geblokkeerd
// - Admin mag dit later wel aanpassen
const currentStatus = normalizeDbStatus(form?.status || "OFFERTE");
const isLocked = currentStatus !== "OFFERTE";

  // Admin mag alles
  const isAdmin = role.toLowerCase() === "admin";

  // Eindresultaat: readonly als:
  // - readonly user
  // - OF offerte niet meer DRAFT en geen admin
  const isFormDisabled = isReadonly || (isLocked && !isAdmin);

  // org detail (voor correspondentieadres)
  const [orgDetail, setOrgDetail] = useState(null);

  // payments
  const [payments, setPayments] = useState([]);
  const [lines, setLines] = useState([]);
  const [lineDraft, setLineDraft] = useState({
    line_type: "Artikel",
    quantity: 1,
    description: "",
    unit_price: "",
    vat_rate: "hoog",
  });

useEffect(() => {
  if (lineDescRef.current) {
    lineDescRef.current.style.height = "auto";
    lineDescRef.current.style.height =
      lineDescRef.current.scrollHeight + "px";
  }
}, [lineDraft.description]);

useEffect(() => {
  if (selectedProjectLineId === "__manual__") {
    setIncludeChildProjects(false);
  }
}, [selectedProjectLineId]);

  const qtyInputRef = useRef(null);
  const lineDescRef = useRef(null);
  const linePriceRef = useRef(null);
  const lineVatRef = useRef(null);
  const [editingLineId, setEditingLineId] = useState(null);
  const [draggingOfferLineId, setDraggingOfferLineId] = useState(null);
  const [dragOverOfferLineId, setDragOverOfferLineId] = useState(null);
  const [payDraft, setPayDraft] = useState({
    paid_on: new Date().toLocaleDateString("en-CA"),
    amount: "",
    notes: "",
  });

  const [payError, setPayError] = useState(null);

  // cursor index
  const [listIndex, setListIndex] = useState(-1);

  // ui status
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [flash, setFlash] = useState("");
  const [lineError, setLineError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingApproval, setSendingApproval] = useState(false);
  const [lastApprovalInfo, setLastApprovalInfo] = useState(null);

  // AI popup
  const [aiOpen, setAiOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiSource, setAiSource] = useState("page"); // page | popup
  const aiTextRef = useRef(null);

  // vFCT-PTT-05: helper om de lijst opnieuw te laden (gebruik door knop 'Vernieuw' en voice)
  function loadList() {
    setReloadKey((k) => k + 1);
  }

  const [speechInfo, setSpeechInfo] = useState("");
  const [hideVoiceError, setHideVoiceError] = useState(false);
  const prevVoiceErrorRef = useRef("");
  const [pendingSpeechSearch, setPendingSpeechSearch] = useState(false);

  // zoek + filters
  const [q, setQ] = useState(getParam("q"));

  // dropdown filters (grid)
  const EMPTY_VAL = "__EMPTY__";
  const [zoeknaamFilter, setZoeknaamFilter] = useState("");
  const [contactFilter, setContactFilter] = useState("");
  const [invoiceNrFilter, setInvoiceNrFilter] = useState("");
  const [invoiceDateFilter, setInvoiceDateFilter] = useState("");
  const [totaalFilter, setTotaalFilter] = useState("");
  const [telFilter, setTelFilter] = useState("");
  const [mobFilter, setMobFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [companyCodeFilter, setCompanyCodeFilter] = useState("");
  const [paidFilter, setPaidFilter] = useState("");
  const [mode, setMode] = useState("search");
  const isFormLocked = mode !== "form";
  const [sort, setSort] = useState({ key: "invoice_date", dir: "desc" }); // standaard nieuw->oud
  const [showLinesPopup, setShowLinesPopup] = useState(false);
  const [popupLines, setPopupLines] = useState([]);
 const [popupMode, setPopupMode] = useState("edit");
const [silentDocumentPreview, setSilentDocumentPreview] = useState(false);
  const [popupWindow, setPopupWindow] = useState({
    x: 80,
    y: 50,
    width: 1180,
    height: 780,
  });

  const popupDragRef = useRef({
    dragging: false,
    startMouseX: 0,
    startMouseY: 0,
    startX: 0,
    startY: 0,
  });

useEffect(() => {
    if (!showLinesPopup) return;

    focusFirstPopupField();

    const w = Math.min(window.innerWidth - 80, 1180);
    const h = Math.min(window.innerHeight - 80, 780);
    const x = Math.max(20, Math.round((window.innerWidth - w) / 2));
    const y = Math.max(20, Math.round((window.innerHeight - h) / 2));

    setPopupWindow({ x, y, width: w, height: h });
  }, [showLinesPopup]);

  useEffect(() => {
    function onMouseMove(e) {
      if (!popupDragRef.current.dragging) return;

      const dx = e.clientX - popupDragRef.current.startMouseX;
      const dy = e.clientY - popupDragRef.current.startMouseY;

      setPopupWindow((prev) => {
        const nextX = popupDragRef.current.startX + dx;
        const nextY = popupDragRef.current.startY + dy;

        const maxX = Math.max(20, window.innerWidth - prev.width - 20);
        const maxY = Math.max(20, window.innerHeight - prev.height - 20);

        return {
          ...prev,
          x: Math.min(Math.max(20, nextX), maxX),
          y: Math.min(Math.max(20, nextY), maxY),
        };
      });
    }

    function onMouseUp() {
      popupDragRef.current.dragging = false;
    }

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, []);

  function startPopupDrag(e) {
    popupDragRef.current = {
      dragging: true,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startX: popupWindow.x,
      startY: popupWindow.y,
    };
  }

  // refs
  const searchRef = useRef(null);
  const rowRefs = useRef([]);
  const midScrollRef = useRef(null);
  const rightScrollRef = useRef(null);
  const popupPrintRef = useRef(null);

  // right form refs (focus order)
  const orgRef = useRef(null);
  const contactRef = useRef(null);
  const invNumRef = useRef(null);
  const invDateRef = useRef(null);
  const statusRef = useRef(null);
  const payDateRef = useRef(null);
  const payAmtRef = useRef(null);
  const payNotesRef = useRef(null);

  const focusOrderRefs = useMemo(
    () => [
      orgRef,
      contactRef,
      invNumRef,
      invDateRef,
      payDateRef,
      payAmtRef,
      payNotesRef,
      statusRef,
    ],
    []
  );

  const [focusedKey, setFocusedKey] = useState("");

  function formHasId() {
    return form.id !== null && form.id !== undefined && String(form.id).length > 0;
  }

  // ---------- LOAD ORGS ----------
  useEffect(() => {
    let cancelled = false;

    async function loadInvoiceSettings() {
      try {
        const data = await tryFetchJson(["/api/invoice-settings"]);
        if (!cancelled) {
          setInvoiceSettings(data && typeof data === "object" ? data : {});
        }
      } catch {
        if (!cancelled) {
          setInvoiceSettings({});
        }
      }
    }

    loadInvoiceSettings();

    return () => {
      cancelled = true;
    };
  }, []);

useEffect(() => {
    let cancelled = false;

    async function loadOfferTemplates() {
      try {
        const data = await tryFetchJson(["/api/offer-templates?active=1", "/api/offer-templates"]);
        if (!cancelled) setOfferTemplates(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setOfferTemplates([]);
      }
    }

    loadOfferTemplates();

    return () => {
      cancelled = true;
    };
  }, []);

useEffect(() => {
    let cancelled = false;

    async function loadProjectItems() {
      try {
        const data = await tryFetchJson(["/api/projectmanagement"]);
        if (!cancelled) setProjectItems(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setProjectItems([]);
      }
    }

    loadProjectItems();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function loadOrgs() {
      try {
        const orgData = await tryFetchJson(["/api/organisations?q=", "/api/organisations"]);
        if (!cancelled) setOrgs(Array.isArray(orgData) ? orgData : []);
      } catch {
        if (!cancelled) setOrgs([]);
      }
    }

    loadOrgs();
    return () => {
      cancelled = true;
    };
  }, []);

function pickOrgName(o) {
    return o?.name || o?.naam || o?.bedrijfsnaam || "";
  }

  function pickOrgSearchName(o) {
    return (
      o?.search_name ||
      o?.zoeknaam ||
      o?.zoek_naam ||
      o?.organisation_search_name ||
      o?.organisatie_zoeknaam ||
      pickOrgName(o)
    );
  }

  function isPrivateRelation(o) {
    const entityType = String(
      o?.relation_entity_type ||
      o?.relation_type ||
      o?.entity_type ||
      ""
    )
      .toLowerCase()
      .trim();

    return (
      entityType === "private" ||
      entityType === "particulier" ||
      entityType === "person" ||
      entityType === "persoon"
    );
  }

  function pickOrgSearchNameFromRow(it) {
    const org =
      orgs.find((o) => String(o.id) === String(it?.organisation_id || "")) ||
      null;

    return (
      pickOrgSearchName(org) ||
      it?.organisation_search_name ||
      it?.organisation_zoeknaam ||
      it?.search_name ||
      it?.organisation_name ||
      ""
    );
  }

  function pickCompanyCodeName(row) {
    return (
      row?.company_code_name ||
      row?.bedrijfscode_naam ||
      row?.bedrijfscode ||
      row?.company_code ||
      ""
    );
  }

  function pickCompanyCodeNameFromOrganisation(org) {
    return (
      org?.company_code_name ||
      org?.bedrijfscode_naam ||
      org?.bedrijfscode ||
      org?.company_code ||
      ""
    );
  }

  function pickContactFullName(c) {
    const fn = c?.first_name || c?.voornaam || "";
    const tv = c?.name_prefix || c?.tussenvoegsels || c?.tussenvoegsel || "";
    const ln = c?.last_name || c?.achternaam || "";
    const merged = [fn, tv, ln].map(safeTrim).filter(Boolean).join(" ");
    const full = safeTrim(c?.full_name || c?.naam || "");
    return merged || full || "";
  }

  function pickContactNameFromRow(it) {
    const fn = safeTrim(it?.contact_first_name || "");
    const tv = safeTrim(it?.contact_name_prefix || "");
    const ln = safeTrim(it?.contact_last_name || "");
    const merged = [fn, tv, ln].filter(Boolean).join(" ").trim();
    return merged || safeTrim(it?.contact_name || "");
  }

// ---------- LOAD CONTACTS (voor gekozen relatie) ----------
  // Bij Particulieren is Contactpersoon niet van toepassing.
  useEffect(() => {
    let cancelled = false;

    async function loadContacts() {
      try {
        const selectedRelation = orgs.find(
          (o) => String(o.id) === String(form?.organisation_id || "")
        );

        const isPrivate = isPrivateRelation(selectedRelation);

        if (!form?.organisation_id || isPrivate) {
          if (!cancelled) setContacts([]);
          return;
        }

        const data = await tryFetchJson([
          `/api/contacts?organisation_id=${encodeURIComponent(form.organisation_id)}`
        ]);

        if (!cancelled) setContacts(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setContacts([]);
      }
    }

    loadContacts();
    return () => {
      cancelled = true;
    };
  }, [form?.organisation_id, orgs]);

  // ---------- LOAD INVOICES ----------
  const serverQuery = useMemo(() => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    return p.toString();
  }, [q]);

  useEffect(() => {
    let cancelled = false;

    async function loadItems() {
      setLoading(true);
      setError(null);

      try {
const data = await tryFetchJson(
          [`/api/offers?${serverQuery}`, "/api/offers"]
        );

        if (cancelled) return;

        const arr = Array.isArray(data) ? data : [];

        // 🔥 HIER ZAT HET PROBLEEM
        setItems(arr);

      } catch (e) {
        if (!cancelled) setError(e?.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadItems();

    return () => {
      cancelled = true;
    };
  }, [serverQuery, reloadKey]);

  // ---------- GRID OPTIONS ----------
const zoeknaamOptions = useMemo(() => {
    const set = new Set(
      items
        .map((x) => safeTrim(pickOrgSearchNameFromRow(x)))
        .filter(Boolean)
    );

    return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
  }, [items, orgs]);

  const contactOptions = useMemo(() => {
    const set = new Set(items.map((x) => safeTrim(pickContactNameFromRow(x))).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
  }, [items]);

  const invoiceNrOptions = useMemo(() => {
    const set = new Set(items.map((x) => safeTrim(x.invoice_number || "")).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
  }, [items]);

  const invoiceDateOptions = useMemo(() => {
    const set = new Set(items.map((x) => safeTrim(isoDate(x.invoice_date) || "")).filter(Boolean));
    // nieuw->oud
    return Array.from(set).sort((a, b) => (a === b ? 0 : a < b ? 1 : -1));
  }, [items]);

  const totaalOptions = [...new Set(items.map(x => x.total))];

  const telOptions = useMemo(() => {
    const set = new Set(items.map((x) => safeTrim(x.org_phone || "")).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
  }, [items]);

  const mobOptions = useMemo(() => {
    const set = new Set(items.map((x) => safeTrim(x.org_mobile || "")).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
  }, [items]);

  const emailOptions = useMemo(() => {
    const set = new Set(items.map((x) => safeTrim(x.org_email || "")).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
  }, [items]);

  const cityOptions = useMemo(() => {
    const set = new Set(items.map((x) => safeTrim(x.org_city || "")).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
  }, [items]);

  const countryOptions = useMemo(() => {
    const set = new Set(items.map((x) => safeTrim(x.org_country || "")).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
  }, [items]);

  const companyCodeOptions = useMemo(() => {
    const set = new Set(items.map((x) => safeTrim(pickCompanyCodeName(x))).filter(Boolean));
    return Array.from(set).sort((a, b) => a.localeCompare(b, "nl"));
  }, [items]);

const statusOptions = [
  "Offerte",
  "Opdracht",
  "Factuur",
];

const statusSelectOptions = [
  { value: "OFFERTE", label: "Offerte" },
  { value: "OPDRACHT", label: "Opdracht" },
  { value: "FACTUUR", label: "Factuur" },
];

  // paidFilter opties zijn (alle/open/betaald); wordt client-side berekend op basis van payments.
  // In grid tonen we status "open/betaald" op basis van payments in detail; voor grid gebruiken we server.status als indicatie.
  const paidOptions = ["open", "betaald"];

  // ---------- FILTER + SORT ----------
  const filteredItems = useMemo(() => {
    const qq = norm(q);
    const arr = items.slice();

    const byQ = (it) => {
      if (!qq) return true;
      const hay = norm(
        [
          it.organisation_name || "",
          pickContactNameFromRow(it),
          it.invoice_number || "",
          isoDate(it.invoice_date),
          it.org_city || "",
          it.org_country || "",
          it.status || "",
          pickCompanyCodeName(it),
        ].join(" ")
      );
      return hay.includes(qq);
    };

    const byCol = (it) => {
      const orgName = safeTrim(pickOrgSearchNameFromRow(it));
      const contactName = safeTrim(pickContactNameFromRow(it));
      const invNr = safeTrim(it.invoice_number || "");
      const invDt = safeTrim(isoDate(it.invoice_date) || "");
      const tel = safeTrim(it.org_phone || "");
      const mob = safeTrim(it.org_mobile || "");
      const email = safeTrim(it.org_email || "");
      const city = safeTrim(it.org_city || "");
      const ctry = safeTrim(it.org_country || "");
      const companyCodeName = safeTrim(pickCompanyCodeName(it));
      const st = statusLabel(it.status);

      const match = (val, filt) => {
        if (!filt) return true;
        if (filt === EMPTY_VAL) return !safeTrim(val);
        return String(val) === String(filt);
      };

      if (!match(orgName, zoeknaamFilter)) return false;
      if (!match(contactName, contactFilter)) return false;
      if (!match(invNr, invoiceNrFilter)) return false;
      if (!match(invDt, invoiceDateFilter)) return false;
      if (!match(it.total, totaalFilter)) return false;
      if (!match(tel, telFilter)) return false;
      if (!match(mob, mobFilter)) return false;
      if (!match(email, emailFilter)) return false;
      if (!match(city, cityFilter)) return false;
      if (!match(ctry, countryFilter)) return false;
      if (!match(st, statusFilter)) return false;
      if (!match(companyCodeName, companyCodeFilter)) return false;

      // paidFilter: op grid-niveau hebben we geen payments per offerte geladen.
      // We gebruiken simpele heuristiek: status=gefactureerd => open/betaald onbekend (laat leeg), tenzij user filtert -> dan laten we alles door.
      if (!paidFilter) return true;
      // Als je wilt filteren op betaald/open: we laten dit nu nog "alle" (anders gaan we offerte-per-offerte payments ophalen: te zwaar).
      // Je kunt betaald/open wél rechts zien zodra je een offerte selecteert.
      return true;
    };

    return arr.filter((it) => byQ(it) && byCol(it));
}, [
    items,
    orgs,
    q,
    zoeknaamFilter,
    contactFilter,
    invoiceNrFilter,
    invoiceDateFilter,
    totaalFilter,
    telFilter,
    mobFilter,
    emailFilter,
    cityFilter,
    countryFilter,
    statusFilter,
    companyCodeFilter,
    paidFilter,
  ]);

const sortedItems = useMemo(() => {
    const copy = filteredItems.slice();
    const dirMul = sort.dir === "desc" ? -1 : 1;

    const cmpText = (a, b) => {
      const aa = norm(a);
      const bb = norm(b);
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    const cmpDate = (a, b) => {
      const aa = isoDate(a) || "";
      const bb = isoDate(b) || "";
      if (aa === bb) return 0;
      return aa < bb ? -1 : 1;
    };

    const customSort = (a, b) => {
      if (sort.key === "invoice_date") return dirMul * cmpDate(a.invoice_date, b.invoice_date);
      if (sort.key === "invoice_number") return dirMul * cmpText(a.invoice_number || "", b.invoice_number || "");
      if (sort.key === "org") return dirMul * cmpText(pickOrgSearchNameFromRow(a), pickOrgSearchNameFromRow(b));
      if (sort.key === "contact") return dirMul * cmpText(pickContactNameFromRow(a), pickContactNameFromRow(b));
      if (sort.key === "totaal") return dirMul * ((Number(a.total || 0)) - (Number(b.total || 0)));
      if (sort.key === "city") return dirMul * cmpText(a.org_city || "", b.org_city || "");
      if (sort.key === "country") return dirMul * cmpText(a.org_country || "", b.org_country || "");
      if (sort.key === "status") return dirMul * cmpText(statusLabel(a.status), statusLabel(b.status));
      if (sort.key === "company_code") return dirMul * cmpText(pickCompanyCodeName(a), pickCompanyCodeName(b));
      return dirMul * cmpDate(a.invoice_date, b.invoice_date);
    };

    copy.sort(customSort);
    return copy;
  }, [filteredItems, sort, orgs]);

  const gridCount = Math.min(sortedItems.length, 9999);

  // ---------- LIST NAV HELPERS ----------
  function scrollMidTop() {
    try {
      if (midScrollRef.current) midScrollRef.current.scrollTop = 0;
    } catch { }
  }

  function scrollRightTop() {
    try {
      if (rightScrollRef.current) rightScrollRef.current.scrollTop = 0;
    } catch { }
  }

  function scrollRowIntoView(idx) {
    try {
      const el = rowRefs.current?.[idx];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "nearest" });
      }
    } catch { }
  }

  function clampIndex(idx, len) {
    const L = Number(len || 0);
    if (L <= 0) return -1;
    let x = Number.isFinite(idx) ? idx : 0;
    if (x < 0) x = 0;
    if (x > L - 1) x = L - 1;
    return x;
  }

  function ensureCursorExists() {
    const L = sortedItems.length;
    if (L <= 0) return -1;
    const idx = listIndex < 0 ? 0 : clampIndex(listIndex, L);
    if (idx !== listIndex) setListIndex(idx);
    return idx;
  }

  function moveCursor(delta) {
    setListIndex((prev) => {
      const L = sortedItems.length;
      if (L <= 0) return -1;
      const start = prev < 0 ? 0 : prev;
      const next = clampIndex(start + delta, L);
      setTimeout(() => scrollRowIntoView(next), 0);
      return next;
    });
  }



  function getFocusableElements() {
    const els = focusOrderRefs.map((r) => r?.current).filter(Boolean);
    return els.filter((el) => {
      try {
        if (el.disabled) return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      } catch {
        return false;
      }
    });
  }

function focusFirstRightField() {
    setTimeout(() => {
      try {
        scrollRightTop();
        orgRef.current?.focus?.();
      } catch { }
    }, 0);
  }

  function focusFirstRightFieldMobile() {
    setTimeout(() => {
      try {
        const panel = rightScrollRef.current;
        if (!panel) return;

        panel.scrollTop = 0;
        panel.scrollIntoView?.({
          behavior: "smooth",
          block: "start",
          inline: "nearest",
        });
      } catch {}
    }, 120);
  }

  function focusNextField(dir = +1) {
    const els = getFocusableElements();
    if (!els.length) return;

    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : els.length;

    let next = idx + dir;
    if (next < 0) next = 0;
    if (next > els.length - 1) next = els.length - 1;

    const el = els[next];
    if (!el) return;

    try {
      el.focus?.();
      el.scrollIntoView?.({ block: "nearest" });
    } catch { }
  }

  // ---------- RIGHT PANEL LOADERS ----------
  async function loadOrgDetail(organisation_id) {
    const oid = safeTrim(organisation_id);
    if (!oid) return setOrgDetail(null);
    try {
      const det = await tryFetchJson([`/api/organisations/${encodeURIComponent(oid)}`]);
      setOrgDetail(det && typeof det === "object" ? det : null);
    } catch {
      setOrgDetail(null);
    }
  }

const selectedOrgFromList = useMemo(() => {
    const oid = String(form.organisation_id || "");
    if (!oid) return null;
    return orgs.find((o) => String(o.id || "") === oid) || null;
  }, [orgs, form.organisation_id]);

  const selectedRelationIsPrivate = isPrivateRelation(selectedOrgFromList);

  useEffect(() => {
    if (!selectedRelationIsPrivate) return;
    if (!form.contact_id) return;

    setForm((prev) => ({
      ...prev,
      contact_id: "",
    }));
  }, [selectedRelationIsPrivate, form.contact_id]);

  const currentInvoiceEmail = useMemo(() => {
    const selectedRow = items.find((x) => String(x.id || "") === String(form.id || ""));
    return safeTrim(
      orgDetail?.email ||
      selectedOrgFromList?.email ||
      selectedRow?.org_email ||
      ""
    );
  }, [orgDetail, selectedOrgFromList, items, form.id]);

  const currentInvoiceDigital = useMemo(() => {
    const selectedRow = items.find((x) => String(x.id || "") === String(form.id || ""));
    const raw =
      orgDetail?.receives_invoices_digital ??
      orgDetail?.digital_invoices ??
      orgDetail?.invoice_digital ??
      selectedOrgFromList?.receives_invoices_digital ??
      selectedOrgFromList?.digital_invoices ??
      selectedOrgFromList?.invoice_digital ??
      selectedRow?.receives_invoices_digital ??
      selectedRow?.digital_invoices ??
      selectedRow?.invoice_digital ??
      false;

    return raw === true || raw === 1 || raw === "1" || raw === "true" || raw === "ja";
  }, [orgDetail, selectedOrgFromList, items, form.id]);

async function loadPayments(invoiceId) {
  setPayments([]);
  return [];
}

function loadItemFromRowSync(it) {
  const selected = String(it?.id || "");

  setSelectedId(selected);

  const f = {
    ...emptyForm,
    id: selected,
    organisation_id: it?.organisation_id ? String(it.organisation_id) : "",
    contact_id: it?.contact_id ? String(it.contact_id) : "",
    template_id: it?.template_id ? String(it.template_id) : "",
    invoice_number: it?.invoice_number || "",
    series_code: it?.series_code || "",
    invoice_date: isoDate(it?.invoice_date) || "",
    due_date: isoDate(it?.due_date) || "",
    status: normalizeDbStatus(it?.status || "DRAFT"),
    notes: it?.notes || "",
    subtotal: Number(it?.subtotal || 0),
    vat_total: Number(it?.vat_total || 0),
    total: Number(it?.total || 0),
    created_at: it?.created_at || "",
    updated_at: it?.updated_at || "",
  };

  setForm(f);
  setPayError(null);
  setSaveError(null);
  setFlash("");
}

async function loadItemFromRow(it) {
  const selected = String(it?.id || "");

  setLastApprovalInfo(null);
  setSelectedId(selected);
  loadItemFromRowSync(it);

  if (window.innerWidth <= 768) {
    focusFirstRightFieldMobile();
  }

  try {
    const det = await tryFetchJson([`/api/offers/${encodeURIComponent(selected)}`]);

    if (det && typeof det === "object") {
      const offerObj = det.offer ? det.offer : det;

      loadItemFromRowSync({
        ...it,
        ...offerObj,
        id: selected,
      });

      const loadedLines =
        Array.isArray(det.lines) ? det.lines :
        Array.isArray(det.items) ? det.items :
        Array.isArray(offerObj.lines) ? offerObj.lines :
        Array.isArray(offerObj.items) ? offerObj.items :
        [];

      setLines(loadedLines);
    } else {
      setLines([]);
    }
  } catch {
    setLines([]);
  }

  await loadOrgDetail(it?.organisation_id);

  setMode("form");

  if (window.innerWidth <= 768) {
    focusFirstRightFieldMobile();
  } else {
    setTimeout(() => {
      try {
        scrollRightTop();
        orgRef.current?.focus?.();
      } catch {}
    }, 0);
  }
}

  // ---------- ACTIES ----------
  function resetRightPanelEmpty() {
    setSelectedId(null);
    setForm(emptyForm);
    setOrgDetail(null);

    setPayments([]);
    setLines([]);
    setLineDraft({
      line_type: "Artikel",
      quantity: 1,
      description: "",
      unit_price: "",
      vat_rate: "hoog",
    });

    setPayDraft({
      paid_on: new Date().toLocaleDateString("en-CA"),
      amount: "",
      notes: "",
    });

    setPayError(null);
    setSaveError(null);
    setLineError(null);
    setFlash("");
  }

  function resetAllGridFilters() {
    setZoeknaamFilter("");
    setContactFilter("");
    setInvoiceNrFilter("");
    setInvoiceDateFilter("");
    setTelFilter("");
    setMobFilter("");
    setCityFilter("");
    setCountryFilter("");
    setStatusFilter("");
    setCompanyCodeFilter("");
    setPaidFilter("");
  }

function vernieuwList() {
    setQ("");
    resetAllGridFilters();
    resetRightPanelEmpty();
    setLastApprovalInfo(null);
    setListIndex(-1);

    setHideVoiceError(true);
    setSort({ key: "invoice_date", dir: "desc" });
    setReloadKey((k) => k + 1);

    setMode("search");
    if (window.innerWidth > 768) {
      setTimeout(() => {
        try {
          searchRef.current?.focus?.();
        } catch {}
      }, 0);
    }

    setFlash("Vernieuwd ✅");
    setTimeout(() => setFlash(""), 800);
    setTimeout(() => scrollMidTop(), 0);
  }

  function newItem() {
    resetRightPanelEmpty();

    const today = new Date();
    const yyyy = today.getFullYear();
    const mm = String(today.getMonth() + 1).padStart(2, "0");
    const dd = String(today.getDate()).padStart(2, "0");
    const todayIso = `${yyyy}-${mm}-${dd}`;

    const newForm = {
      ...emptyForm,
      organisation_id: "",
      contact_id: "",
      invoice_number: "",
      status: "DRAFT",
      invoice_date: todayIso,
      due_date: "",
    };

    setForm(newForm);
    setMode("form");
    setListIndex(-1);
    setHideVoiceError(true);

    setTimeout(() => {
      try {
        scrollRightTop();
        orgRef.current?.focus?.();
      } catch { }
    }, 0);
  }

async function finalizeInvoice() {
  setSaveError(null);

  const currentStatus = normalizeDbStatus(form.status || "OFFERTE");

  if (currentStatus !== "OFFERTE") {
    setSaveError("Alleen offertes kunnen worden goedgekeurd.");
    return;
  }

  if (!formHasId()) {
    setSaveError("Sla de offerte eerst op.");
    return;
  }

  if (!safeTrim(form.organisation_id)) {
    setSaveError("Kies eerst een organisatie.");
    return;
  }

  if (!safeTrim(form.invoice_date)) {
    setSaveError("Offertedatum is verplicht.");
    return;
  }

  const ok = window.confirm("Weet je zeker dat je deze offerte wilt goedkeuren?");
  if (!ok) return;

  try {
setSilentDocumentPreview(true);
setPopupMode("preview");
setShowLinesPopup(true);

// eerst React laten renderen
await new Promise((resolve) => requestAnimationFrame(resolve));
await new Promise((resolve) => requestAnimationFrame(resolve));

await saveOfferDocumentFromPreview(form.id, "offer");

setShowLinesPopup(false);
setSilentDocumentPreview(false);

    const finalForm = {
      ...form,
      status: "OPDRACHT",
    };

    const payload = {
      organisation_id: finalForm.organisation_id ? Number(finalForm.organisation_id) : null,
      contact_id: finalForm.contact_id ? Number(finalForm.contact_id) : null,
      template_id: finalForm.template_id ? Number(finalForm.template_id) : null,
      invoice_date: finalForm.invoice_date || null,
      due_date: finalForm.due_date || null,
      invoice_number: finalForm.invoice_number || null,
      status: "OPDRACHT",
      series_code: finalForm.series_code || null,
      notes: finalForm.notes || "",
      subtotal: invoiceTotals.subtotal || 0,
      vat_total: invoiceTotals.vatTotal || 0,
      total: invoiceTotals.total || 0,
    };

    const saved = await tryFetchJson([`/api/offers/${finalForm.id}`], {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (saved && typeof saved === "object") {
      setItems((prev) => upsertById(prev, saved));
      setForm((prev) => ({
        ...prev,
        ...saved,
        status: "OPDRACHT",
      }));
    }

    await refreshSelectedInvoice();

    setFlash("Offerte goedgekeurd en opgeslagen bij documenten ✅");
    setTimeout(() => setFlash(""), 1200);
  } catch (e) {
    setShowLinesPopup(false);
    setSilentDocumentPreview(false);
    setSaveError(e?.message || String(e));
  }
}

function buildOfferPrintHtml() {
  const el = popupPrintRef.current;
  if (!el) return "";

  return `
    <html>
      <head>
        <title>${normalizeDbStatus(form.status || "OFFERTE") === "OPDRACHT" ? "Opdracht" : "Offerte"}</title>
        <style>
          @page {
            size: A4;
            margin: 18mm 12mm 12mm 12mm;
          }

          @page:first {
            size: A4;
            margin: 12mm;
          }

          html, body {
            margin: 0;
            padding: 0;
            background: #fff;
            font-family: Arial, Helvetica, sans-serif;
            color: #222;
          }

          body {
            font-size: 12px;
          }

          .print-wrap {
            width: 186mm;
            max-width: 186mm;
            margin: 0 auto;
          }

          button {
            display: none !important;
          }

          .offer-total-footer-block,
          .offer-totals-block,
          .offer-footer-block {
            break-inside: auto !important;
            page-break-inside: auto !important;
            break-before: auto !important;
            page-break-before: auto !important;
            break-after: auto !important;
            page-break-after: auto !important;
          }

          .offer-total-footer-block *,
          .offer-totals-block *,
          .offer-footer-block * {
            break-inside: auto !important;
            page-break-inside: auto !important;
          }

          input, textarea, select {
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
            background: transparent !important;
            appearance: none !important;
            -webkit-appearance: none !important;
            color: #222 !important;
          }

          ::-webkit-scrollbar {
            display: none;
          }
        </style>
      </head>
      <body>
        <div class="print-wrap">
          ${el.innerHTML}
        </div>
      </body>
    </html>
  `;
}

async function waitForOfferPreviewHtml() {
  for (let i = 0; i < 50; i += 1) {

    // popup moet echt bestaan
    if (!popupPrintRef.current) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      continue;
    }

    const html = buildOfferPrintHtml();

    // voldoende html aanwezig?
    if (
      html &&
      html.length > 5000 &&
      html.includes("Aantal") &&
      html.includes("Omschrijving")
    ) {
      return html;
    }

    await new Promise((resolve) => setTimeout(resolve, 100));
  }

  return "";
}

async function saveOfferDocumentFromPreview(offerId, kind = "offer") {
  if (!offerId) return;

  const html = await waitForOfferPreviewHtml();

  if (!html) {
    throw new Error("Offerte/opdracht voorbeeld niet gevonden voor documentopslag.");
  }

  await tryFetchJson(
    [`/api/offers/${encodeURIComponent(offerId)}/save-document-html`],
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ html, kind }),
    }
  );
}

function printOfferPopup() {
  const html = buildOfferPrintHtml();
  if (!html) return;

  const printWindow = window.open("", "_blank", "width=1000,height=800");
  if (!printWindow) return;

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  printWindow.focus();

  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 300);
}


function boolFromAny(value) {
  return value === true || value === 1 || value === "1" || value === "true" || value === "ja" || value === "j";
}

function getApprovalDocumentType() {
  const status = normalizeDbStatus(form.status || "OFFERTE");
  return status === "OPDRACHT" || status === "FACTUUR" ? "order" : "offer";
}

function getApprovalDocumentLabel() {
  return getApprovalDocumentType() === "order" ? "Opdracht" : "Offerte";
}

function getRelationApprovalPreference() {
  const org = activeOrg || selectedOrgFromList || selectedInvoiceRow || {};
  const mobile = safeTrim(org.mobile || org.org_mobile || selectedInvoiceRow?.org_mobile || "");
  const email = safeTrim(org.email || org.org_email || currentInvoiceEmail || "");

  const whatsappAllowed = boolFromAny(org.correspondence_whatsapp) && !!mobile;
  const emailAllowed = boolFromAny(org.correspondence_email) && !!email;

  if (whatsappAllowed) {
    return {
      primary: "whatsapp",
      fallback: emailAllowed ? "email" : "",
      mobile,
      email,
      whatsappAllowed,
      emailAllowed,
    };
  }

  if (emailAllowed) {
    return {
      primary: "email",
      fallback: "",
      mobile,
      email,
      whatsappAllowed,
      emailAllowed,
    };
  }

  return {
    primary: "",
    fallback: "",
    mobile,
    email,
    whatsappAllowed,
    emailAllowed,
  };
}

function openApprovalUrl(url) {
  const u = safeTrim(url);
  if (!u) return;
  window.open(u, "_blank", "noopener,noreferrer");
  setLastApprovalInfo(null);
}

function isMobileDeviceForWhatsapp() {
  try {
    return /Android|iPhone|iPad|iPod|Windows Phone/i.test(navigator.userAgent || "");
  } catch {
    return false;
  }
}

function whatsappAppUrlFromWebUrl(webUrl) {
  const raw = safeTrim(webUrl);
  if (!raw) return "";

  try {
    const parsed = new URL(raw);
    const host = String(parsed.hostname || "").toLowerCase();

    if (!host.includes("wa.me") && !host.includes("whatsapp.com")) {
      return raw;
    }

    let phone = "";

    if (host.includes("wa.me")) {
      phone = parsed.pathname.replace(/^\/+/, "").split("/")[0] || "";
    } else {
      phone = parsed.searchParams.get("phone") || "";
    }

    const text = parsed.searchParams.get("text") || "";
    const params = new URLSearchParams();

    if (phone) params.set("phone", phone.replace(/[^\d]/g, ""));
    if (text) params.set("text", text);

    const query = params.toString();
    return `whatsapp://send${query ? `?${query}` : ""}`;
  } catch {
    return raw;
  }
}

function bestWhatsappOpenUrl(webUrl) {
  const raw = safeTrim(webUrl);
  if (!raw) return "";

  if (isMobileDeviceForWhatsapp()) {
    return whatsappAppUrlFromWebUrl(raw);
  }

  return raw;
}

function openWhatsAppSmart(webUrl) {
  const raw = safeTrim(webUrl);
  if (!raw) return;

  const target = bestWhatsappOpenUrl(raw);

  if (isMobileDeviceForWhatsapp()) {
    try {
      window.location.href = target;
      return;
    } catch {}
  }

  window.open(target, "_blank", "noopener,noreferrer");
}

function escapeAttr(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function writeWhatsappReadyPage(whatsappWindow, webUrl) {
  if (!whatsappWindow || whatsappWindow.closed) return false;

  const raw = safeTrim(webUrl);
  if (!raw) return false;

  const appUrl = bestWhatsappOpenUrl(raw);
  const fallbackUrl = raw;

  try {
    whatsappWindow.document.open();
    whatsappWindow.document.write(`
      <html>
        <head>
          <title>WhatsApp openen</title>
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </head>
        <body style="font-family: Arial, sans-serif; padding: 24px; line-height: 1.45; color: #111;">
          <h2>WhatsApp staat klaar</h2>
          <p>
            Tik op de groene knop hieronder om het bericht in WhatsApp te openen.
            Daarna moet je het bericht zelf nog verzenden.
          </p>

          <p style="margin: 24px 0;">
            <a
              href="${escapeAttr(appUrl)}"
              style="display:inline-block;background:#25D366;color:#111;text-decoration:none;padding:14px 18px;border-radius:8px;font-weight:700;"
            >
              Open WhatsApp
            </a>
          </p>

          <p style="font-size:13px;color:#555;">
            Werkt de groene knop niet? Probeer dan deze koppeling:<br />
            <a href="${escapeAttr(fallbackUrl)}" style="word-break:break-all;">Open via WhatsApp-web</a>
          </p>
        </body>
      </html>
    `);
    whatsappWindow.document.close();

    // We proberen de app alsnog automatisch te openen. Sommige mobiele browsers staan dit
    // na een async actie niet toe; daarom blijft de handmatige knop zichtbaar als fallback.
    if (isMobileDeviceForWhatsapp()) {
      setTimeout(() => {
        try {
          whatsappWindow.location.href = appUrl;
        } catch {}
      }, 250);
    } else {
      try {
        whatsappWindow.location.href = fallbackUrl;
      } catch {}
    }

    return true;
  } catch {
    return false;
  }
}

function chooseApprovalChannel(pref, docLabel) {
  if (pref.whatsappAllowed && pref.emailAllowed) {
    const choice = window.prompt(
      `${docLabel} ${form.invoice_number || form.id} ter akkoord aanbieden.

Kies verzendwijze:
1 = WhatsApp
2 = E-mail
3 = WhatsApp + E-mail`,
      "1"
    );

    const c = String(choice || "").trim().toLowerCase();
    if (!c) return "";
    if (c === "1" || c === "w" || c === "wa" || c === "whatsapp") return "whatsapp";
    if (c === "2" || c === "e" || c === "email" || c === "e-mail" || c === "mail") return "email";
    if (c === "3" || c === "beide" || c === "allebei" || c === "whatsapp + email" || c === "whatsapp + e-mail") return "both";

    alert("Ongeldige keuze. Gebruik 1, 2 of 3.");
    return "";
  }

  if (pref.whatsappAllowed) return "whatsapp";
  if (pref.emailAllowed) return "email";
  return "";
}

function approvalChannelLabel(channel) {
  if (channel === "both") return "WhatsApp + E-mail";
  if (channel === "whatsapp") return "WhatsApp";
  if (channel === "email") return "E-mail";
  return "";
}

async function sendApprovalEmail(approvalId) {
  if (!approvalId) throw new Error("Akkoordverzoek-id ontbreekt.");

  return await tryFetchJson(
    [`/api/document-approvals/${encodeURIComponent(approvalId)}/send-email`],
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    }
  );
}

async function createApprovalRequest(channel) {
  const docType = getApprovalDocumentType();
  const docLabel = getApprovalDocumentLabel();

  const approvalText =
    `Ik verklaar bevoegd te zijn om namens ${recipientName || "de relatie"} akkoord te geven op deze ${docLabel.toLowerCase()} ${form.invoice_number || form.id || ""}.`;

  return await tryFetchJson(["/api/document-approvals"], {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      document_type: docType,
      document_id: Number(form.id),
      channel,
      approval_text: approvalText,
      remarks: "",
    }),
  });
}

async function sendOfferForApproval() {
  setSaveError(null);
  setLastApprovalInfo(null);

  if (!formHasId()) {
    setSaveError("Sla de offerte/opdracht eerst op.");
    return;
  }

  const pref = getRelationApprovalPreference();

  if (!pref.whatsappAllowed && !pref.emailAllowed) {
    setSaveError("Bij deze relatie staat geen akkoord via WhatsApp of E-mail aangevinkt. De huidige procedure blijft van toepassing.");
    return;
  }

  if (!popupPrintRef.current) {
    setSaveError("Open eerst het voorbeeld van de offerte/opdracht.");
    return;
  }

  const docLabel = getApprovalDocumentLabel();
  const chosenChannel = chooseApprovalChannel(pref, docLabel);

  if (!chosenChannel) return;

  if (!(pref.whatsappAllowed && pref.emailAllowed)) {
    const ok = window.confirm(
      `${docLabel} ${form.invoice_number || form.id} ter akkoord aanbieden via ${approvalChannelLabel(chosenChannel)}?`
    );

    if (!ok) return;
  }

  // Belangrijk:
  // Browsers blokkeren vaak window.open na een async actie.
  // Daarom openen we het WhatsApp-tabblad alvast direct vanuit de klikactie.
  let whatsappWindow = null;
  const wantsWhatsapp = chosenChannel === "whatsapp" || chosenChannel === "both";

  if (wantsWhatsapp) {
    try {
      whatsappWindow = window.open("", "_blank");

      if (whatsappWindow) {
        try { whatsappWindow.opener = null; } catch {}
        whatsappWindow.document.open();
        whatsappWindow.document.write(`
          <html>
            <head><title>WhatsApp openen</title></head>
            <body style="font-family: Arial, sans-serif; padding: 24px;">
              <h2>WhatsApp wordt voorbereid...</h2>
              <p>Dit venster wordt zo doorgestuurd naar WhatsApp.</p>
            </body>
          </html>
        `);
        whatsappWindow.document.close();
      }
    } catch {
      whatsappWindow = null;
    }
  }

  try {
    setSendingApproval(true);

    const docType = getApprovalDocumentType();
    await saveOfferDocumentFromPreview(form.id, docType);

    const requestChannel = chosenChannel === "email" ? "email" : "whatsapp";
    const created = await createApprovalRequest(requestChannel);
    const approval = created?.approval || {};
    const approvalId = approval?.id || created?.id;
    const approvalUrl = created?.approval_url || approval?.approval_url || "";
    const whatsappUrl = created?.whatsapp_url || approval?.whatsapp_url || "";

    let popupBlocked = false;
    let emailResult = null;

    if (wantsWhatsapp) {
      if (whatsappUrl) {
        if (whatsappWindow && !whatsappWindow.closed) {
          const readyPageWritten = writeWhatsappReadyPage(whatsappWindow, whatsappUrl);
          if (!readyPageWritten) {
            whatsappWindow.location.href = bestWhatsappOpenUrl(whatsappUrl);
          }
        } else {
          popupBlocked = true;
        }
      } else if (whatsappWindow && !whatsappWindow.closed) {
        try {
          whatsappWindow.close();
        } catch {}
      }
    }

    setLastApprovalInfo({
      approval_id: approvalId,
      approval_url: approvalUrl,
      whatsapp_url: whatsappUrl,
      channel: chosenChannel,
      popup_blocked: popupBlocked,
      sent_by_name: approval?.sent_by_name || "",
      sent_by_email: approval?.sent_by_email || "",
      sent_to_name: approval?.sent_to_name || "",
      sent_to: approval?.sent_to || approval?.sent_to_email || approval?.sent_to_mobile || "",
    });

    if (wantsWhatsapp) {
      if (!whatsappUrl) {
        if (chosenChannel === "whatsapp" && pref.emailAllowed) {
          await sendApprovalEmail(approvalId);
          setFlash("WhatsApp-link kon niet worden gemaakt. Akkoordverzoek per e-mail verstuurd ✅");
          setTimeout(() => setFlash(""), 2200);
          return;
        }

        if (chosenChannel === "whatsapp") {
          setSaveError("WhatsApp-link kon niet worden gemaakt en er is geen e-mail fallback ingesteld.");
          return;
        }
      }

      if (popupBlocked) {
        setFlash("Akkoordlink gemaakt. WhatsApp-popup is geblokkeerd; gebruik de WhatsApp-link hieronder.");
      }
    }

    if (chosenChannel === "email" || chosenChannel === "both") {
      emailResult = await sendApprovalEmail(approvalId);

      const accepted = Array.isArray(emailResult?.accepted)
        ? emailResult.accepted.join(", ")
        : "";

      const rejected = Array.isArray(emailResult?.rejected)
        ? emailResult.rejected.join(", ")
        : "";

      setLastApprovalInfo((prev) => ({
        ...(prev || {}),
        approval_id: approvalId,
        approval_url: approvalUrl,
        whatsapp_url: whatsappUrl,
        channel: chosenChannel,
        popup_blocked: popupBlocked,
        sent_by_name: approval?.sent_by_name || "",
        sent_by_email: approval?.sent_by_email || "",
        sent_to_name: approval?.sent_to_name || "",
        sent_to: approval?.sent_to || approval?.sent_to_email || approval?.sent_to_mobile || "",
        email_to: emailResult?.to || "",
        email_message: emailResult?.message || "",
        email_accepted: accepted,
        email_rejected: rejected,
      }));

      alert(
        `${emailResult?.message || "Akkoordverzoek per e-mail verstuurd."}` +
          `${accepted ? `\nGeaccepteerd door mailserver: ${accepted}` : ""}` +
          `${rejected ? `\nNiet geaccepteerd: ${rejected}` : ""}`
      );
    }

    if (chosenChannel === "both") {
      setFlash(popupBlocked
        ? "Akkoordlink gemaakt. E-mail verstuurd. WhatsApp-popup is geblokkeerd; gebruik de WhatsApp-link hieronder."
        : (isMobileDeviceForWhatsapp() ? "Akkoordlink gemaakt. E-mail verstuurd. WhatsApp staat klaar in het geopende venster ✅" : "Akkoordlink gemaakt. WhatsApp geopend en e-mail verstuurd ✅")
      );
    } else if (chosenChannel === "whatsapp") {
      setFlash(popupBlocked
        ? "Akkoordlink gemaakt. WhatsApp-popup is geblokkeerd; gebruik de WhatsApp-link hieronder."
        : (isMobileDeviceForWhatsapp() ? "Akkoordlink gemaakt. WhatsApp staat klaar in het geopende venster ✅" : "Akkoordlink gemaakt en WhatsApp geopend ✅")
      );
    } else {
      setFlash("Akkoordverzoek per e-mail verstuurd ✅");
    }

    setTimeout(() => setFlash(""), 2200);
  } catch (e) {
    try {
      if (whatsappWindow && !whatsappWindow.closed) whatsappWindow.close();
    } catch {}
    setSaveError(e?.message || String(e));
  } finally {
    setSendingApproval(false);
  }
}

  async function sendOfferEmail() {
    setSaveError(null);

    if (!formHasId()) {
      setSaveError("Sla de offerte eerst op.");
      return;
    }

    const to = currentInvoiceEmail;
    if (!to) {
      setSaveError("Bij deze relatie staat geen e-mailadres.");
      return;
    }

if (!currentInvoiceEmail) {
  setSaveError("Bij deze relatie staat geen e-mailadres.");
  return;
}

    if (!popupPrintRef.current) {
      setSaveError("Offerteweergave niet gevonden.");
      return;
    }

    try {
      setSendingEmail(true);

      const invoiceTitle = form.invoice_number
        ? `Offerte ${form.invoice_number}`
        : `Offerte ${form.id}`;

const printableHtml = buildOfferPrintHtml();

      const mailResult = await tryFetchJson(
        [`/api/offers/${encodeURIComponent(form.id)}/send-email`],
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            to,
            subject: invoiceTitle,
            invoiceNumber: form.invoice_number || String(form.id),
            html: printableHtml,
            total: invoiceTotals.total,
          }),
        }
      );

      const serverMessage = mailResult?.message || `Offerte/Opdracht verstuurd naar ${to}`;
      const accepted = Array.isArray(mailResult?.accepted) ? mailResult.accepted.join(", ") : "";
      const rejected = Array.isArray(mailResult?.rejected) ? mailResult.rejected.join(", ") : "";

      alert(`${serverMessage}${accepted ? `\nGeaccepteerd door mailserver: ${accepted}` : ""}${rejected ? `\nNiet geaccepteerd: ${rejected}` : ""}`);
      setFlash(`${serverMessage} ✅`);
      setTimeout(() => setFlash(""), 1500);
    } catch (e) {
      setSaveError(e?.message || String(e));
    } finally {
      setSendingEmail(false);
    }
  }

async function saveItem() {
  if (isFormLocked || isReadonly) return;

  setSaveError(null);
  setFlash("");
  setLastApprovalInfo(null);

    if (isReadonly) {
      setSaveError("Deze gebruiker heeft alleen-lezen rechten.");
      return;
    }

    if (!safeTrim(form.organisation_id)) return setSaveError("Kies eerst een relatie.");
    if (!safeTrim(form.invoice_date)) return setSaveError("Offertedatum is verplicht.");

    const payload = {
      organisation_id: form.organisation_id ? Number(form.organisation_id) : null,
      contact_id: form.contact_id ? Number(form.contact_id) : null,
      template_id: form.template_id ? Number(form.template_id) : null,
      invoice_date: form.invoice_date || null,
      due_date: form.due_date || null,
      invoice_number: form.invoice_number || null,
      status: (form.status || "DRAFT"),
      series_code: form.series_code || null,
      notes: form.notes || "",
    };

const isUpdate = formHasId();
const method = isUpdate ? "PUT" : "POST";
const urls = isUpdate ? [`/api/offers/${form.id}`] : ["/api/offers"];

    try {
      const saved = await tryFetchJson(urls, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const savedId = saved?.id || form.id;

      if (saved && typeof saved === "object") {
        setItems((prev) => upsertById(prev, saved));
      }

      const pendingLine = getPendingLinePayload();

if (savedId && pendingLine) {
  await tryFetchJson(["/api/offer-lines"], {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      ...pendingLine,
      offer_id: Number(savedId),
      line_no: 1,
    }),
  });
}

      resetRightPanelEmpty();
      setListIndex(-1);

      setFlash("Opgeslagen ✅");
      setTimeout(() => setFlash(""), 800);
      setReloadKey((k) => k + 1);

      setMode("search");
      setTimeout(() => {
        try {
          scrollRightTop();
          scrollMidTop();
          searchRef.current?.focus?.();
        } catch { }
      }, 0);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }
  async function openOfferPopupEdit() {
    setPopupMode("edit");
    await saveItemForPopup();
  }

  async function openOfferPopupPreview() {
    setPopupMode("preview");
    await saveItemForPopup();
  }
  async function saveItemForPopup() {
    setSaveError(null);
    setFlash("");

    if (isLocked) {
      if (formHasId()) {
        await refreshSelectedInvoice();
        setShowLinesPopup(true);
      }
      return;
    }

    if (!safeTrim(form.organisation_id)) {
      setSaveError("Kies eerst een organisatie.");
      return;
    }

    if (!safeTrim(form.invoice_date)) {
      setSaveError("Offertedatum is verplicht.");
      return;
    }

    // Bestaat al? Dan gewoon popup openen met verse data
    if (formHasId()) {
      await refreshSelectedInvoice();
      setShowLinesPopup(true);
      return;
    }

    const payload = {
      organisation_id: form.organisation_id ? Number(form.organisation_id) : null,
      contact_id: form.contact_id ? Number(form.contact_id) : null,
      template_id: form.template_id ? Number(form.template_id) : null,
      invoice_date: form.invoice_date || null,
      due_date: form.due_date || null,
      invoice_number: form.invoice_number || null,
      status: (form.status || "DRAFT"),
      series_code: form.series_code || null,
      notes: form.notes || "",
    };

    try {
const saved = await tryFetchJson(["/api/offers"], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const newId = saved?.id ? String(saved.id) : "";

      if (saved && typeof saved === "object") {
        setItems((prev) => upsertById(prev, saved));
      }

      if (newId) {
        setSelectedId(newId);

        setForm((prev) => ({
          ...prev,
          ...saved,
          id: newId,
          organisation_id: saved?.organisation_id ? String(saved.organisation_id) : prev.organisation_id,
          contact_id: saved?.contact_id ? String(saved.contact_id) : prev.contact_id,
          status: saved?.status || prev.status || "DRAFT",
        }));

        await loadOrgDetail(saved?.organisation_id || form.organisation_id);
        await loadPayments(newId);

        const pendingLine = getPendingLinePayload();

        if (pendingLine) {
await tryFetchJson(["/api/offer-lines"], {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    ...pendingLine,
    offer_id: Number(newId),
    line_no: 1,
  }),
});
          setLineDraft({
            line_type: "Artikel",
            quantity: 1,
            description: "",
            unit_price: "",
            vat_rate: pickOrgDefaultVatRate(activeOrg),
          });
        }

        await refreshSelectedInvoice();
      }

      setFlash("Offerte opgeslagen ✅");
      setTimeout(() => setFlash(""), 800);
      setShowLinesPopup(true);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

async function deleteItem() {
  if (isFormLocked) return;

  setSaveError(null);
  setFlash("");
  setLastApprovalInfo(null);

    if (!formHasId()) return;
    if (isLocked) return setSaveError("Gefactureerde facturen kun je niet verwijderen (crediteer-route volgt).");

    const ok = window.confirm("Weet je zeker dat je deze offerte wilt verwijderen?");
    if (!ok) return;

    const id = form.id;
    try {
      await tryFetchJson([`/api/offers/${encodeURIComponent(form.id)}`], {
  method: "DELETE",
});
      setItems((prev) => removeById(prev, id));
      resetRightPanelEmpty();
      setListIndex(-1);

      setFlash("Verwijderd ✅");
      setTimeout(() => setFlash(""), 800);

      setMode("search");
      setTimeout(() => {
        try {
          scrollRightTop();
          scrollMidTop();
          searchRef.current?.focus?.();
        } catch { }
      }, 0);
    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }

  // ---------- PAYMENTS ----------
  const paidTotal = useMemo(() => payments.reduce((s, p) => s + Number(p.amount || 0), 0), [payments]);
  const openAmount = useMemo(() => Number(form.total || 0) - paidTotal, [form.total, paidTotal]);
  const paidLabel = useMemo(() => boolPaidLabel(openAmount), [openAmount]);


  async function addPayment() {
    setPayError(null);

    if (!formHasId()) {
      return setPayError("Sla eerst de offerte op, voordat je betalingen toevoegt.");
    }

    const currentStatus = normalizeDbStatus(form.status || "DRAFT");
    if (currentStatus === "DRAFT") {
      return setPayError("Bij een conceptofferte mogen nog geen betalingen worden ingevoerd.");
    }

    const paid_on = isoDate(payDraft.paid_on);
    const amount = Number(String(payDraft.amount || "").replace(",", "."));

    if (!paid_on) return setPayError("Kies een betaaldatum.");
    if (!Number.isFinite(amount) || amount <= 0) {
      return setPayError("Bedrag moet > 0 zijn.");
    }

    const payload = {
      paid_on,
      amount,
      notes: payDraft.notes || "",
    };

    try {
      const saved = await tryFetchJson([`/api/invoices/${form.id}/payments`], {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      let nextPayments;

      if (saved && typeof saved === "object") {
        nextPayments = [...payments, saved].sort((a, b) =>
          isoDate(a.paid_on) < isoDate(b.paid_on) ? -1 : 1
        );
        setPayments(nextPayments);
      } else {
        await loadPayments(form.id);
        nextPayments = payments;
      }

      setPayDraft({
        paid_on: new Date().toLocaleDateString("en-CA"),
        amount: "",
        notes: "",
      });

      setFlash("Betaling toegevoegd ✅");
      setTimeout(() => setFlash(""), 800);

      await refreshSelectedInvoice();
    } catch (e) {
      setPayError(e?.message || String(e));
    }
  }

  async function deletePayment(pid) {
    setPayError(null);
    if (!formHasId()) return;
    const ok = window.confirm("Betaling verwijderen?");
    if (!ok) return;

    try {
      await tryFetchJson([`/api/invoices/${form.id}/payments/${pid}`], { method: "DELETE" });

      const nextPayments = payments.filter((x) => String(x.id) !== String(pid));
      setPayments(nextPayments);

      setFlash("Betaling verwijderd ✅");
      setTimeout(() => setFlash(""), 800);
      await refreshSelectedInvoice();
    } catch (e) {
      setPayError(e?.message || String(e));
    }
  }

  // ---------- OFFERTEREGELS ----------
  async function refreshSelectedInvoice() {
    if (!formHasId()) return;
    try {
      const det = await tryFetchJson([`/api/offers/${encodeURIComponent(form.id)}`]);
      const invoiceObj = det && det.invoice ? det.invoice : det;

      if (invoiceObj) {
        setForm((prev) => ({
          ...prev,
          ...invoiceObj,
          status: normalizeDbStatus(invoiceObj.status || prev.status || "DRAFT"),
        }));
      }

      const loadedLines =
        Array.isArray(det?.lines) ? det.lines :
          Array.isArray(det?.items) ? det.items :
            Array.isArray(invoiceObj?.lines) ? invoiceObj.lines :
              Array.isArray(invoiceObj?.items) ? invoiceObj.items :
                [];

      setLines(loadedLines);

      // refresh list (totals / status)
      await loadList();
    } catch (e) {
      // ignore
    }
  }
  function getPendingLinePayload() {
    const type = normalizeOfferLineType(lineDraft.line_type || "Artikel");
    const qty = numberFromAny(lineDraft.quantity, 0);
    const unitNumber = numberFromAny(lineDraft.unit_price, 0);
    const unit = Number.isFinite(unitNumber) ? Number(unitNumber.toFixed(2)) : NaN;
    const desc = String(lineDraft.description || "").trim();
    const vat = String(lineDraft.vat_rate || "hoog").toLowerCase();
    const isArticle = type === "Artikel";

    if (!desc) return null;

    if (isArticle) {
      if (!Number.isFinite(qty) || qty <= 0) return null;
      if (!Number.isFinite(unit) || unit < 0) return null;
    }

    return {
      line_type: backendOfferLineType(type),
      lineType: type,
      type,
      regeltype: type,
      description: desc,

      quantity: isArticle ? qty : 0,
      qty: isArticle ? qty : 0,
      aantal: isArticle ? qty : 0,

      unit_price: isArticle ? unit : 0,
      price: isArticle ? unit : 0,
      unitPrice: isArticle ? unit : 0,
      bedrag: isArticle ? unit : 0,
      amount: isArticle ? unit : 0,

      vat_rate: vat,
      vat: vat,
      btw: vat,
    };
  }


function buildOfferLineUpdatePayload(ln, lineNo) {
  const type = lineTypeOf(ln);
  const isArticle = type === "Artikel";

  return {
    line_no: Number(lineNo || 0),

    line_type: backendOfferLineType(type),
    lineType: type,
    type,
    regeltype: type,

    description: lineDescription(ln),

    quantity: isArticle ? lineQty(ln) : 0,
    qty: isArticle ? lineQty(ln) : 0,
    aantal: isArticle ? lineQty(ln) : 0,

    unit_price: isArticle ? lineUnitPrice(ln) : 0,
    price: isArticle ? lineUnitPrice(ln) : 0,
    unitPrice: isArticle ? lineUnitPrice(ln) : 0,
    bedrag: isArticle ? lineUnitPrice(ln) : 0,
    amount: isArticle ? lineUnitPrice(ln) : 0,

    vat_rate: lineVatRate(ln),
    vat: lineVatRate(ln),
    btw: lineVatRate(ln),
  };
}

async function persistOfferLineOrder(nextLines) {
  const ordered = Array.isArray(nextLines) ? nextLines : [];

  for (let i = 0; i < ordered.length; i += 1) {
    const ln = ordered[i];
    if (!ln?.id) continue;

    await tryFetchJson([`/api/offer-lines/${encodeURIComponent(ln.id)}`], {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(buildOfferLineUpdatePayload(ln, i + 1)),
    });
  }
}

async function reorderOfferLine(lineId, targetLineId) {
  if (!lineId || !targetLineId) return;
  if (String(lineId) === String(targetLineId)) return;
  if (isLocked || popupMode === "preview") return;

  const fromIndex = lines.findIndex((ln) => String(ln.id) === String(lineId));
  const toIndex = lines.findIndex((ln) => String(ln.id) === String(targetLineId));

  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;

  const previousLines = lines.slice();
  const nextLines = lines.slice();
  const [moved] = nextLines.splice(fromIndex, 1);
  nextLines.splice(toIndex, 0, moved);

  setLines(nextLines);
  setLineError(null);

  try {
    await persistOfferLineOrder(nextLines);
    setFlash("Volgorde aangepast ✅");
    setTimeout(() => setFlash(""), 900);
    await refreshSelectedInvoice();
  } catch (e) {
    setLines(previousLines);
    setLineError(e?.message || "Volgorde wijzigen mislukt.");
  }
}

async function moveOfferLineStep(lineId, direction) {
  if (!lineId || isLocked || popupMode === "preview") return;

  const idx = lines.findIndex((ln) => String(ln.id) === String(lineId));
  if (idx < 0) return;

  const nextIdx = idx + Number(direction || 0);
  if (nextIdx < 0 || nextIdx >= lines.length) return;

  const previousLines = lines.slice();
  const nextLines = lines.slice();
  const [moved] = nextLines.splice(idx, 1);
  nextLines.splice(nextIdx, 0, moved);

  setLines(nextLines);
  setLineError(null);

  try {
    await persistOfferLineOrder(nextLines);
    setFlash("Volgorde aangepast ✅");
    setTimeout(() => setFlash(""), 900);
    await refreshSelectedInvoice();
  } catch (e) {
    setLines(previousLines);
    setLineError(e?.message || "Volgorde wijzigen mislukt.");
  }
}


async function addInvoiceLine() {
  setLineError(null);

  if (!formHasId()) {
    setLineError("Sla eerst de offerte op.");
    return false;
  }

  if (isLocked) {
    setLineError("Deze offerte is gefactureerd/verzonden/betaald; regels zijn niet meer aanpasbaar.");
    return false;
  }

  const isEdit = !!editingLineId;
  const type = normalizeOfferLineType(lineDraft.line_type || "Artikel");
  const qty = numberFromAny(lineDraft.quantity, 0);
  const unitNumber = numberFromAny(lineDraft.unit_price, 0);
  const unit = Number.isFinite(unitNumber) ? Number(unitNumber.toFixed(2)) : NaN;
  const desc = String(lineDraft.description || "").trim();
  const vat = String(lineDraft.vat_rate || "hoog").toLowerCase();
  const isArticle = type === "Artikel";

  // vOFF-035: onthoud positie vóór opslaan, zodat de pagina na opslaan
  // niet naar onder/boven springt bij het opnieuw laden van offerteregels.
  const scrollSnapshot = {
    windowX: window.scrollX || 0,
    windowY: window.scrollY || 0,
    midTop: midScrollRef.current?.scrollTop ?? null,
    rightTop: rightScrollRef.current?.scrollTop ?? null,
  };

  function restoreScrollAfterLineSave() {
    setTimeout(() => {
      try {
        if (scrollSnapshot.midTop !== null && midScrollRef.current) {
          midScrollRef.current.scrollTop = scrollSnapshot.midTop;
        }
      } catch {}

      try {
        if (scrollSnapshot.rightTop !== null && rightScrollRef.current) {
          rightScrollRef.current.scrollTop = scrollSnapshot.rightTop;
        }
      } catch {}

      try {
        window.scrollTo(scrollSnapshot.windowX, scrollSnapshot.windowY);
      } catch {}

      try {
        if (isEdit) {
          const idx = lines.findIndex((ln) => String(ln.id) === String(editingLineId));
          const nextLine = idx >= 0 ? lines[idx + 1] : null;

          if (nextLine?.id) {
            const nextButton = document.querySelector(
              `[data-offer-line-edit-id="${String(nextLine.id)}"]`
            );

            nextButton?.scrollIntoView?.({
              block: "center",
              inline: "nearest",
              behavior: "auto",
            });
          }
        } else {
          lineDescRef.current?.focus?.({ preventScroll: true });
        }
      } catch {}
    }, 80);
  }

  if (!desc) {
    setLineError("Omschrijving is verplicht.");
    restoreScrollAfterLineSave();
    return false;
  }

  if (isArticle) {
    if (!Number.isFinite(qty) || qty <= 0) {
      setLineError("Aantal moet groter dan 0 zijn.");
      restoreScrollAfterLineSave();
      return false;
    }

    if (!Number.isFinite(unit) || unit < 0) {
      setLineError("Bedrag (per stuk) mag niet negatief zijn.");
      restoreScrollAfterLineSave();
      return false;
    }
  }

  const payload = {
    line_type: backendOfferLineType(type),
    lineType: type,
    type,
    regeltype: type,
    description: desc,

    quantity: isArticle ? qty : 0,
    qty: isArticle ? qty : 0,
    aantal: isArticle ? qty : 0,

    unit_price: isArticle ? unit : 0,
    price: isArticle ? unit : 0,
    unitPrice: isArticle ? unit : 0,
    bedrag: isArticle ? unit : 0,
    amount: isArticle ? unit : 0,

    vat_rate: vat,
    vat: vat,
    btw: vat,
  };

  try {
    const saved = await tryFetchJson(
      isEdit
        ? [`/api/offer-lines/${encodeURIComponent(editingLineId)}`]
        : ["/api/offer-lines"],
      {
        method: isEdit ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          isEdit
            ? payload
            : { ...payload, offer_id: Number(form.id), line_no: lines.length + 1 }
        ),
      }
    );

    const savedLineRaw =
      saved?.line ||
      saved?.invoice_line ||
      saved?.data ||
      saved;

    const savedLine =
      savedLineRaw && typeof savedLineRaw === "object"
        ? { ...savedLineRaw, ...payload, id: savedLineRaw.id ?? editingLineId }
        : null;

    if (savedLine) {
      if (isEdit) {
        setLines((prev) =>
          prev.map((ln) =>
            String(ln.id) === String(editingLineId) ? { ...ln, ...savedLine } : ln
          )
        );
      } else {
        setLines((prev) => [...prev, savedLine]);
      }
    }

    setEditingLineId(null);
    setSelectedProjectLineId("__manual__");
    setIncludeChildProjects(false);

    setLineDraft({
      line_type: "Artikel",
      quantity: 1,
      description: "",
      unit_price: "",
      vat_rate: pickOrgDefaultVatRate(activeOrg),
    });

    setFlash(isEdit ? "Offerteregel gewijzigd ✅" : "Offerteregel toegevoegd ✅");
    setTimeout(() => setFlash(""), 900);

    await refreshSelectedInvoice();

    restoreScrollAfterLineSave();

    return true;
  } catch (e) {
    setLineError(e?.message || String(e));
    restoreScrollAfterLineSave();
    return false;
  }
}

function formatLineUnitPriceValue(raw) {
    const s = String(raw || "").trim();
    if (!s) return "";

    const normalized = s.replace(",", ".");
    const num = Number(normalized);
    if (!Number.isFinite(num)) return s;

    return num.toFixed(2).replace(".", ",");
  }

  function handleUnitPriceCommit() {
    setLineDraft((prev) => ({
      ...prev,
      unit_price: formatLineUnitPriceValue(prev.unit_price),
    }));
  }

function startEditInvoiceLine(ln) {
  setLineError(null);
  setEditingLineId(ln.id);

  setLineDraft({
    line_type: lineTypeOf(ln),
    quantity: String(lineQty(ln) || 1),
    description: String(lineDescription(ln) || ""),
    unit_price: String(lineUnitPrice(ln) || "").replace(".", ","),
    vat_rate: String(lineVatRate(ln) || "hoog"),
  });

  setTimeout(() => {
    try {
      qtyInputRef.current?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });

      qtyInputRef.current?.focus?.();
      qtyInputRef.current?.select?.();

      if (lineDescRef.current) {
        lineDescRef.current.style.height = "auto";
        lineDescRef.current.style.height =
          lineDescRef.current.scrollHeight + "px";
      }
    } catch {}
  }, 50);
}

function cancelEditInvoiceLine() {
  setEditingLineId(null);
  setLineError(null);
  setLineDraft({
    line_type: "Artikel",
    quantity: 1,
    description: "",
    unit_price: "",
    vat_rate: pickOrgDefaultVatRate(activeOrg),
  });
}

function getOnderliggendeProjecten(parentId) {
  const result = [];

  function walk(pid) {
    const children = projectItems
      .filter((x) => String(x.parent_id || "") === String(pid || ""))
      .sort((a, b) =>
        String(a.full_code || a.code || "").localeCompare(
          String(b.full_code || b.code || ""),
          "nl"
        )
      );

    children.forEach((child) => {
      result.push(child);
      walk(child.id);
    });
  }

  walk(parentId);

  return result;
}

async function voegProjectRegelsToe(projectId) {
  if (!formHasId()) {
    setLineError("Sla eerst de offerte op.");
    return;
  }

  const hoofd = projectItems.find(
    (x) => String(x.id) === String(projectId)
  );

  if (!hoofd) return;

  const alleRegels = [hoofd];

  if (includeChildProjects) {
    alleRegels.push(...getOnderliggendeProjecten(hoofd.id));
  }

  const bruikbareRegels = alleRegels.filter((r) => {
    const hasPrijs = Number(r.price || 0) > 0;
    const hasTekst =
      safeTrim(r.offer_text) ||
      safeTrim(r.name);

    return hasPrijs || hasTekst;
  });

  try {
    for (const r of bruikbareRegels) {
      const omschrijving =
        safeTrim(r.offer_text) ||
        safeTrim(r.name) ||
        safeTrim(r.full_name) ||
        "";

      const type = lineTypeOf(r);
      const price = Number(r.price || 0);

      const payload = {
        offer_id: Number(form.id),
        line_no: lines.length + 1,

        line_type: backendOfferLineType(type),
        lineType: type,
        type,
        regeltype: type,
        description: omschrijving,

        quantity: type === "Artikel" ? 1 : 0,
        qty: type === "Artikel" ? 1 : 0,

        unit_price: type === "Artikel" ? price : 0,
        price: type === "Artikel" ? price : 0,
        unitPrice: type === "Artikel" ? price : 0,
        bedrag: type === "Artikel" ? price : 0,
        amount: type === "Artikel" ? price : 0,

        vat_rate: normalizeVatRate(r.vat_rate || "hoog"),
        vat: normalizeVatRate(r.vat_rate || "hoog"),
      };

      await tryFetchJson(
        ["/api/offer-lines"],
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
    }

await refreshSelectedInvoice();

setSelectedProjectLineId("__manual__");
    setIncludeChildProjects(false);

    setLineDraft({
      line_type: "Artikel",
      quantity: 1,
      description: "",
      unit_price: "",
      vat_rate: pickOrgDefaultVatRate(activeOrg),
    });

    setTimeout(() => {
      try {
        lineDescRef.current?.focus?.();
      } catch {}
    }, 80);

    setFlash(
      includeChildProjects
        ? "Project + onderliggende onderdelen toegevoegd ✅"
        : "Project toegevoegd ✅"
    );

    setTimeout(() => setFlash(""), 1200);

  } catch (e) {
    setLineError(e?.message || String(e));
  }
}

function vulRegelUitProjectbeheer(projectId) {
  setSelectedProjectLineId(projectId);

  if (!projectId) {
    setLineDraft({
      line_type: "Artikel",
      quantity: 1,
      description: "",
      unit_price: "",
      vat_rate: pickOrgDefaultVatRate(activeOrg),
    });
    return;
  }

if (projectId === "__manual__") {
    setLineDraft({
      line_type: "Artikel",
      quantity: 1,
      description: "",
      unit_price: "",
      vat_rate: pickOrgDefaultVatRate(activeOrg),
    });

    setTimeout(() => {
      try {
        lineDescRef.current?.focus?.();
      } catch {}
    }, 50);

    return;
  }

const p = projectItems.find((x) => String(x.id) === String(projectId));
if (!p) return;

if (includeChildProjects) {
  voegProjectRegelsToe(projectId);
  return;
}

  const omschrijving =
    safeTrim(p.offer_text) ||
    safeTrim(p.name) ||
    safeTrim(p.full_name) ||
    "";

  const projectLineType = lineTypeOf(p);

  setLineDraft({
    line_type: projectLineType,
    quantity: projectLineType === "Artikel" ? 1 : 0,
    description: omschrijving,
    unit_price: projectLineType === "Artikel" ? String(p.price ?? "").replace(".", ",") : "",
    vat_rate: normalizeVatRate(p.vat_rate || "hoog"),
  });

  setTimeout(() => {
    try {
      if (lineDescRef.current) {
        lineDescRef.current.style.height = "auto";
        lineDescRef.current.style.height =
          lineDescRef.current.scrollHeight + "px";
      }
    } catch {}
  }, 50);
}

async function deleteInvoiceLine(lineId) {
  setLineError(null);

  if (!formHasId()) return;

  if (isLocked) {
    setLineError("Deze offerte is verzonden; regels zijn niet meer aanpasbaar.");
    return;
  }

  const ok = window.confirm("Offerteregel verwijderen?");
  if (!ok) return;

  try {
    await tryFetchJson([`/api/offer-lines/${encodeURIComponent(lineId)}`], {
      method: "DELETE",
    });

    setLines((prev) => prev.filter((x) => String(x.id) !== String(lineId)));

    setFlash("Offerteregel verwijderd ✅");
    setTimeout(() => setFlash(""), 900);

    await refreshSelectedInvoice();
  } catch (e) {
    setLineError(e?.message || String(e));
  }
}

  // ---------- SPEECH ----------

  function isPopupOpen() {
    return !!showLinesPopup;
  }

  function isPopupField(el) {
    if (!el) return false;
    return (
      el === qtyInputRef.current ||
      el === lineDescRef.current ||
      el === linePriceRef.current ||
      el === lineVatRef.current
    );
  }

  function popupFocusOrder() {
    const arr = [qtyInputRef.current, lineDescRef.current, linePriceRef.current, lineVatRef.current]
      .filter(Boolean)
      .filter((el) => !el.disabled);
    return arr;
  }

function focusFirstPopupField() {
    setTimeout(() => {
      try {
        lineDescRef.current?.focus?.();
      } catch { }
    }, 120);
  }

  function focusNextPopupField(dir = +1) {
    const arr = popupFocusOrder();
    if (!arr.length) return;

    const active = document.activeElement;
    let idx = arr.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : arr.length;

    let next = idx + dir;
    if (next < 0) next = 0;
    if (next > arr.length - 1) next = arr.length - 1;

    const el = arr[next];
    if (!el) return;

    try {
      el.focus?.();
      if (el.tagName?.toLowerCase() === "input" || el.tagName?.toLowerCase() === "textarea") {
        el.select?.();
      }
    } catch { }
  }

  function popupPunctMap(t) {
    const x = norm(t);
    if (x === "punt") return ".";
    if (x === "komma") return ",";
    if (x === "dubbele punt" || x === "dubbelpunt") return ":";
    if (x === "puntkomma") return ";";
    if (x === "vraagteken") return "?";
    if (x === "uitroepteken") return "!";
    if (x === "nieuwe regel" || x === "enter" || x === "nieuwe regel maken") return "\n";
    return null;
  }

  function normalizePopupQty(raw) {
    const s = String(raw || "").trim().replace(",", ".");
    const n = Number(s);
    if (!Number.isFinite(n) || n <= 0) return "";
    return String(n);
  }

  function normalizePopupPrice(raw) {
    const s = String(raw || "").trim().replace(",", ".");
    const n = Number(s);
    if (!Number.isFinite(n) || n <= 0) return "";
    return n.toFixed(2).replace(".", ",");
  }

  function popupVatFromSpeech(raw) {
    const x = norm(raw);
    if (x.includes("21") || x.includes("hoog")) return "hoog";
    if (x.includes("9") || x.includes("laag")) return "laag";
    if (x === "0" || x.includes("nul")) return "0";
    if (x.includes("verlegd")) return "verlegd";
    return "";
  }

  function applyDictationToPopupField(raw0) {
    const el = document.activeElement;
    if (!isPopupOpen() || !isPopupField(el)) return false;

    if (el === qtyInputRef.current) {
      const v = normalizePopupQty(raw0);
      if (!v) return false;
      setLineDraft((prev) => ({ ...prev, quantity: v }));
      return true;
    }

    if (el === lineDescRef.current) {
      const punct = popupPunctMap(raw0);

      if (punct !== null) {
        setLineDraft((prev) => ({
          ...prev,
          description: `${String(prev.description || "")}${punct}`,
        }));
        return true;
      }

      let txt = String(raw0 || "").trim()
        .replace(/\bnieuwe regel\b/gi, "\n")
        .replace(/\bpuntkomma\b/gi, ";")
        .replace(/\bdubbele punt\b/gi, ":")
        .replace(/\buitroepteken\b/gi, "!")
        .replace(/\bvraagteken\b/gi, "?")
        .replace(/\bkomma\b/gi, ",")
        .replace(/\bpunt\b/gi, ".")
        .replace(/\s+([.,!?;:])/g, "$1");

      txt = txt.replace(/(^|[\n.!?]\s*)([a-zà-ÿ])/g, (_, p1, p2) => p1 + p2.toUpperCase());

      setLineDraft((prev) => {
        const cur = String(prev.description || "");
        const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";
        return { ...prev, description: cur + sep + txt };
      });
      return true;
    }

    if (el === linePriceRef.current) {
      const v = normalizePopupPrice(raw0);
      if (!v) return false;
      setLineDraft((prev) => ({ ...prev, unit_price: v }));
      return true;
    }

    if (el === lineVatRef.current) {
      const v = popupVatFromSpeech(raw0);
      if (!v) return false;
      setLineDraft((prev) => ({ ...prev, vat_rate: v }));
      return true;
    }

    return false;
  }

  function inFormContext() {
    try {
      const el = document.activeElement;
      if (!el) return false;

      if (isPopupOpen() && isPopupField(el)) return true;

      const inRight = rightScrollRef.current?.contains?.(el) || false;

      // Alleen echte invoer-velden in rechterblok tellen als formulier-context
      const tag = (el.tagName || "").toLowerCase();
      const isInputLike =
        tag === "input" ||
        tag === "textarea" ||
        tag === "select" ||
        el.isContentEditable;

      return inRight && isInputLike;
    } catch {
      return false;
    }
  }

async function closeOfferPopup() {
  setLineError(null);

  const pendingLine = getPendingLinePayload();

  if (pendingLine) {
    await addInvoiceLine();
  }

  setShowLinesPopup(false);
}

function annuleerOfferPopup() {
  setLineError(null);
  setEditingLineId(null);
  setSelectedProjectLineId("__manual__");
  setIncludeChildProjects(false);

  setLineDraft({
    line_type: "Artikel",
    quantity: 1,
    description: "",
    unit_price: "",
    vat_rate: pickOrgDefaultVatRate(activeOrg),
  });

  setShowLinesPopup(false);
}

  function handleVoiceCommandLike(raw) {
    const t = norm(raw);
    const parts = t.split(" ").filter(Boolean);
    const w1 = parts[0] || "";
    const active = document.activeElement;

    if (w1 === "offerte") {
      saveItemForPopup();
      return true;
    }

if (w1 === "sluiten" && isPopupOpen()) {
  closeOfferPopup();
  return true;
}

    if (w1 === "regel" && isPopupOpen()) {
      addInvoiceLine();
      return true;
    }

    if (w1 === "volgende") {
      if (isPopupOpen() && isPopupField(active)) {
        if (active === lineVatRef.current) {
          addInvoiceLine();
          setTimeout(() => {
            qtyInputRef.current?.focus?.();
            qtyInputRef.current?.select?.();
          }, 0);
        } else {
          focusNextPopupField(+1);
        }
        return true;
      }

      if (inFormContext()) {
        if (active === payDateRef.current) {
          setTimeout(() => {
            payAmtRef.current?.focus?.();
          }, 0);
        } else if (active === payAmtRef.current) {
          addPayment();
          setTimeout(() => {
            payNotesRef.current?.focus?.();
          }, 0);
        } else {
          focusNextField(+1);
        }
      } else {
        moveCursor(+1);
      }
      return true;
    }

    if (w1 === "vorige") {
      if (isPopupOpen() && isPopupField(active)) {
        focusNextPopupField(-1);
        return true;
      }

      if (inFormContext()) {
        focusNextField(-1);
      } else {
        moveCursor(-1);
      }
      return true;
    }

if (w1 === "terug" || w1 === "links") {
  if (isPopupOpen()) {
    closeOfferPopup();
    return true;
  }

      resetRightPanelEmpty();
      setMode("search");
      setTimeout(() => {
        searchRef.current?.focus?.();
      }, 0);
      return true;
    }

if (w1 === "rechts" || w1 === "selecteer" || w1 === "selecteren" || w1 === "item") {
      openCurrentRowToRight();
      return true;
    }

    if (w1 === "nieuw" || w1 === "nieuwe") {
      if (isPopupOpen()) {
        setEditingLineId(null);
        setLineDraft({
          line_type: "Artikel",
          quantity: 1,
          description: "",
          unit_price: "",
          vat_rate: pickOrgDefaultVatRate(activeOrg),
        });
        focusFirstPopupField();
        return true;
      }

      newItem();
      return true;
    }

    if (w1 === "vernieuw" || w1 === "vernieuwen") {
      vernieuwList();
      return true;
    }

    if (w1 === "opslaan" || w1 === "bewaren") {
      if (isPopupOpen()) {
        addInvoiceLine();
        return true;
      }

      saveItem();
      return true;
    }

    if (w1 === "verwijder" || w1 === "verwijderen" || w1 === "delete") {
      deleteItem();
      return true;
    }

    if (w1 === "betaling") {
      addPayment();
      setTimeout(() => {
        payNotesRef.current?.focus?.();
      }, 0);
      return true;
    }

    return false;
  }

  function highlightItemIndex(idx) {
    const L = sortedItems.length;
    if (L <= 0) return;
    const clamped = clampIndex(idx, L);
    setListIndex(clamped);
    setTimeout(() => scrollRowIntoView(clamped), 0);
  }

function openCurrentRowToRight() {
    const L = sortedItems.length;
    if (L <= 0) return;

    const idx = ensureCursorExists();
    if (idx < 0) return;

    const it = sortedItems[idx];
    if (!it) return;

    setListIndex(idx);
    loadItemFromRow(it);
    setMode("form");

    if (window.innerWidth <= 768) {
      focusFirstRightFieldMobile();
    } else {
      focusFirstRightField();
    }

    setTimeout(() => scrollRowIntoView(idx), 0);
  }

  function getFocusableElements() {
    const els = focusOrderRefs.map((r) => r?.current).filter(Boolean);
    return els.filter((el) => {
      try {
        if (el.disabled) return false;
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      } catch {
        return false;
      }
    });
  }

  function focusFirstRightField() {
    setTimeout(() => {
      try {
        scrollRightTop();
        orgRef.current?.focus?.();
      } catch { }
    }, 0);
  }

  function focusNextField(dir = +1) {
    const els = getFocusableElements();
    if (!els.length) return;

    const active = document.activeElement;
    let idx = els.findIndex((el) => el === active);
    if (idx === -1) idx = dir > 0 ? -1 : els.length;

    let next = idx + dir;
    if (next < 0) next = 0;
    if (next > els.length - 1) next = els.length - 1;

    const el = els[next];
    if (!el) return;

    try {
      el.focus?.();
      el.scrollIntoView?.({ block: "nearest" });
    } catch { }
  }
  useEffect(() => {
    if (!pendingSpeechSearch) return;
    if (loading) return;

    if (sortedItems.length > 0) {
      setListIndex(0);
      setTimeout(() => {
        scrollMidTop();
        scrollRowIntoView(0);
      }, 0);
    } else {
      setListIndex(-1);
      setTimeout(() => scrollMidTop(), 0);
    }

    setPendingSpeechSearch(false);
  }, [pendingSpeechSearch, loading, sortedItems.length]);

  function applyDictationToFocusedField(raw0) {
    const el = document.activeElement;
    if (!el) return false;

    const containerHas = rightScrollRef.current?.contains?.(el);
    if (!containerHas) return false;

    const tag = (el.tagName || "").toLowerCase();
    const type = (el.getAttribute?.("type") || "").toLowerCase();

    if (el === orgRef.current) {
      const spoken = norm(raw0);
      const found = orgs.find((o) => norm(pickOrgName(o)) === spoken || norm(pickOrgName(o)).includes(spoken));
      if (!found) return false;

      setForm((prev) => ({
        ...prev,
        organisation_id: String(found.id || ""),
        contact_id: "",
      }));
      return true;
    }

    if (el === contactRef.current) {
      const spoken = norm(raw0);
      const found = contacts.find((c) => {
        const nm = pickContactFullName(c);
        return norm(nm) === spoken || norm(nm).includes(spoken);
      });
      if (!found) return false;

      setForm((prev) => ({
        ...prev,
        contact_id: String(found.id || ""),
      }));
      return true;
    }

    if (el === invNumRef.current) {
      const v = String(raw0 || "").trim();
      setForm((prev) => ({ ...prev, invoice_number: v }));
      return true;
    }

    if (el === invDateRef.current || (tag === "input" && type === "date" && el === invDateRef.current)) {
      const iso = parseDutchDateToISO(raw0);
      if (!iso) return false;
      setForm((prev) => ({ ...prev, invoice_date: iso }));
      return true;
    }

    if (el === payDateRef.current) {
      const iso = parseDutchDateToISO(raw0);
      if (!iso) return false;
      setPayDraft((prev) => ({ ...prev, paid_on: iso }));
      return true;
    }

    if (el === payAmtRef.current) {
      const amt = decimal2OrEmptyString(raw0);
      if (!amt) return false;
      setPayDraft((prev) => ({ ...prev, amount: amt }));
      return true;
    }

    if (el === payNotesRef.current) {
      const punct = punctToChar(raw0);

      if (punct) {
        setForm((prev) => ({
          ...prev,
          notes: `${String(prev.notes || "")}${punct}`,
        }));
        return true;
      }

      let txt = String(raw0 || "").trim()
        .replace(/\bnieuwe regel\b/gi, "\n")
        .replace(/\bpuntkomma\b/gi, ";")
        .replace(/\bdubbele punt\b/gi, ":")
        .replace(/\buitroepteken\b/gi, "!")
        .replace(/\bvraagteken\b/gi, "?")
        .replace(/\bkomma\b/gi, ",")
        .replace(/\bpunt\b/gi, ".")
        .replace(/\s+([.,!?;:])/g, "$1");

      txt = txt.replace(/(^|[\n.!?]\s*)([a-zà-ÿ])/g, (_, p1, p2) => p1 + p2.toUpperCase());

      setForm((prev) => {
        const cur = String(prev.notes || "");
        const sep = cur && !cur.endsWith(" ") && !cur.endsWith("\n") ? " " : "";
        return { ...prev, notes: cur + sep + txt };
      });
      return true;
    }

if (el === statusRef.current && tag === "select") {
      const spoken = norm(raw0 || "");
      let next = "";
      if (spoken.includes("concept")) next = "DRAFT";
      else if (spoken.includes("verzonden")) next = "SENT";
      if (!next) return false;
      setForm((prev) => ({ ...prev, status: next }));
      return true;
    }

    return false;
  }

  const voice = useVoiceCommands({
    lang: "nl-NL",
    timeoutMs: 8500,
    onCommand: (cmd, rawHeard) => {
      const raw = String(rawHeard || "").trim();
      if (raw) {
        setSpeechInfo(`🎙 ${raw}`);
        setTimeout(() => setSpeechInfo(""), 2000);
      }
      if (raw && handleVoiceCommandLike(raw)) return true;
      if (!cmd || !cmd.type) return false;
      if (cmd.type === "new" || cmd.type === "nieuw") { newItem(); return true; }
      if (cmd.type === "refresh") { vernieuwList(); return true; }
      if (cmd.type === "save") { saveItem(); return true; }
      if (cmd.type === "delete") { deleteItem(); return true; }
      return false;
    },
    onDictation: (text) => {
      const raw0 = String(text || "").trim();
      if (!raw0) return;
      setSpeechInfo(`🎙 ${raw0}`);
      setTimeout(() => setSpeechInfo(""), 2000);
      if (handleVoiceCommandLike(raw0)) return;
      if (applyDictationToPopupField(raw0)) return;
      if (applyDictationToFocusedField(raw0)) return;
      if (mode === "search") {
        if (isEmptyWord(raw0)) {
          setQ("");
        } else {
          setQ(raw0);
        }
      }
    },
  });

    useEffect(() => {
      const cur = String(voice.error || "");
      const prev = String(prevVoiceErrorRef.current || "");
      if (cur && cur !== prev) {
        setHideVoiceError(false);
        prevVoiceErrorRef.current = cur;
      }
      if (!cur) prevVoiceErrorRef.current = "";
    }, [voice.error]);

    function startSpeakSmart() {
      if (!voice.supported || voice.listening) return;
      setHideVoiceError(true);
      voice.startOnce();
    }


// ---------- AI OFFERTES ----------
function openAiPopup(source = "page") {
  setAiSource(source);
  setAiOpen(true);
  setAiText("");
  setFlash("");
  setTimeout(() => {
    try {
      aiTextRef.current?.focus?.({ preventScroll: true });
    } catch {
      try { aiTextRef.current?.focus?.(); } catch {}
    }
  }, 0);
}

function closeAiPopup() {
  if (aiBusy) return;
  setAiOpen(false);
  setAiText("");
  setAiSource("page");
}

function firstFilled(...vals) {
  for (const v of vals) {
    const t = safeTrim(v);
    if (t) return t;
  }
  return "";
}

function formatAnyAmount(v) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "-";
  return euro(n);
}

function getOrganisationIdForAi() {
  return form?.organisation_id || activeOrg?.id || selectedInvoiceRow?.organisation_id || "";
}

function getOrganisationNameForAi() {
  return firstFilled(
    activeOrg?.name,
    activeOrg?.organisation_name,
    selectedInvoiceRow?.organisation_name,
    recipientName
  );
}

function getContactForAi() {
  return selectedContact || null;
}

function buildCurrentOfferContext() {
  const orgName = getOrganisationNameForAi();
  const contact = getContactForAi();
  const contactName = firstFilled(
    contact?.search_name,
    contact?.full_name,
    contact?.naam,
    [contact?.first_name, contact?.name_prefix, contact?.last_name].filter(Boolean).join(" "),
    pickContactNameFromRow(selectedInvoiceRow || {})
  );

  const offerNumber = firstFilled(form.invoice_number, selectedInvoiceRow?.invoice_number, selectedInvoiceRow?.offer_number);
  const offerDate = firstFilled(formatNlDate(form.invoice_date), formatNlDate(selectedInvoiceRow?.invoice_date), formatNlDate(selectedInvoiceRow?.offer_date));
  const status = statusLabel(form.status || selectedInvoiceRow?.status);

  const out = [];
  out.push("HUIDIGE OFFERTE");
  out.push(`Relatie: ${orgName || "-"}`);
  out.push(`Contactpersoon: ${contactName || "-"}`);
  out.push(`Offertenummer/referentie: ${offerNumber || "-"}`);
  out.push(`Offertedatum: ${offerDate || "-"}`);
  out.push(`Status: ${status || "-"}`);
  out.push(`Totaal: ${euro(invoiceTotals.total || form.total || selectedInvoiceRow?.total || 0)}`);
  out.push("");
  out.push("REGELS");

  const useLines = Array.isArray(linesForTotals) ? linesForTotals : [];
  if (!useLines.length) {
    out.push("Geen offerteregels gevonden.");
  } else {
    useLines.forEach((ln, idx) => {
      out.push(`${idx + 1}. ${lineQty(ln)} x ${lineDescription(ln) || "-"} à ${euro(lineUnitPrice(ln))} (${vatLabelForRate(lineVatRate(ln))})`);
    });
  }

  if (safeTrim(form.notes)) {
    out.push("");
    out.push("NOTITIES");
    out.push(form.notes);
  }

  return out.join("\n");
}

function runAiOrganisationData() {
  const org = activeOrg || selectedInvoiceRow || {};
  const lines = [];
  lines.push("RELATIE GEGEVENS");
  lines.push(`Naam: ${getOrganisationNameForAi() || "-"}`);
  lines.push(`Telefoon: ${firstFilled(org.phone, org.org_phone) || "-"}`);
  lines.push(`Mobiel: ${firstFilled(org.mobile, org.org_mobile) || "-"}`);
  lines.push(`E-mail: ${firstFilled(org.email, org.org_email) || "-"}`);
  lines.push(`Website: ${firstFilled(org.website) || "-"}`);
  lines.push(`KvK: ${firstFilled(org.kvk, org.kvk_number) || "-"}`);
  lines.push(`BTW nummer: ${pickOrgVatNumber(org) || "-"}`);
  lines.push("");
  lines.push("Correspondentieadres:");
  lines.push(firstFilled([
    org.corr_street,
    org.corr_house_number,
    org.corr_addition,
  ].filter(Boolean).join(" ")) || "-");
  lines.push(firstFilled([org.corr_postcode, org.corr_city].filter(Boolean).join(" ")) || "-");
  lines.push(firstFilled(org.corr_country) || "-");

  setAiText(lines.join("\n"));
  setFlash("Relatiegegevens getoond ✓");
}

function runAiContactData() {
  const c = getContactForAi();
  const lines = [];
  lines.push("CONTACTPERSOON GEGEVENS");

  if (!c) {
    lines.push("Geen contactpersoon gekozen of geladen.");
    setAiText(lines.join("\n"));
    setFlash("");
    return;
  }

  const contactName = firstFilled(
    c.search_name,
    c.full_name,
    c.naam,
    [c.first_name, c.name_prefix, c.last_name].filter(Boolean).join(" ")
  );

  lines.push(`Naam: ${contactName || "-"}`);
  lines.push(`Functie: ${firstFilled(c.job_title, c.function, c.functie) || "-"}`);
  lines.push(`Telefoon: ${firstFilled(c.phone, c.telephone) || "-"}`);
  lines.push(`Mobiel: ${firstFilled(c.mobile) || "-"}`);
  lines.push(`E-mail: ${firstFilled(c.email) || "-"}`);
  lines.push(`Aanschrijving: ${firstFilled(c.aanschrijving, c.aanhef, c.salutation) || "-"}`);

  setAiText(lines.join("\n"));
  setFlash("Contactpersoongegevens getoond ✓");
}

async function runAiDossierOverview() {
  const orgId = getOrganisationIdForAi();
  if (!orgId) {
    setAiText("Kies eerst een relatie.");
    return;
  }

  try {
    setAiBusy(true);
    setFlash("Dossieroverzicht wordt gemaakt...");

    const dossier = await tryFetchJson([`/api/organisations/${encodeURIComponent(orgId)}/dossier`]);
    const org = dossier?.organisation || {};
    const contactsList = Array.isArray(dossier?.contacts) ? dossier.contacts : [];
    const notesTasks = Array.isArray(dossier?.notes_tasks) ? dossier.notes_tasks : [];
    const offers = Array.isArray(dossier?.offers) ? dossier.offers : [];
    const invoices = Array.isArray(dossier?.invoices) ? dossier.invoices : [];

    const lines = [];
    lines.push("DOSSIER OVERZICHT");
    lines.push(`Relatie: ${firstFilled(org.name, getOrganisationNameForAi()) || "-"}`);
    lines.push(`Telefoon: ${firstFilled(org.phone, org.mobile) || "-"}`);
    lines.push(`E-mail: ${firstFilled(org.email) || "-"}`);
    lines.push(`KvK: ${firstFilled(org.kvk, org.kvk_number) || "-"}`);
    lines.push("");

    lines.push(`CONTACTPERSONEN (${contactsList.length})`);
    if (!contactsList.length) lines.push("Geen contactpersonen gevonden.");
    contactsList.forEach((c) => {
      const nm = firstFilled(c.search_name, c.full_name, c.naam, [c.last_name, c.first_name].filter(Boolean).join(", ")) || "-";
      lines.push(`- ${nm} | ${firstFilled(c.phone, c.mobile) || "-"} | ${firstFilled(c.email) || "-"}`);
    });
    lines.push("");

    lines.push(`NOTITIES/TAKEN (${notesTasks.length})`);
    if (!notesTasks.length) lines.push("Geen notities/taken gevonden.");
    notesTasks.slice(0, 20).forEach((n) => {
      lines.push(`- ${firstFilled(n.kind, n.type) || "-"} | ${firstFilled(n.subject, n.title) || "-"} | ${firstFilled(n.status) || "-"}`);
    });
    lines.push("");

    lines.push(`OFFERTES (${offers.length}) - nieuw naar oud`);
    if (!offers.length) lines.push("Geen offertes gevonden.");
    offers.forEach((o) => {
      lines.push(`- ${firstFilled(o.dossier_date, formatNlDate(o.offer_date), formatNlDate(o.invoice_date)) || "-"} | ${firstFilled(o.dossier_number, o.offer_number, o.invoice_number) || "-"} | ${firstFilled(o.status) || "-"} | ${formatAnyAmount(firstFilled(o.dossier_total, o.total))}`);
    });
    lines.push("");

    lines.push(`FACTUREN (${invoices.length}) - nieuw naar oud`);
    if (!invoices.length) lines.push("Geen facturen gevonden.");
    invoices.forEach((i) => {
      lines.push(`- ${firstFilled(i.dossier_date, formatNlDate(i.invoice_date)) || "-"} | ${firstFilled(i.dossier_number, i.invoice_number) || "-"} | ${firstFilled(i.status) || "-"} | ${formatAnyAmount(firstFilled(i.dossier_total, i.total))}`);
    });

    setAiText(lines.join("\n"));
    setFlash("Dossieroverzicht gemaakt ✓");
  } catch (err) {
    setAiText("FOUT:\n" + (err?.message || "Onbekende fout"));
    setFlash("");
  } finally {
    setAiBusy(false);
  }
}

async function runAiOfferCheck() {
  try {
    setAiBusy(true);
    setFlash("AI controleert offerte...");

    const prompt = `
Je bent een ervaren Nederlandse sales- en administratie-assistent in StaffIQ.
Controleer deze offerte inhoudelijk en praktisch.

${buildCurrentOfferContext()}

Geef antwoord in duidelijk Nederlands met deze kopjes:
1. Korte conclusie
2. Mogelijke fouten of ontbrekende gegevens
3. Financiële controle
4. Advies om de offerte sterker te maken
5. Actiepunten
    `.trim();

    const res = await tryFetchJson(["/api/ai/organisations/check"], {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        organisation: activeOrg || {},
        mode: "chat",
        question: "Controleer deze offerte.",
        prompt,
      }),
    });

    if (!res?.ok) throw new Error(res?.error || "AI controle mislukt");

    setAiText(res.result || "Geen resultaat");
    setFlash("Offerte gecontroleerd ✓");
  } catch (err) {
    setAiText("FOUT:\n" + (err?.message || "Onbekende fout"));
    setFlash("");
  } finally {
    setAiBusy(false);
  }
}

function appendAiConversation(qText, aText) {
  const block =
    `Vraag:\n${qText || "-"}\n\n` +
    `----------------------------------------\n\n` +
    `Antwoord:\n${aText || "-"}\n\n` +
    `========================================\n\n`;

  setAiText((prev) => {
    const cur = String(prev || "").trim();
    if (!cur) return block;
    return `${cur}\n\n${block}`;
  });
}

async function runAiChat() {
  const question = String(aiText || "").trim();

  if (!question) {
    setAiText("Typ eerst een vraag voor Chat met AI.\n\nBijvoorbeeld:\nWat ontbreekt er nog in deze offerte?");
    setTimeout(() => {
      try { aiTextRef.current?.focus?.({ preventScroll: true }); } catch { try { aiTextRef.current?.focus?.(); } catch {} }
    }, 0);
    return;
  }

  try {
    setAiBusy(true);
    setFlash("AI beantwoordt vraag...");

    const prompt = `
Je bent een zakelijke assistent voor StaffIQ.
Beantwoord de vraag op basis van deze offerte, relatie en contactpersoon.

${buildCurrentOfferContext()}

VRAAG:
${question}

Antwoord in helder Nederlands. Zeg eerlijk als iets niet uit de gegevens blijkt.
    `.trim();

    const res = await tryFetchJson(["/api/ai/organisations/check"], {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        organisation: activeOrg || {},
        mode: "chat",
        question,
        prompt,
      }),
    });

    if (!res?.ok) throw new Error(res?.error || "AI chat mislukt");

    appendAiConversation(question, res.result || "Geen antwoord");
    setFlash("AI chat gereed ✓");
  } catch (err) {
    appendAiConversation(question, "FOUT: " + (err?.message || "Onbekende fout"));
    setFlash("");
  } finally {
    setAiBusy(false);
  }
}

function printAiDocument() {
  const title = aiSource === "popup" ? "StaffIQ AI Offerte" : "StaffIQ AI Offertes";
  const content = String(aiText || "").trim() || "Geen inhoud om te printen.";

  const html = `
    <html>
      <head>
        <title>${title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 24px; color: #111; line-height: 1.45; white-space: pre-wrap; }
          h1 { font-size: 20px; margin: 0 0 18px 0; }
        </style>
      </head>
      <body>
        <h1>${title}</h1>
        <div>${content.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</div>
      </body>
    </html>
  `;

  const win = window.open("", "_blank", "width=900,height=700");
  if (!win) return;
  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { try { win.print(); } catch {} }, 300);
}

function startAiSpeak() {
  if (!voice.supported || voice.listening) return;
  try { aiTextRef.current?.focus?.({ preventScroll: true }); } catch { try { aiTextRef.current?.focus?.(); } catch {} }
  voice.startOnce();
}

// ---------- LAYOUT (zelfde stijl als Contactpersonen) ----------
    const isMobile = window.innerWidth <= 768;
    const RIGHT_WIDTH = isMobile ? "100%" : 320;
    const GAP = isMobile ? 18 : 14;
    const TABLE_MIN_WIDTH = 1410;
    const MID_BOTTOM_OFFSET = isMobile ? 90 : 135;

    // Kolombreedtes
    const colWidths = {
      zoeknaam: 68,
      contact: 62,
      invnr: 44,
      invdt: 44,
      totaal: 48,
      tel: 62,
      mob: 62,
      email: 78,
      city: 44,
      country: 40,
      status: 38,
      companyCode: 90,
    };

    const searchInputStyle = {
      padding: "7px 10px",
      width: 180,
      border: "1px solid #d9d9d9",
      borderRadius: 4,
      background: "white",
      outline: "none",
      boxSizing: "border-box",
    };

    const inputStyle = {
      padding: "7px 10px",
      width: "100%",
      minWidth: 0,
      border: "1px solid #d9d9d9",
      borderRadius: 4,
      background: "white",
      boxSizing: "border-box",
    };

    const dateInputStyle = {
      ...inputStyle,
      paddingRight: 30,
    };

const buttonStyle = {
  padding: "6px 6px",
  border: "1px solid #d9d9d9",
  borderRadius: 4,
  background: "white",
  cursor: "pointer",
  whiteSpace: "nowrap",
  fontSize: 12,
  minWidth: 0,
};

    const buttonPrimary = (bg) => ({
      ...buttonStyle,
      background: bg,
      color: "white",
      border: "1px solid rgba(0,0,0,0.05)",
    });

    const buttonDisabledStyle = { ...buttonStyle, opacity: 0.55, cursor: "not-allowed" };

    const selectStyle = {
      height: 30,
      padding: "0 8px",
      border: "1px solid #d9d9d9",
      borderRadius: 4,
      background: "white",
      width: "100%",
      minWidth: 0,
      boxSizing: "border-box",
    };

    const tableStyle = { width: "100%", borderCollapse: "separate", borderSpacing: 0, tableLayout: "fixed" };

    const thBase = {
      textAlign: "left",
      fontSize: 12,
      color: "#444",
      fontWeight: 700,
      padding: "8px 6px",
      borderBottom: "1px solid #e6e6e6",
      whiteSpace: "nowrap",
      position: "sticky",
      top: 0,
      zIndex: 5,
      background: "#fff",
      userSelect: "none",
    };

    const thClickable = { ...thBase, cursor: "pointer" };

    const filterThStyle = {
      ...thBase,
      fontWeight: 400,
      padding: "6px 6px 8px 6px",
      borderBottom: "1px solid #ededed",
      top: 32,
      zIndex: 4,
      cursor: "default",
    };

    const tdStyle = {
      padding: "7px 6px",
      borderBottom: "1px solid #efefef",
      fontSize: 13,
      color: "#222",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    };

    const rowBgCursor = "#f3f7ff";
    const rowBgSelected = "#e6f0ff";

    function sortToggle(key) {
      setSort((s) => {
        if (s.key !== key) return { key, dir: key === "invoice_date" ? "desc" : "asc" };
        return { key, dir: s.dir === "asc" ? "desc" : "asc" };
      });
    }

    function sortArrow(key) {
      if (sort.key !== key) return "";
      return sort.dir === "asc" ? " ▲" : " ▼";
    }

    const deleteEnabled = formHasId() && !isLocked;

    const companyInvoiceProfile = {
      name: "Tech Company BV",
      addressLines: ["Europaplein 1", "Gebouw 21 - 2.05", "5684 ZC BEST"],
      email: "peter@techcompany.nl",
      phone: "+31 76 2031933",
      kvk: "67282466",
      vatNumber: "NL856911252B01",
      iban: "NL59 INGB 0007 6330 52",
      gIban: "G-REKENING NOG IN TE STELLEN",
      footerText:
        "Wij verzoeken u vriendelijk bij uw reactie de referentie te vermelden.",
    };

    const selectedInvoiceRow = useMemo(() => {
      const rid = String(form.id || selectedId || "");
      if (!rid) return null;
      return items.find((x) => String(x.id || "") === rid) || null;
    }, [items, form.id, selectedId]);

    const activeOrg = orgDetail || selectedOrgFromList || null;
    const approvalPreference = getRelationApprovalPreference();
    const canSendForApproval = approvalPreference.whatsappAllowed || approvalPreference.emailAllowed;
    const approvalButtonDisabled = sendingApproval || !formHasId() || !canSendForApproval;
    const approvalButtonTitle = !formHasId()
      ? "Sla de offerte/opdracht eerst op."
      : !canSendForApproval
        ? "Bij deze relatie staat akkoord via WhatsApp of E-mail niet aan, of mobiel/e-mail ontbreekt."
        : "Maak akkoordlink en verstuur volgens relatievoorkeur WhatsApp/E-mail";

    function templateTypeLabel(value) {
      const t = String(value || "offer").toLowerCase();

      if (t === "order") return "Opdracht";
      if (t === "both") return "Beide";

      return "Offerte";
    }

    const selectableOfferTemplates = useMemo(() => {
      return (offerTemplates || [])
        .filter((tpl) => tpl.active !== false)
        .slice()
        .sort((a, b) => {
          const ta = templateTypeLabel(a.template_type);
          const tb = templateTypeLabel(b.template_type);

          if (ta !== tb) return ta.localeCompare(tb, "nl");

          return String(a.name || "").localeCompare(String(b.name || ""), "nl");
        });
    }, [offerTemplates]);

    const selectedOfferTemplate = useMemo(() => {
      const wanted = String(form.template_id || "");
      if (!wanted) return null;
      return (offerTemplates || []).find((tpl) => String(tpl.id) === wanted) || null;
    }, [offerTemplates, form.template_id]);

    function renderTemplateBlock(text, extraStyle = {}) {
      const value = String(text || "");
      if (!value.trim()) return null;

      return (
        <div
          style={{
            marginTop: 8,
            marginBottom: 12,
            fontSize: 14,
            lineHeight: 1.55,
            whiteSpace: "pre-wrap",
            width: "100%",
            maxWidth: "100%",
            ...fixedOfferTextStyle(value),
            ...extraStyle,
          }}
        >
          {value}
        </div>
      );
    }

    const orgVatNumber = pickOrgVatNumber(activeOrg);
    const gPercentage = pickOrgGrekeningPercent(activeOrg);

const linesForTotals = useMemo(() => {
  const arr = Array.isArray(lines) ? [...lines] : [];

  const draftLineType = normalizeOfferLineType(lineDraft.line_type || "Artikel");
  const draftQty = numberFromAny(lineDraft.quantity, 0);
  const draftDesc = String(lineDraft.description || "").trim();
  const draftUnit = numberFromAny(lineDraft.unit_price, 0);

  if (!draftDesc) {
    return arr;
  }

  if (draftLineType === "Artikel" && !(draftQty > 0 && draftUnit > 0)) {
    return arr;
  }

  const draftLine = {
    line_type: draftLineType,
    quantity: draftLineType === "Artikel" ? draftQty : 0,
    qty: draftLineType === "Artikel" ? draftQty : 0,
    description: draftDesc,
    unit_price: draftLineType === "Artikel" ? draftUnit : 0,
    price: draftLineType === "Artikel" ? draftUnit : 0,
    vat_rate: lineDraft.vat_rate || "hoog",
    vat: lineDraft.vat_rate || "hoog",
  };

  if (editingLineId) {
    return arr.map((ln) =>
      String(ln.id) === String(editingLineId) ? { ...ln, ...draftLine } : ln
    );
  }

  return [...arr, draftLine];
}, [lines, lineDraft, editingLineId]);

    const invoiceTotals = useMemo(
      () => calcOfferTotals(linesForTotals),
      [linesForTotals]
    );

    const offerteTotaalBedrag = Number(invoiceTotals.total || 0);

    const gAmount = useMemo(() => {
      return invoiceTotals.total * (gPercentage / 100);
    }, [invoiceTotals.total, gPercentage]);

    const ibanAmount = useMemo(() => {
      return invoiceTotals.total - gAmount;
    }, [invoiceTotals.total, gAmount]);

    const isCreditInvoice = invoiceTotals.total < 0;
    const effectiveGPercentage = isCreditInvoice ? 0 : gPercentage;

    const recipientName =
      activeOrg?.name ||
      activeOrg?.organisation_name ||
      activeOrg?.bedrijfsnaam ||
      "";

    const selectedContact = useMemo(() => {
      const cid = String(form.contact_id || "");
      if (!cid) return null;
      return contacts.find((c) => String(c.id || "") === cid) || null;
    }, [contacts, form.contact_id]);

    const contactAanschrijving =
      safeTrim(
        selectedContact?.aanschrijving ||
        selectedContact?.aanhef ||
        selectedContact?.salutation ||
        ""
      );

    const recipientAddressLines = useMemo(() => {
      if (!activeOrg) return [];

      const line1 = safeTrim(
        [
          activeOrg.corr_street,
          activeOrg.corr_house_number,
          activeOrg.corr_addition,
        ]
          .filter(Boolean)
          .join(" ")
      );

      const pcCity = [
        safeTrim(activeOrg.corr_postcode),
        safeTrim(activeOrg.corr_city),
      ]
        .filter(Boolean)
        .join(" ");

      const country = safeTrim(activeOrg.corr_country);

      return [line1, pcCity, country].filter(Boolean);
    }, [activeOrg]);

    useEffect(() => {
      const defaultVat = pickOrgDefaultVatRate(activeOrg);
      setLineDraft((prev) => ({ ...prev, vat_rate: defaultVat }));
    }, [form.organisation_id, activeOrg?.id]);


    function offerLineGridTemplate() {
      if (popupMode === "preview") {
        return invoiceTotals.hideVatColumnOnInvoice
          ? "70px 1fr 110px 110px"
          : "70px 1fr 110px 110px 70px";
      }

      return invoiceTotals.hideVatColumnOnInvoice
        ? "70px 1fr 110px 110px 56px"
        : "70px 1fr 110px 110px 70px 56px";
    }

    function offerLineDraftIsAmount() {
      return normalizeOfferLineType(lineDraft.line_type || "Artikel") === "Artikel";
    }

    function renderLineTypeSelect() {
      return (
        <select
          value={normalizeOfferLineType(lineDraft.line_type || "Artikel")}
          onChange={(e) => {
            const nextType = normalizeOfferLineType(e.target.value);

            setLineDraft((prev) => ({
              ...prev,
              line_type: nextType,
              quantity: nextType === "Artikel" ? (prev.quantity || 1) : 0,
              unit_price: nextType === "Artikel" ? prev.unit_price : "",
            }));
          }}
          disabled={!form.id || isLocked}
          style={{
            ...selectStyle,
            width: 130,
            marginBottom: 6,
            padding: "6px 6px",
            fontWeight: 700,
          }}
        >
          {OFFER_LINE_TYPES.map((type) => (
            <option key={type} value={type}>
              {type}
            </option>
          ))}
        </select>
      );
    }

    function offerLineDragProps(ln, isEditingLine = false) {
      const lineId = ln?.id ? String(ln.id) : "";
      const canDragLine = !!lineId && popupMode !== "preview" && !isLocked && !isEditingLine;

      if (!canDragLine) {
        return {};
      }

      return {
        draggable: true,
        onDragStart: (e) => {
          setDraggingOfferLineId(lineId);
          setDragOverOfferLineId(null);
          try {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("text/plain", lineId);
          } catch {}
        },
        onDragOver: (e) => {
          e.preventDefault();
          setDragOverOfferLineId(lineId);
          try {
            e.dataTransfer.dropEffect = "move";
          } catch {}
        },
        onDragLeave: () => {
          setDragOverOfferLineId((current) =>
            String(current || "") === lineId ? null : current
          );
        },
        onDrop: async (e) => {
          e.preventDefault();
          const draggedId =
            (() => {
              try {
                return e.dataTransfer.getData("text/plain");
              } catch {
                return "";
              }
            })() || draggingOfferLineId;

          setDraggingOfferLineId(null);
          setDragOverOfferLineId(null);
          await reorderOfferLine(draggedId, lineId);
        },
        onDragEnd: () => {
          setDraggingOfferLineId(null);
          setDragOverOfferLineId(null);
        },
      };
    }

    function renderOfferHeader(key = "offer-line-header") {
      return (
        <div
          key={key}
          style={{
            display: "grid",
            gridTemplateColumns: offerLineGridTemplate(),
            gap: 12,
            borderBottom: "2px solid #444",
            paddingBottom: 8,
            fontWeight: 700,
            fontSize: 14,
            marginTop: 8,
          }}
        >
          <div style={{ textAlign: "right", paddingRight: 0 }}>Aantal</div>
          <div>Omschrijving</div>
          <div style={{ textAlign: "right" }}>Bedrag</div>
          <div style={{ textAlign: "right" }}>Totaal</div>
          {!invoiceTotals.hideVatColumnOnInvoice && (
            <div style={{ textAlign: "right" }}>Btw</div>
          )}
          {popupMode !== "preview" && <div></div>}
        </div>
      );
    }

    function renderOfferActions(ln) {
      if (popupMode === "preview") return null;

      const lineId = String(ln?.id || "");
      const idx = lines.findIndex((x) => String(x.id || "") === lineId);
      const canMove = !!lineId && !isLocked && idx >= 0;

      const iconButtonStyle = {
        border: "none",
        background: "transparent",
        cursor: "pointer",
        fontSize: 14,
        opacity: 0.75,
        padding: 0,
        margin: 0,
        lineHeight: 1,
      };

      return (
        <div
          data-offer-actions="1"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 5,
            flexWrap: "nowrap",
            minHeight: 24,
          }}
        >
          <span
            title="Sleep deze regel naar boven of beneden"
            style={{
              ...iconButtonStyle,
              cursor: canMove ? "grab" : "default",
              opacity: canMove ? 0.75 : 0.25,
              userSelect: "none",
            }}
          >
            ↕
          </span>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              moveOfferLineStep(ln.id, -1);
            }}
            disabled={!canMove || idx <= 0}
            title="Regel omhoog"
            style={{
              ...iconButtonStyle,
              opacity: canMove && idx > 0 ? 0.75 : 0.25,
              cursor: canMove && idx > 0 ? "pointer" : "not-allowed",
            }}
          >
            ↑
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              moveOfferLineStep(ln.id, 1);
            }}
            disabled={!canMove || idx >= lines.length - 1}
            title="Regel omlaag"
            style={{
              ...iconButtonStyle,
              opacity: canMove && idx < lines.length - 1 ? 0.75 : 0.25,
              cursor: canMove && idx < lines.length - 1 ? "pointer" : "not-allowed",
            }}
          >
            ↓
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              startEditInvoiceLine(ln);
            }}
            title="Bewerken"
            style={iconButtonStyle}
          >
            ✏️
          </button>

          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              deleteInvoiceLine(ln.id);
            }}
            title="Verwijderen"
            style={iconButtonStyle}
          >
            🗑️
          </button>
        </div>
      );
    }

    function renderOfferLine(ln, idx) {
      const type = lineTypeOf(ln);
      const isArticle = type === "Artikel";
      const isSubtotalLike = type === "Subtotaal" || type === "Totaal";
      const isHeader = type === "Kop";
      const qty = lineQty(ln);
      const unit = lineUnitPrice(ln);
      const rate = lineVatRate(ln);
      const lineTotal = lineTotalExVat(ln);
      const isEditingLine = String(editingLineId || "") === String(ln.id || "");
      const canEditLine = popupMode !== "preview" && !isLocked;

      if (isEditingLine) {
        const draftIsArticle = offerLineDraftIsAmount();

        return (
          <div
            key={ln.id}
            style={{
              display: "grid",
              gridTemplateColumns: offerLineGridTemplate(),
              gap: 12,
              padding: "8px 0",
              borderBottom: "1px solid #ddd",
              fontSize: 14,
              lineHeight: 1.45,
              alignItems: "start",
              background: "#f5f9ff",
            }}
          >
            {draftIsArticle ? (
              <input
                ref={qtyInputRef}
                value={lineDraft.quantity}
                onChange={(e) =>
                  setLineDraft({ ...lineDraft, quantity: e.target.value })
                }
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                placeholder="1"
                style={{
                  ...inputStyle,
                  textAlign: "right",
                  padding: "8px 8px 8px 12px",
                }}
              />
            ) : (
              <div></div>
            )}

            <div>
              {renderLineTypeSelect()}

              <textarea
                ref={lineDescRef}
                value={lineDraft.description}
                onChange={(e) => {
                  setLineDraft({ ...lineDraft, description: e.target.value });
                  e.target.style.height = "auto";
                  e.target.style.height = e.target.scrollHeight + "px";
                }}
                placeholder={"Omschrijving\nBijv. Artikel / Tekst / Kop / Subtotaal / Totaal"}
                style={{
                  ...inputStyle,
                  minHeight: 64,
                  resize: "none",
                  overflow: "hidden",
                  overflowWrap: "anywhere",
                  wordBreak: "break-word",
                  width: "100%",
                  boxSizing: "border-box",
                  fontFamily: fixedOfferTextStyle(lineDraft.description).fontFamily || "inherit",
                  tabSize: fixedOfferTextStyle(lineDraft.description).tabSize || 4,
                  whiteSpace: "pre-wrap",
                  padding: "8px 8px",
                  lineHeight: "1.4",
                  fontWeight: normalizeOfferLineType(lineDraft.line_type || "Artikel") === "Kop" ? 700 : 400,
                }}
              />
            </div>

            {draftIsArticle ? (
              <input
                ref={linePriceRef}
                value={lineDraft.unit_price}
                onChange={(e) =>
                  setLineDraft({ ...lineDraft, unit_price: e.target.value })
                }
                onBlur={handleUnitPriceCommit}
                onFocus={(e) => e.target.select()}
                onClick={(e) => e.target.select()}
                placeholder="excl. btw"
                style={{
                  ...inputStyle,
                  textAlign: "right",
                  padding: "8px 8px",
                }}
              />
            ) : (
              <div></div>
            )}

            <div style={{ textAlign: "right", fontWeight: draftIsArticle ? 400 : 700 }}>
              {draftIsArticle
                ? euro(numberFromAny(lineDraft.quantity, 0) * numberFromAny(lineDraft.unit_price, 0))
                : ""}
            </div>

            {!invoiceTotals.hideVatColumnOnInvoice && (
              draftIsArticle ? (
                <div style={{ textAlign: "right" }}>
                  <select
                    ref={lineVatRef}
                    value={lineDraft.vat_rate}
                    onChange={(e) =>
                      setLineDraft({ ...lineDraft, vat_rate: e.target.value })
                    }
                    disabled={!form.id || isLocked}
                    style={{
                      ...selectStyle,
                      padding: "6px 6px",
                      width: 70,
                      display: "inline-block",
                    }}
                  >
                    <option value="hoog">21%</option>
                    <option value="laag">9%</option>
                    <option value="0">0%</option>
                    <option value="verlegd">verlegd</option>
                  </select>
                </div>
              ) : (
                <div></div>
              )
            )}

            <div
              data-offer-actions="1"
              style={{
                display: popupMode === "preview" ? "none" : "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={addInvoiceLine}
                title="Opslaan"
                style={{
                  border: "1px solid #cfd8e3",
                  background: "#2f7dd1",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: 12,
                  borderRadius: 6,
                  padding: "4px 8px",
                }}
              >
                Opslaan
              </button>

              <button
                type="button"
                onClick={cancelEditInvoiceLine}
                title="Annuleren"
                style={{
                  border: "1px solid #cfd8e3",
                  background: "#f4f4f4",
                  color: "#333",
                  cursor: "pointer",
                  fontSize: 12,
                  borderRadius: 6,
                  padding: "4px 8px",
                }}
              >
                Annuleren
              </button>
            </div>
          </div>
        );
      }

      if (!isArticle) {
        const descriptionColumn = popupMode === "preview" ? "1 / -1" : "1 / -2";
        const subtotalDescriptionColumn = invoiceTotals.hideVatColumnOnInvoice ? "1 / 3" : "1 / 3";
        const subtotalTotalsColumn = invoiceTotals.hideVatColumnOnInvoice ? "3 / 5" : "3 / 6";
        const runningTotals = isSubtotalLike ? calcOfferTotalsUntilLine(lines, idx) : null;
        const description = lineDescription(ln);
        const fixedTextStyle = fixedOfferTextStyle(description);

        return (
          <div
            key={ln.id}
            {...offerLineDragProps(ln, isEditingLine)}
            onClick={() => {
              if (canEditLine) startEditInvoiceLine(ln);
            }}
            title={canEditLine ? "Klik om deze regel te bewerken of sleep om de volgorde te wijzigen" : ""}
            style={{
              display: "grid",
              gridTemplateColumns: offerLineGridTemplate(),
              gap: 12,
              padding: isSubtotalLike ? "10px 0" : "8px 0",
              borderBottom: isSubtotalLike ? "1px solid #999" : "1px solid #ddd",
              borderTop: String(dragOverOfferLineId || "") === String(ln.id || "") ? "3px solid #2f7dd1" : (isSubtotalLike ? "1px solid #ddd" : "none"),
              fontSize: 14,
              lineHeight: 1.45,
              alignItems: isSubtotalLike ? "start" : "center",
              cursor: canEditLine ? "move" : "default",
              background: String(draggingOfferLineId || "") === String(ln.id || "") ? "#eef6ff" : (isSubtotalLike ? "#fafafa" : "transparent"),
            }}
          >
            {isSubtotalLike ? (
              <>
                <div
                  style={{
                    gridColumn: subtotalDescriptionColumn,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    fontWeight: 700,
                    ...fixedTextStyle,
                  }}
                >
                  {description || type}
                </div>

                <div
                  style={{
                    gridColumn: subtotalTotalsColumn,
                    display: "grid",
                    gridTemplateColumns: "minmax(95px, 1fr) 110px",
                    columnGap: 12,
                    rowGap: 3,
                    textAlign: "right",
                    whiteSpace: "nowrap",
                    fontSize: 14,
                  }}
                >
                  <div>Subtotaal excl. btw</div>
                  <div>{euro(runningTotals?.subtotal || 0)}</div>

                  {(runningTotals?.vatRows || []).map((row) => (
                    <React.Fragment key={`${ln.id || idx}-${row.rate}`}>
                      <div>{row.label}</div>
                      <div>{euro(row.vat)}</div>
                    </React.Fragment>
                  ))}

                  <div
                    style={{
                      borderTop: "2px solid #222",
                      paddingTop: 5,
                      fontWeight: 700,
                    }}
                  >
                    Totaal incl. btw
                  </div>
                  <div
                    style={{
                      borderTop: "2px solid #222",
                      paddingTop: 5,
                      fontWeight: 700,
                    }}
                  >
                    {euro(runningTotals?.total || 0)}
                  </div>
                </div>

                {renderOfferActions(ln)}
              </>
            ) : (
              <>
                <div
                  style={{
                    gridColumn: descriptionColumn,
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    fontWeight: isHeader ? 700 : 400,
                    ...fixedTextStyle,
                  }}
                >
                  {description}
                </div>

                {renderOfferActions(ln)}
              </>
            )}
          </div>
        );
      }

      return (
        <div
          key={ln.id}
          {...offerLineDragProps(ln, isEditingLine)}
          style={{
            display: "grid",
            gridTemplateColumns: offerLineGridTemplate(),
            gap: 12,
            padding: "8px 0",
            borderBottom: "1px solid #ddd",
            borderTop: String(dragOverOfferLineId || "") === String(ln.id || "") ? "3px solid #2f7dd1" : "none",
            fontSize: 14,
            lineHeight: 1.45,
            alignItems: "start",
            cursor: canEditLine ? "move" : "default",
            background: String(draggingOfferLineId || "") === String(ln.id || "") ? "#eef6ff" : "transparent",
          }}
        >
          <div
            onClick={() => {
              if (canEditLine) startEditInvoiceLine(ln);
            }}
            title={canEditLine ? "Klik om deze regel te bewerken" : ""}
            style={{ textAlign: "right", cursor: canEditLine ? "pointer" : "default" }}
          >
            {qty}
          </div>

          <div
            onClick={() => {
              if (canEditLine) startEditInvoiceLine(ln);
            }}
            title={canEditLine ? "Klik om deze regel te bewerken" : ""}
            style={{
              whiteSpace: "pre-wrap",
              overflowWrap: "anywhere",
              wordBreak: "break-word",
              width: "100%",
              cursor: canEditLine ? "text" : "default",
            }}
          >
            {lineDescription(ln)}
          </div>

          <div
            onClick={() => {
              if (canEditLine) startEditInvoiceLine(ln);
            }}
            style={{ textAlign: "right", cursor: canEditLine ? "pointer" : "default" }}
          >
            {euro(unit)}
          </div>

          <div style={{ textAlign: "right" }}>
            {euro(lineTotal)}
          </div>

          {!invoiceTotals.hideVatColumnOnInvoice && (
            <div
              onClick={() => {
                if (canEditLine) startEditInvoiceLine(ln);
              }}
              style={{ textAlign: "right", cursor: canEditLine ? "pointer" : "default" }}
            >
              {vatLabelForRate(rate).replace(" btw", "")}
            </div>
          )}

          {renderOfferActions(ln)}
        </div>
      );
    }

    function renderNewOfferLineBlock() {
      const draftIsArticle = offerLineDraftIsAmount();

      if (isLocked || popupMode === "preview" || editingLineId) return null;

      return (
        <div style={{ marginTop: 18 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: offerLineGridTemplate(),
              gap: 12,
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <div></div>

            <div
              style={{
                gridColumn: "2 / 3",
                display: "grid",
                gridTemplateColumns: "110px auto minmax(320px, 1fr)",
                gap: 12,
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 700,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                }}
              >
                Projectbeheer?
              </div>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13,
                  whiteSpace: "nowrap",
                  userSelect: "none",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={includeChildProjects}
                  onChange={(e) => setIncludeChildProjects(e.target.checked)}
                  disabled={!formHasId() || isLocked}
                />

                Onderliggende onderdelen?
              </label>

              <select
                value={selectedProjectLineId}
                onChange={(e) => vulRegelUitProjectbeheer(e.target.value)}
                disabled={!formHasId() || isLocked}
                style={{
                  ...selectStyle,
                  width: "100%",
                }}
              >
                <option value="__manual__">Handmatig invoeren</option>

                {projectItems.map((p) => (
                  <option key={p.id} value={p.id}>
                    {`${p.full_code || p.code || ""} ${p.name || ""}`.trim()}
                  </option>
                ))}
              </select>
            </div>

            <div></div>
          </div>

          {draftIsArticle && renderOfferHeader("new-offer-line-header")}

          {selectedProjectLineId && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: offerLineGridTemplate(),
                gap: 12,
                alignItems: "start",
                marginTop: draftIsArticle ? 8 : 0,
              }}
            >
              {draftIsArticle ? (
                <input
                  ref={qtyInputRef}
                  value={lineDraft.quantity}
                  onChange={(e) =>
                    setLineDraft({ ...lineDraft, quantity: e.target.value })
                  }
                  onFocus={(e) => e.target.select()}
                  onClick={(e) => e.target.select()}
                  placeholder="1"
                  style={{
                    ...inputStyle,
                    textAlign: "right",
                    padding: "8px 8px 8px 12px",
                  }}
                />
              ) : (
                <div></div>
              )}

              <div>
                {renderLineTypeSelect()}

                <textarea
                  ref={lineDescRef}
                  value={lineDraft.description}
                  onChange={(e) => {
                    setLineDraft({ ...lineDraft, description: e.target.value });
                    e.target.style.height = "auto";
                    e.target.style.height = `${e.target.scrollHeight}px`;
                  }}
                  placeholder={"Omschrijving\nBijv. Artikel / Tekst / Kop / Subtotaal / Totaal"}
                  style={{
                    ...inputStyle,
                    minHeight: 64,
                    resize: "none",
                    overflow: "hidden",
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                    width: "100%",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                    whiteSpace: "pre-wrap",
                    padding: "8px 8px",
                    lineHeight: "1.4",
                    fontWeight: normalizeOfferLineType(lineDraft.line_type || "Artikel") === "Kop" ? 700 : 400,
                  }}
                />
              </div>

              {draftIsArticle ? (
                <input
                  ref={linePriceRef}
                  value={lineDraft.unit_price}
                  onChange={(e) =>
                    setLineDraft({ ...lineDraft, unit_price: e.target.value })
                  }
                  onBlur={handleUnitPriceCommit}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      focusNextPopupField(+1);
                    }
                  }}
                  onFocus={(e) => e.target.select()}
                  onClick={(e) => e.target.select()}
                  placeholder="excl. btw"
                  style={{
                    ...inputStyle,
                    textAlign: "right",
                    padding: "8px 8px",
                  }}
                />
              ) : (
                <div></div>
              )}

              <div
                style={{
                  textAlign: "right",
                  fontSize: 14,
                  fontWeight: 600,
                  paddingTop: 10,
                }}
              >
                {draftIsArticle
                  ? euro(numberFromAny(lineDraft.quantity, 0) * numberFromAny(lineDraft.unit_price, 0))
                  : ""}
              </div>

              {!invoiceTotals.hideVatColumnOnInvoice && (
                draftIsArticle ? (
                  <div style={{ textAlign: "right" }}>
                    <select
                      ref={lineVatRef}
                      value={lineDraft.vat_rate}
                      onChange={(e) =>
                        setLineDraft({ ...lineDraft, vat_rate: e.target.value })
                      }
                      disabled={!form.id || isLocked}
                      style={{
                        ...selectStyle,
                        padding: "6px 6px",
                        width: 70,
                        display: "inline-block",
                      }}
                    >
                      <option value="hoog">21%</option>
                      <option value="laag">9%</option>
                      <option value="0">0%</option>
                      <option value="verlegd">verlegd</option>
                    </select>
                  </div>
                ) : (
                  <div></div>
                )
              )}

              <div style={{ textAlign: "center", paddingTop: 2 }}>
                <button
                  type="button"
                  onClick={addInvoiceLine}
                  disabled={!formHasId() || isLocked}
                  title="Regel opslaan"
                  style={{
                    border: "1px solid #ddd",
                    background: !formHasId() || isLocked ? "#f2f2f2" : "#2f7dd1",
                    color: !formHasId() || isLocked ? "#999" : "#fff",
                    cursor: !formHasId() || isLocked ? "not-allowed" : "pointer",
                    fontSize: 12,
                    borderRadius: 6,
                    padding: "6px 8px",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  ✓
                </button>
              </div>
            </div>
          )}
        </div>
      );
    }

    function renderOfferLinesBlock() {
      const rendered = [];
      let lastWasArticle = false;

      (lines || []).forEach((ln, idx) => {
        const currentIsArticle = lineTypeOf(ln) === "Artikel";

        if (currentIsArticle && !lastWasArticle) {
          rendered.push(renderOfferHeader(`offer-line-header-${idx}`));
        }

        rendered.push(renderOfferLine(ln, idx));
        lastWasArticle = currentIsArticle;
      });

      return (
        <>
          <div style={{ marginTop: 8 }}>
            {(lines || []).length === 0 ? (
              <div style={{ padding: "14px 0", color: "#777", fontSize: 14 }}>
                (nog geen offerteregels)
              </div>
            ) : (
              rendered
            )}

            {renderNewOfferLineBlock()}

            {lineError && (
              <div style={{ marginTop: 10, color: "crimson", fontSize: 13 }}>
                {lineError}
              </div>
            )}
          </div>
        </>
      );
    }

    // ---------- RENDER ----------
    return (
  <>
        <div>
<div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "1fr"
                : `minmax(0, 1fr) minmax(${RIGHT_WIDTH}px, ${RIGHT_WIDTH}px)`,
              columnGap: GAP,
              rowGap: isMobile ? 22 : 0,
              alignItems: "start",
              minWidth: 0,
            }}
          >
            {/* LINKS */}
            <div style={{ minWidth: 0 }}>

<h1 style={{ marginTop: 0, marginBottom: 10 }}>
  Opdrachten <span style={{ fontSize: 16 }}>(Offertes)</span>{" "}
  <span style={{ fontSize: 12, opacity: 0.5 }}>{VERSION}</span>
</h1>

<>
                {!isMobile ? (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      alignItems: "center",
                      flexWrap: "nowrap",
                    }}
                  >
                    <input
                      ref={searchRef}
                      value={q}
                      onChange={(e) => {
                        setQ(e.target.value);
                        setMode("search");
                      }}
                      placeholder="Zoek…"
                      style={searchInputStyle}
                      onFocus={() => setMode("search")}
                    />

                    <div
                      title={`Aantal regels in het grid: ${gridCount}`}
                      style={{
                        minWidth: 52,
                        height: 32,
                        padding: "0 10px",
                        border: "1px solid #d9d9d9",
                        borderRadius: 4,
                        background: "#f7fafc",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#1f4e79",
                        boxSizing: "border-box",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {gridCount}
                    </div>

                    <button
                      onMouseDown={(e) => e.preventDefault()}
                      onTouchStart={(e) => e.preventDefault()}
                      onClick={startSpeakSmart}
                      disabled={!voice.supported || voice.listening}
                      style={!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle}
                      title="1x klikken = 1 commando of invul-tekst dicteren"
                    >
                      {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
                    </button>

<button
  onClick={newItem}
  disabled={isReadonly}
  style={isReadonly ? buttonDisabledStyle : buttonStyle}
>
  ✚ Nieuw
</button>

<button onClick={vernieuwList} style={buttonStyle}>
  🔄 Vernieuw
</button>

<button
  onClick={() => openAiPopup("page")}
  style={buttonStyle}
>
  ✨ AI
</button>
                  </div>
                ) : (
                  <>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "minmax(0, 1fr) 58px",
                        gap: 6,
                        marginBottom: 8,
                        alignItems: "center",
                      }}
                    >
                      <input
                        ref={searchRef}
                        value={q}
                        onChange={(e) => {
                          setQ(e.target.value);
                          setMode("search");
                        }}
                        placeholder="Zoek…"
                        style={{
                          ...searchInputStyle,
                          width: "100%",
                          minWidth: 0,
                        }}
                        onFocus={() => setMode("search")}
                      />

                      <div
                        title={`Aantal regels in het grid: ${gridCount}`}
                        style={{
                          height: 34,
                          minHeight: 34,
                          padding: "0 8px",
                          border: "1px solid #d9d9d9",
                          borderRadius: 4,
                          background: "#f7fafc",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#1f4e79",
                          boxSizing: "border-box",
                          whiteSpace: "nowrap",
                          lineHeight: "1",
                        }}
                      >
                        {gridCount}
                      </div>
                    </div>

                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr 1fr 1fr",
                        gap: 6,
                      }}
                    >
                      <button
                        onMouseDown={(e) => e.preventDefault()}
                        onTouchStart={(e) => e.preventDefault()}
                        onClick={startSpeakSmart}
                        disabled={!voice.supported || voice.listening}
                        style={{
                          ...(!voice.supported || voice.listening ? buttonDisabledStyle : buttonStyle),
                          padding: "10px 8px",
                          fontSize: 13,
                        }}
                        title="1x klikken = 1 commando of invul-tekst dicteren"
                      >
                        {voice.listening ? "🎤..." : "🎤 Spreek"}
                      </button>

                      <button
                        onClick={newItem}
                        disabled={isReadonly}
                        style={{
                          ...(isReadonly
                            ? { ...buttonStyle, opacity: 0.5, cursor: "not-allowed" }
                            : buttonStyle),
                          padding: "10px 8px",
                          fontSize: 13,
                        }}
                      >
                        ✚ Nieuw
                      </button>

                      <button
                        onClick={() => openAiPopup("page")}
                        style={{
                          ...buttonStyle,
                          padding: "10px 8px",
                          fontSize: 13,
                        }}
                      >
                        ✨ AI
                      </button>

                      <button
                        onClick={vernieuwList}
                        style={{
                          ...buttonStyle,
                          padding: "10px 8px",
                          fontSize: 13,
                        }}
                      >
                        🔄 Vernieuw
                      </button>
                    </div>
                  </>
                )}
              </>

              <div style={{ marginTop: 10 }}>
                {loading && <div>Laden…</div>}
                {error && <div style={{ color: "crimson" }}>{error}</div>}
                {flash && <div style={{ marginBottom: 8, color: "green" }}>{flash}</div>}
                {saveError && <div style={{ marginBottom: 8, color: "crimson" }}>{saveError}</div>}
                {payError && <div style={{ marginBottom: 8, color: "crimson" }}>{payError}</div>}
                {lastApprovalInfo?.approval_url && (
                  <div style={{ marginBottom: 8, color: "#1f4e79", lineHeight: 1.45 }}>
                    Akkoordlink gemaakt.
                    <br />
                    Verzonden via: {approvalChannelLabel(lastApprovalInfo.channel)}
                    <br />
                    Verzonden door: {lastApprovalInfo.sent_by_name || "StaffIQ"}{lastApprovalInfo.sent_by_email ? ` (${lastApprovalInfo.sent_by_email})` : ""}
                    <br />
                    Ontvangen door: {lastApprovalInfo.sent_to_name || "-"}{lastApprovalInfo.sent_to ? ` (${lastApprovalInfo.sent_to})` : ""}
                    {lastApprovalInfo.email_to && (
                      <>
                        <br />
                        E-mail verstuurd naar: {lastApprovalInfo.email_to}
                      </>
                    )}
                    {lastApprovalInfo.email_accepted && (
                      <>
                        <br />
                        Geaccepteerd door mailserver: {lastApprovalInfo.email_accepted}
                      </>
                    )}
                    {lastApprovalInfo.email_rejected && (
                      <>
                        <br />
                        <span style={{ color: "#9b1c1c", fontWeight: 700 }}>
                          Niet geaccepteerd: {lastApprovalInfo.email_rejected}
                        </span>
                      </>
                    )}
                    {lastApprovalInfo.popup_blocked && (
                      <>
                        <br />
                        <span style={{ color: "#9b1c1c", fontWeight: 700 }}>
                          WhatsApp werd door de browser geblokkeerd.
                        </span>
                      </>
                    )}
                    <br />
                    <button
                      type="button"
                      onClick={() => openApprovalUrl(lastApprovalInfo.approval_url)}
                      style={{ border: "none", background: "transparent", color: "#1f4e79", textDecoration: "underline", cursor: "pointer", padding: 0 }}
                    >
                      Open akkoordpagina
                    </button>
                    {lastApprovalInfo?.whatsapp_url && lastApprovalInfo.channel !== "email" && (
                      <>
                        <br />
                        <button
                          type="button"
                          onClick={() => openWhatsAppSmart(lastApprovalInfo.whatsapp_url)}
                          style={{ border: "none", background: "transparent", color: "#1f4e79", textDecoration: "underline", cursor: "pointer", padding: 0 }}
                        >
                          Open WhatsApp opnieuw
                        </button>
                      </>
                    )}
                  </div>
                )}
                {voice.error && !hideVoiceError && <div style={{ color: "crimson" }}>Spraakfout: {voice.error}</div>}
              </div>

              {!loading && !error && (
                <div style={{ marginTop: 8, minWidth: 0 }}>
                  <div
                    ref={midScrollRef}
                    style={{
                      overflowX: "auto",
                      overflowY: "auto",

                      maxHeight: `calc(100vh - ${MID_BOTTOM_OFFSET}px)`,
                      border: "1px solid #f0f0f0",
                      borderRadius: 6,
                      background: "#fff",
                    }}
                  >
                    <div style={{ minWidth: TABLE_MIN_WIDTH }}>
                      <table style={tableStyle}>
<colgroup>
  <col style={{ width: colWidths.zoeknaam }} />
  <col style={{ width: colWidths.status }} />
  <col style={{ width: colWidths.contact }} />
  <col style={{ width: colWidths.invnr }} />
  <col style={{ width: colWidths.invdt }} />
  <col style={{ width: colWidths.totaal }} />
  <col style={{ width: colWidths.tel }} />
  <col style={{ width: colWidths.mob }} />
  <col style={{ width: colWidths.email }} />
  <col style={{ width: colWidths.city }} />
  <col style={{ width: colWidths.country }} />
  <col style={{ width: colWidths.companyCode }} />
</colgroup>

                        <thead>
<tr>
  <th style={thClickable} onClick={() => setSort({ key: "org", dir: sort.key === "org" && sort.dir === "asc" ? "desc" : "asc" })}>
    Relatie
  </th>
  <th style={thClickable} onClick={() => setSort({ key: "status", dir: sort.key === "status" && sort.dir === "asc" ? "desc" : "asc" })}>
    Status
  </th>
  <th style={thClickable} onClick={() => setSort({ key: "contact", dir: sort.key === "contact" && sort.dir === "asc" ? "desc" : "asc" })}>
    Contactpersoon
  </th>
  <th style={thClickable} onClick={() => setSort({ key: "invoice_number", dir: sort.key === "invoice_number" && sort.dir === "asc" ? "desc" : "asc" })}>
    Referentie
  </th>
  <th style={thClickable} onClick={() => setSort({ key: "invoice_date", dir: sort.key === "invoice_date" && sort.dir === "asc" ? "desc" : "asc" })}>
    Datum
  </th>
  <th
    style={{ ...thClickable, textAlign: "right" }}
    onClick={() =>
      setSort({
        key: "totaal",
        dir: sort.key === "totaal" && sort.dir === "asc" ? "desc" : "asc",
      })
    }
  >
    Totaal
  </th>

  <th style={thBase}>Telefoon</th>
  <th style={thBase}>Mobiel</th>
  <th style={thBase}>E-mail</th>
  <th style={thClickable} onClick={() => setSort({ key: "city", dir: sort.key === "city" && sort.dir === "asc" ? "desc" : "asc" })}>
    Plaats
  </th>
  <th style={thClickable} onClick={() => setSort({ key: "country", dir: sort.key === "country" && sort.dir === "asc" ? "desc" : "asc" })}>
    Land
  </th>
  <th style={thClickable} onClick={() => setSort({ key: "company_code", dir: sort.key === "company_code" && sort.dir === "asc" ? "desc" : "asc" })}>
    Bedrijfscode
  </th>
</tr>
<tr>
                            <th style={filterThStyle}>
                              <select value={zoeknaamFilter} onChange={(e) => setZoeknaamFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {zoeknaamOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {statusOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={contactFilter} onChange={(e) => setContactFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {contactOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={invoiceNrFilter} onChange={(e) => setInvoiceNrFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {invoiceNrOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={invoiceDateFilter} onChange={(e) => setInvoiceDateFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {invoiceDateOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {formatNlDate(x)}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select
                                value={totaalFilter}
                                onChange={(e) => setTotaalFilter(e.target.value)}
                                style={selectStyle}
                              >
                                <option value="">(alle)</option>
                                {totaalOptions.map((x) => (
                                  <option key={x} value={x}>
                                    € {formatMoney(x)}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={telFilter} onChange={(e) => setTelFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {telOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={mobFilter} onChange={(e) => setMobFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {mobOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={emailFilter} onChange={(e) => setEmailFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {emailOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {cityOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={countryFilter} onChange={(e) => setCountryFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {countryOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>

                            <th style={filterThStyle}>
                              <select value={companyCodeFilter} onChange={(e) => setCompanyCodeFilter(e.target.value)} style={selectStyle}>
                                <option value="">(alle)</option>
                                {companyCodeOptions.map((x) => (
                                  <option key={x} value={x}>
                                    {x}
                                  </option>
                                ))}
                              </select>
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          {sortedItems.map((it, idx) => {
                            const isCursor = idx === listIndex;
                            const isSelected = String(selectedId || "") === String(it.id || "");

                            let bg = "transparent";
                            if (isSelected) bg = rowBgSelected;
                            else if (isCursor) bg = rowBgCursor;

                            const orgName = safeTrim(it.organisation_name || "-") || "-";
                            const contactName = safeTrim(pickContactNameFromRow(it) || "-") || "-";
                            const invNr = safeTrim(it.invoice_number || "-") || "-";
                            const invDt = isoDate(it.invoice_date) || "-";
                            const totaal = Number(it.total || 0);
                            const tel = safeTrim(it.org_phone || "-") || "-";
                            const mob = safeTrim(it.org_mobile || "-") || "-";
                            const email = safeTrim(it.org_email || "-") || "-";
                            const city = safeTrim(it.org_city || "-") || "-";
                            const ctry = safeTrim(it.org_country || "-") || "-";
                            const companyCodeName = safeTrim(pickCompanyCodeName(it));
                            const st = statusLabel(it.status);

                            const paidText = it.id === selectedId ? paidLabel : "-";

                            return (
<tr
  key={it.id}
  ref={(el) => (rowRefs.current[idx] = el)}
  onClick={() => loadItemFromRow(it)}
  style={{
    cursor: "pointer",
    background:
      String(selectedId) === String(it.id)
        ? "#eef6ff"
        : listIndex === idx
        ? "#f5faff"
        : "transparent",
  }}
>
  <td style={tdStyle}>{safeTrim(pickOrgSearchNameFromRow(it))}</td>
  <td style={tdStyle}>{statusLabel(it.status)}</td>
  <td style={tdStyle}>{safeTrim(pickContactNameFromRow(it))}</td>
  <td style={tdStyle}>{safeTrim(it.invoice_number || "")}</td>
  <td style={tdStyle}>{formatNlDate(it.invoice_date)}</td>
  <td style={{ ...tdStyle, textAlign: "right" }}>{euro(it.total)}</td>
  <td style={tdStyle} title={safeTrim(it.org_phone || "")}>
    {safeTrim(it.org_phone || "") ? (
      <button
        type="button"
        onClick={(e) => handlePhoneGridClick(e, safeTrim(it.org_phone || ""))}
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          margin: 0,
          color: "#1f4e79",
          textDecoration: "underline",
          cursor: "pointer",
          font: "inherit",
        }}
        title={`Bellen: ${safeTrim(it.org_phone || "")}`}
      >
        {safeTrim(it.org_phone || "")}
      </button>
    ) : (
      "-"
    )}
  </td>

  <td style={tdStyle} title={safeTrim(it.org_mobile || "")}>
    {safeTrim(it.org_mobile || "") ? (
      <button
        type="button"
        onClick={(e) => handleMobileGridClick(e, safeTrim(it.org_mobile || ""))}
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          margin: 0,
          color: "#1f4e79",
          textDecoration: "underline",
          cursor: "pointer",
          font: "inherit",
        }}
        title={`Bellen / SMS / WhatsApp: ${safeTrim(it.org_mobile || "")}`}
      >
        {safeTrim(it.org_mobile || "")}
      </button>
    ) : (
      "-"
    )}
  </td>

  <td style={tdStyle} title={safeTrim(it.org_email || "")}>
    {safeTrim(it.org_email || "") ? (
      <button
        type="button"
        onClick={(e) => handleEmailGridClick(e, safeTrim(it.org_email || ""))}
        style={{
          border: "none",
          background: "transparent",
          padding: 0,
          margin: 0,
          color: "#1f4e79",
          textDecoration: "underline",
          cursor: "pointer",
          font: "inherit",
        }}
        title={`E-mailen: ${safeTrim(it.org_email || "")}`}
      >
        {safeTrim(it.org_email || "")}
      </button>
    ) : (
      "-"
    )}
  </td>
  <td style={tdStyle}>{safeTrim(it.org_city || "")}</td>
  <td style={tdStyle}>{safeTrim(it.org_country || "")}</td>
  <td style={tdStyle} title={companyCodeName}>{companyCodeName || "-"}</td>
</tr>
                            );
                          })}

                          {sortedItems.length === 0 && (
                            <tr>
                              <td colSpan={12} style={{ padding: 12, opacity: 0.7 }}>
                                Geen resultaten (filters te streng?)
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>

{/* RECHTS */}
<div
  ref={rightScrollRef}
  style={{
    width: isMobile ? "100%" : 320,
    minWidth: isMobile ? 0 : 320,
    maxWidth: isMobile ? "100%" : 320,
    position: isMobile ? "relative" : "sticky",
    top: isMobile ? "auto" : 0,
    alignSelf: "start",
    maxHeight: isMobile ? "none" : "calc(100vh - 40px)",
    overflowY: isMobile ? "visible" : "auto",
    overflowX: "hidden",
    background: "white",
    border: "10px solid #0b4f7a",
    borderRadius: 10,
    padding: 10,
    boxSizing: "border-box",
    marginTop: isMobile ? 6 : 0,
    paddingTop: isMobile ? 6 : 10,
  }}
>

              <div
                style={{
                  background: "white",
                  borderRadius: 10,
                  padding: 12,
                  boxSizing: "border-box",
                  width: "100%",
                  maxWidth: "100%",
                  minWidth: 0,
                  overflowX: "hidden",
                }}
              >
<h3 style={{ margin: "0 0 10px 0" }}>
  Opdracht (Offerte)
</h3>

<div style={{ display: "grid", gap: 8, width: "100%", minWidth: 0 }}>

<label style={{ fontSize: 13, color: "#333" }}>Relatie</label>
<select
  ref={orgRef}
  value={form.organisation_id}
  onChange={(e) => {
    const v = e.target.value;
    setForm((p) => ({ ...p, organisation_id: v, contact_id: "" }));
    setOrgDetail(null);
    if (v) loadOrgDetail(v);
  }}
  style={selectStyle}
  disabled={isFormLocked || isFormDisabled}
  title={isLocked ? "Read-only (gefactureerd)" : ""}
>
  <option value="">(kies)</option>
  {orgs.map((o) => (
    <option key={o.id} value={String(o.id)}>
      {pickOrgSearchName(o)}
    </option>
  ))}
</select>

<label
  style={{
    fontSize: 13,
    color: "#333",
    opacity: selectedRelationIsPrivate ? 0.55 : 1,
  }}
>
  Contactpersoon
</label>
<select
  ref={contactRef}
  value={selectedRelationIsPrivate ? "" : form.contact_id}
  onChange={(e) => setForm((p) => ({ ...p, contact_id: e.target.value }))}
  style={{
    ...selectStyle,
    opacity: selectedRelationIsPrivate ? 0.55 : 1,
    cursor: selectedRelationIsPrivate ? "not-allowed" : "pointer",
  }}
  disabled={isFormLocked || isFormDisabled || selectedRelationIsPrivate}
  title={
    selectedRelationIsPrivate
      ? "Niet van toepassing bij particulier"
      : isLocked
        ? "Read-only (gefactureerd)"
        : ""
  }
>
  <option value="">
    {selectedRelationIsPrivate
      ? "(niet van toepassing bij particulier)"
      : "(geen)"}
  </option>
  {!selectedRelationIsPrivate &&
    contacts
      .filter((c) => c.active !== false)
      .map((c) => (
        <option key={c.id} value={String(c.id)}>
          {pickContactFullName(c)}
        </option>
      ))}
</select>

<label style={{ fontSize: 13, color: "#333" }}>Bedrijfscode</label>
<input
  value={pickCompanyCodeNameFromOrganisation(activeOrg) || ""}
  readOnly
  disabled
  style={{
    ...inputStyle,
    background: "#f7f7f7",
    color: "#555",
  }}
/>

<label style={{ fontSize: 13, color: "#333" }}>Sjabloon</label>
<select
  value={form.template_id || ""}
  onChange={(e) => setForm((p) => ({ ...p, template_id: e.target.value }))}
  style={selectStyle}
  disabled={isFormLocked || isFormDisabled}
>
  <option value="">(geen sjabloon)</option>
  {selectableOfferTemplates.map((tpl) => (
    <option key={tpl.id} value={String(tpl.id)}>
      {tpl.name} ({templateTypeLabel(tpl.template_type)})
    </option>
  ))}
</select>

<label style={{ fontSize: 13, color: "#333" }}>Referentie</label>
<input
  ref={invNumRef}
  value={form.invoice_number || ""}
  onChange={(e) => setForm((p) => ({ ...p, invoice_number: e.target.value }))}
  style={inputStyle}
  disabled={isFormLocked || isFormDisabled}
  placeholder="(vrij in te vullen)"
  title=""
/>

<label style={{ fontSize: 13, color: "#333" }}>Datum</label>
<input
  ref={invDateRef}
  type="date"
  value={form.invoice_date || ""}
  onChange={(e) => setForm((p) => ({ ...p, invoice_date: e.target.value }))}
  style={dateInputStyle}
  disabled={isFormLocked || isFormDisabled}
/>

<div style={{ display: "grid", gap: 8 }}>

<button
  onClick={openOfferPopupEdit}
  disabled={isFormLocked}
  style={
    isFormLocked
      ? {
          ...buttonPrimary("#2f7dd1"),
          width: "100%",
          boxSizing: "border-box",
          opacity: 0.5,
          cursor: "not-allowed",
        }
      : {
          ...buttonPrimary("#2f7dd1"),
          width: "100%",
          boxSizing: "border-box",
        }
  }
  title={!formHasId() ? "Sla op en open opdracht" : "Open complete opdracht in popup"}
>
  Opdracht (Offerte)
</button>
</div>

<hr style={{ margin: "10px 0" }} />

<div style={{ fontWeight: 600, marginBottom: 6 }}>Totalen</div>

<div
  style={{
    border: "1px solid #e5e5e5",
    borderRadius: 8,
    padding: 10,
    fontSize: 12,
    display: "grid",
    gap: 6,
  }}
>
</div>

                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <span>Subtotaal</span>
                      <b>{euro(invoiceTotals.subtotal)}</b>
                    </div>

                    {invoiceTotals.vatRows.map((row) => (
                      <div
                        key={row.rate}
                        style={{ display: "flex", justifyContent: "space-between" }}
                      >
                        <span>{row.label}</span>
                        <span>{euro(row.vat)}</span>
                      </div>
                    ))}

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        borderTop: "1px solid #ddd",
                        paddingTop: 6,
                        marginTop: 2,
                        fontSize: 13,
                      }}
                    >
                      <span><b>Totaal</b></span>
                      <b>{euro(invoiceTotals.total)}</b>
                    </div>

                    {invoiceTotals.allVerlegd && (
                      <div style={{ color: "#555", marginTop: 4 }}>Btw verlegd</div>
                    )}

                    {gPercentage > 0 && (
                      <>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            borderTop: "1px solid #eee",
                            paddingTop: 6,
                            marginTop: 4,
                          }}
                        >
                          <span>G-rekening ({gPercentage}%)</span>
                          <b>{euro(gAmount)}</b>
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                          <span>IBAN ({100 - gPercentage}%)</span>
                          <b>{euro(ibanAmount)}</b>
                        </div>
                      </>
                    )}
                  </div>

                  <hr style={{ margin: "14px 0 10px 0" }} />

                  <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
                    <label style={{ fontSize: 13, color: "#333" }}>Opmerking</label>

                    <input
                      ref={payNotesRef}
                      value={form.notes || ""}
                      onChange={(e) => setForm({ ...form, notes: e.target.value })}
                      placeholder="(optioneel)"
                      style={{
                        ...inputStyle,
                        marginTop: 6
                      }}
                      disabled={!form.id}
                    />

                  </div>
                </div>

                <hr style={{ margin: "10px 0" }} />

<label style={{ fontSize: 13, color: "#333" }}>Status</label>
<select
  ref={statusRef}
  value={normalizeDbStatus(form.status || "OFFERTE")}
  disabled={!isAdmin}
  onChange={(e) =>
    setForm((prev) => ({ ...prev, status: e.target.value }))
  }
>
{statusSelectOptions.map((opt) => (
  <option key={opt.value} value={opt.value}>
    {opt.label}
  </option>
))}
</select>

                <div style={{ fontSize: 12, color: "#666" }}>
                  <div>

                  </div>
                </div>

                <hr style={{ margin: "10px 0" }} />

<div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 12 }}>
  <div style={{ display: "flex", gap: 6, minWidth: 0 }}>
<button
  onClick={openOfferPopupPreview}
  disabled={
    isFormLocked ||
    normalizeDbStatus(form.status || "OFFERTE") !== "OFFERTE"
  }
  style={{
    ...buttonPrimary("#6c757d"),
    flex: 1,
    ...(isFormLocked ||
    normalizeDbStatus(form.status || "OFFERTE") !== "OFFERTE"
      ? { opacity: 0.5, cursor: "not-allowed" }
      : {}),
  }}
>
  Voorbeeld offerte
</button>

<button
      onClick={finalizeInvoice}
      disabled={
        isFormLocked ||
        isReadonly ||
        !formHasId() ||
        normalizeDbStatus(form.status || "OFFERTE") !== "OFFERTE"
      }
      style={{
        ...buttonPrimary("#28a745"),
        flex: 1,
        ...(isFormLocked ||
        isReadonly ||
        !formHasId() ||
        normalizeDbStatus(form.status || "OFFERTE") !== "OFFERTE"
          ? { opacity: 0.5, cursor: "not-allowed" }
          : {}),
      }}
    >
      Offerte goedgekeurd
    </button>

  </div>

  <div style={{ display: "flex", gap: 6, minWidth: 0 }}>
<button
  onClick={openOfferPopupPreview}
  disabled={
    isFormLocked ||
    normalizeDbStatus(form.status || "OFFERTE") === "OFFERTE"
  }
  style={{
    ...buttonPrimary("#6c757d"),
    flex: 1,
    ...(isFormLocked ||
    normalizeDbStatus(form.status || "OFFERTE") === "OFFERTE"
      ? { opacity: 0.5, cursor: "not-allowed" }
      : {}),
  }}
>
  Voorbeeld opdracht
</button>

<button
  onClick={async () => {
    if (isFormLocked || isReadonly) return;

    try {
      setSaveError(null);
      setFlash("");

      if (!formHasId()) {
        setSaveError("Sla eerst de opdracht op.");
        return;
      }

      const ok = window.confirm("Opdracht omzetten naar factuur?");
      if (!ok) return;

      // 1. Status naar FACTUUR zetten
      const approvedOffer = await tryFetchJson(
        [`/api/offers/${encodeURIComponent(form.id)}`],
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            organisation_id: form.organisation_id ? Number(form.organisation_id) : null,
            contact_id: form.contact_id ? Number(form.contact_id) : null,
            invoice_date: form.invoice_date || null,
            due_date: form.due_date || null,
            invoice_number: form.invoice_number || null,
            status: "FACTUUR",
            series_code: form.series_code || null,
            notes: form.notes || "",
            subtotal: Number(form.subtotal || 0),
            vat_total: Number(form.vat_total || 0),
            total: Number(form.total || 0),
          }),
        }
      );

      // 2. OFFERTE REGELS OPHALEN
      const offerDetail = await tryFetchJson([
        `/api/offers/${encodeURIComponent(form.id)}`
      ]);

      const offerLines =
        Array.isArray(offerDetail?.lines) ? offerDetail.lines :
        Array.isArray(offerDetail?.items) ? offerDetail.items :
        Array.isArray(offerDetail?.offer_lines) ? offerDetail.offer_lines :
        Array.isArray(lines) ? lines :
        [];

      // 3. NIEUWE FACTUUR AANMAKEN
      const invoicePayload = {
        organisation_id: approvedOffer?.organisation_id ?? Number(form.organisation_id),
        contact_id: approvedOffer?.contact_id ?? Number(form.contact_id),
        invoice_date: todayLocalIso(),
        due_date: "",
        invoice_number: "",
        status: "DRAFT",
        series_code: form.series_code || null,
        notes: form.notes || "",
      };

      const createdInvoice = await tryFetchJson(
        ["/api/invoices"],
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(invoicePayload),
        }
      );

      const newInvoiceId = createdInvoice?.id;
      if (!newInvoiceId) {
        throw new Error("Geen factuur-id ontvangen.");
      }

      // 4. REGELS KOPIËREN
      for (let i = 0; i < offerLines.length; i++) {
        const ln = offerLines[i];

await tryFetchJson(
  [`/api/invoices/${newInvoiceId}/lines`],
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              invoice_id: newInvoiceId,
              line_type: lineTypeOf(ln),
              quantity: isAmountLine(ln) ? (ln.quantity ?? ln.qty ?? 0) : 0,
              description: ln.description || "",
              unit_price: isAmountLine(ln) ? (ln.unit_price ?? ln.price ?? 0) : 0,
              vat_rate: ln.vat_rate ?? ln.vat ?? "hoog",
            }),
          }
        );
      }

setSilentDocumentPreview(true);
setPopupMode("preview");
setShowLinesPopup(true);

await saveOfferDocumentFromPreview(form.id, "order");

setShowLinesPopup(false);
setSilentDocumentPreview(false);

setTimeout(() => {
  setShowLinesPopup(false);
}, 50);

// 5. UI UPDATEN
      setForm((prev) => ({
        ...prev,
        ...approvedOffer,
        status: "FACTUUR",
      }));

      setItems((prev) =>
        prev.map((it) =>
          String(it.id) === String(form.id)
            ? { ...it, ...approvedOffer, status: "FACTUUR" }
            : it
        )
      );

      setFlash("Opdracht → Factuur + conceptfactuur gemaakt ✅");
      setTimeout(() => setFlash(""), 2000);

      alert("Conceptfactuur is aangemaakt inclusief regels.");

    } catch (e) {
      setSaveError(e?.message || String(e));
    }
  }}
  disabled={
    isFormLocked ||
    isReadonly ||
    !formHasId() ||
    normalizeDbStatus(form.status || "OFFERTE") !== "OPDRACHT"
  }
  style={
    isFormLocked ||
    isReadonly ||
    !formHasId() ||
    normalizeDbStatus(form.status || "OFFERTE") !== "OPDRACHT"
      ? { ...buttonPrimary("#28a745"), opacity: 0.5, cursor: "not-allowed" }
      : buttonPrimary("#28a745")
  }
>
  Opdracht goedgekeurd
</button>

  </div>

  <div style={{ display: "flex", gap: 6, minWidth: 0 }}>
    <button
      onClick={saveItem}
      disabled={isFormLocked || isReadonly}
      style={{
        ...buttonPrimary("#2f7dd1"),
        flex: 1,
        ...(isFormLocked || isReadonly ? { opacity: 0.5, cursor: "not-allowed" } : {}),
      }}
    >
      Opslaan
    </button>

    <button
      onClick={deleteItem}
      disabled={isFormLocked || !deleteEnabled || isLocked || isReadonly}
      style={{
        ...buttonPrimary("#d64545"),
        flex: 1,
        ...(isFormLocked || !deleteEnabled || isLocked || isReadonly
          ? { opacity: 0.5, cursor: "not-allowed" }
          : {}),
      }}
    >
      Verwijderen
    </button>
  </div>

  <div style={{ marginTop: 10, opacity: 0.7, fontSize: 12 }}>
    Offerte-id: {formHasId() ? String(form.id) : "(nieuw)"}{" "}
    {isLocked ? "— read-only" : ""}
  </div>
</div>
            </div>
          </div>
        </div>

{showLinesPopup && (
<div
  style={{
    position: "fixed",
    inset: 0,
    background: silentDocumentPreview ? "transparent" : "rgba(0,0,0,0.35)",
    zIndex: 99999,
    overflowY: "auto",
    overflowX: "auto",
    padding: "20px",
    opacity: silentDocumentPreview ? 0 : 1,
    pointerEvents: silentDocumentPreview ? "none" : "auto",
  }}
>
<div
  ref={popupPrintRef}
  style={{
    width: 1120,
    minWidth: 1120,
    margin: "0 auto",
    background: "#fff",
    borderRadius: 8,
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    padding: "40px 50px 40px 50px",
  }}
>

{/* KNOPPEN BOVEN */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                  marginBottom: 18,
                  paddingRight: 4,
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => openAiPopup("popup")}
                  style={buttonPrimary("#6f42c1")}
                >
                  ✨ AI
                </button>

                {popupMode === "preview" && (
                  <>
                    <button
                      onClick={printOfferPopup}
                      style={buttonPrimary("#6c757d")}
                    >
                      Printen
                    </button>

                    <button
                      onClick={sendOfferEmail}
                      disabled={sendingEmail || !formHasId() || !currentInvoiceEmail}
                      style={
                        sendingEmail || !formHasId() || !currentInvoiceEmail
                          ? { ...buttonPrimary("#198754"), opacity: 0.5, cursor: "not-allowed" }
                          : buttonPrimary("#198754")
                      }
                    >
                      {sendingEmail ? "Bezig..." : "E-mailen"}
                    </button>

                    <button
                      onClick={sendOfferForApproval}
                      disabled={approvalButtonDisabled}
                      style={
                        approvalButtonDisabled
                          ? { ...buttonPrimary("#0d6efd"), opacity: 0.5, cursor: "not-allowed" }
                          : buttonPrimary("#0d6efd")
                      }
                      title={approvalButtonTitle}
                    >
                      {sendingApproval ? "Bezig..." : "Ter akkoord"}
                    </button>
                  </>
                )}

                <button
                  onClick={closeOfferPopup}
                  style={buttonPrimary("#2f7dd1")}
                >
                  {popupMode === "preview" ? "Sluiten" : "Opslaan/Sluiten"}
                </button>

                {popupMode !== "preview" && (
                  <button
                    onClick={annuleerOfferPopup}
                    style={buttonStyle}
                  >
                    Annuleren
                  </button>
                )}
              </div>

              {/* HEADER */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                }}
              >

                {/* LOGO + KLANT */}


                <div>
                  {invoiceSettings?.logo_url ? (
                    <img
                      src={invoiceSettings.logo_url}
                      alt="logo"
                      style={{ height: 92, marginBottom: 34, objectFit: "contain" }}
                    />
                  ) : (
                    <div style={{ height: 92, marginBottom: 34 }} />
                  )}

                  <div style={{ lineHeight: 1.6, fontSize: 14 }}>
                    <div style={{ fontWeight: 600, marginBottom: 2 }}>
                      {recipientName || ""}
                    </div>

                    {selectedContact?.attention ? (
                      <div>{selectedContact.attention}</div>
                    ) : null}

                    {recipientAddressLines.map((line, idx) => (
                      <div key={idx}>{line}</div>
                    ))}
                  </div>
                </div>

{/* BEDRIJF */}
                <div style={{ textAlign: "right", lineHeight: 1.55, fontSize: 14 }}>
                  {invoiceSettings?.offer_company_name ? (
                    <>
                      <strong>{invoiceSettings.offer_company_name}</strong><br />
                    </>
                  ) : null}

                  {invoiceSettings?.offer_contact_person ? <>{invoiceSettings.offer_contact_person}<br /></> : null}

                  {invoiceSettings?.offer_company_address_1 ? <>{invoiceSettings.offer_company_address_1}<br /></> : null}
                  {invoiceSettings?.offer_company_address_2 ? <>{invoiceSettings.offer_company_address_2}<br /></> : null}

                  {invoiceSettings?.offer_company_postcode || invoiceSettings?.offer_company_city ? (
                    <>{[invoiceSettings.offer_company_postcode, invoiceSettings.offer_company_city].filter(Boolean).join(" ")}<br /></>
                  ) : null}

                  {invoiceSettings?.offer_company_country ? <>{invoiceSettings.offer_company_country}<br /></> : null}

                  {invoiceSettings?.offer_company_email || invoiceSettings?.offer_company_phone ? <><br /></> : null}

                  {invoiceSettings?.offer_company_email ? <>{invoiceSettings.offer_company_email}<br /></> : null}
                  {invoiceSettings?.offer_company_phone ? <>{invoiceSettings.offer_company_phone}<br /></> : null}

                  {invoiceSettings?.offer_company_kvk || invoiceSettings?.offer_company_vat || invoiceSettings?.offer_company_iban ? <><br /></> : null}

                  {invoiceSettings?.offer_company_kvk ? <>KvK: {invoiceSettings.offer_company_kvk}<br /></> : null}
                  {invoiceSettings?.offer_company_vat ? <>Btw: {invoiceSettings.offer_company_vat}<br /></> : null}
                  {invoiceSettings?.offer_company_iban ? <>Bank: {invoiceSettings.offer_company_iban}</> : null}
                </div>

              </div>

{/* TITEL + KENMERK */}
<div style={{ marginTop: 32, marginBottom: 18 }}>
  <div style={{ fontSize: 17, fontWeight: 500, marginBottom: 6 }}>
    Betreft:{" "}
    {safeTrim(selectedOfferTemplate?.subject) ||
      (normalizeDbStatus(form.status || "OFFERTE") === "OFFERTE"
        ? "prijsopgave"
        : "bevestiging opdracht")}
  </div>

  {form.reference_code ? (
    <div style={{ fontSize: 13 }}>
      <span style={{ display: "inline-block", width: 110 }}>Kenmerk:</span>
      {form.reference_code}
    </div>
  ) : null}

  {invoiceTotals.allVerlegd && orgVatNumber ? (
    <div style={{ fontSize: 13, marginTop: 4 }}>
      <span style={{ display: "inline-block", width: 110 }}>Btw-id:</span>
      {orgVatNumber}
    </div>
  ) : null}
</div>

{/* DATUMBLOK */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 260px",
                  gap: 20,
                  marginTop: -18,
                  marginBottom: 20,
                }}
              >
                <div />

                <div
                  style={{
                    fontSize: 13,
                    lineHeight: 1.6,
                    textAlign: "right",
                    position: "relative",
                    top: -8,
                  }}
                >
                  <div>
                    <span style={{ display: "inline-block", width: 100, textAlign: "left" }}>
                      Datum:
                    </span>
                    {formatNlDate(form.invoice_date) || "-"}
                  </div>
                  <div>

                  </div>
                </div>
</div>

{selectedOfferTemplate ? (
                <>
                  {renderTemplateBlock(selectedOfferTemplate.salutation, { marginBottom: 10 })}
                  {renderTemplateBlock(selectedOfferTemplate.intro_text)}
                  {renderTemplateBlock(selectedOfferTemplate.body_text)}
                </>
              ) : invoiceSettings?.offer_text ? (
                <div
                  style={{
                    marginTop: 6,
                    marginBottom: 22,
                    fontSize: 14,
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                    width: "100%",
                    maxWidth: "100%",
                    ...fixedOfferTextStyle(invoiceSettings.offer_text),
                  }}
                >
                  {String(invoiceSettings.offer_text || "")}
                </div>
              ) : null}

{renderOfferLinesBlock()}

<div className="offer-total-footer-block">

<div className="offer-total-footer-block">
  {/* TOTALEN */}
  <div className="offer-totals-block" style={{ marginTop: 22 }}>
    <div
      style={{
        display: "grid",

gridTemplateColumns:
  popupMode === "preview"
    ? invoiceTotals.hideVatColumnOnInvoice
      ? "70px 1fr 110px 110px"
      : "70px 1fr 110px 110px 70px"
    : invoiceTotals.hideVatColumnOnInvoice
      ? "70px 1fr 110px 110px 56px"
      : "70px 1fr 110px 110px 70px 56px",
        gap: 12,
        fontSize: 14,
        lineHeight: 1.9,
        alignItems: "center",
      }}
    >
      <div></div>
      <div></div>
      <div style={{ textAlign: "right" }}>Subtotaal</div>
      <div style={{ textAlign: "right" }}>{euro(invoiceTotals.subtotal)}</div>
      {!invoiceTotals.hideVatColumnOnInvoice && <div></div>}
      {popupMode !== "preview" && <div></div>}

      {invoiceTotals.vatRows.map((row) => (
        <React.Fragment key={row.rate}>
          <div></div>
          <div></div>
          <div style={{ textAlign: "right" }}>{row.label}</div>
          <div style={{ textAlign: "right" }}>{euro(row.vat)}</div>
          {!invoiceTotals.hideVatColumnOnInvoice && <div></div>}
          {popupMode !== "preview" && <div></div>}
        </React.Fragment>
      ))}

      <div></div>
      <div></div>
      <div
        style={{
          borderTop: "2px solid #222",
          paddingTop: 8,
          textAlign: "right",
          fontWeight: 700,
        }}
      >
        Totaal
      </div>
      <div
        style={{
          borderTop: "2px solid #222",
          paddingTop: 8,
          textAlign: "right",
          fontWeight: 700,
        }}
      >
        {euro(invoiceTotals.total)}
      </div>
      {!invoiceTotals.hideVatColumnOnInvoice && <div></div>}
      {popupMode !== "preview" && <div></div>}
    </div>
  </div>

{selectedOfferTemplate ? (
  <>
    {renderTemplateBlock(selectedOfferTemplate.conditions_text, { marginTop: 26 })}
    {renderTemplateBlock(selectedOfferTemplate.payment_text)}
    {renderTemplateBlock(selectedOfferTemplate.planning_text)}
    {renderTemplateBlock(selectedOfferTemplate.warranty_text)}
    {renderTemplateBlock(selectedOfferTemplate.closing_text)}

    {selectedOfferTemplate.show_agreement_block === true ? (
      <div
        style={{
          marginTop: 28,
          border: "1px solid #777",
          padding: 14,
          width: "60%",
          minHeight: 120,
          fontSize: 14,
          lineHeight: 1.8,
          whiteSpace: "pre-wrap",
          ...fixedOfferTextStyle(selectedOfferTemplate.agreement_text),
        }}
      >
        <div
          style={{
            fontWeight: 700,
            marginBottom: 8,
            fontFamily: "Arial, Helvetica, sans-serif",
          }}
        >
          Voor akkoord:
        </div>
        {selectedOfferTemplate.agreement_text || "Naam:________________________________________\nDatum:_______________________________________"}
      </div>
    ) : null}
  </>
) : null}

{/* VOETREGEL */}
  <div
    className="offer-footer-block"
    style={{
      marginTop: 60,
      borderTop: "1px solid #ddd",
      paddingTop: 10,
      fontSize: 12,
      color: "#555",
      whiteSpace: "pre-wrap",
      width: "68%",
      maxWidth: "68%",
      ...fixedOfferTextStyle(invoiceSettings?.offer_footer_text),
    }}
  >
    {String(invoiceSettings?.offer_footer_text || "")}
  </div>

{/* KNOPPEN ONDER */}
  <div
    style={{
      display: "flex",
      justifyContent: "flex-end",
      gap: 8,
      marginTop: 28,
      paddingRight: 4,
      flexWrap: "wrap",
    }}
  >
    <button
      onClick={() => openAiPopup("popup")}
      style={buttonPrimary("#6f42c1")}
    >
      ✨ AI
    </button>

{popupMode === "preview" && (
      <>
        <button
          onClick={printOfferPopup}
          style={buttonPrimary("#6c757d")}
        >
          Printen
        </button>

        <button
          onClick={sendOfferEmail}
          disabled={sendingEmail || !formHasId() || !currentInvoiceEmail}
          style={
            sendingEmail || !formHasId() || !currentInvoiceEmail
              ? { ...buttonPrimary("#198754"), opacity: 0.5, cursor: "not-allowed" }
              : buttonPrimary("#198754")
          }
        >
          {sendingEmail ? "Bezig..." : "E-mailen"}
        </button>

        <button
          onClick={sendOfferForApproval}
          disabled={approvalButtonDisabled}
          style={
            approvalButtonDisabled
              ? { ...buttonPrimary("#0d6efd"), opacity: 0.5, cursor: "not-allowed" }
              : buttonPrimary("#0d6efd")
          }
          title={approvalButtonTitle}
        >
          {sendingApproval ? "Bezig..." : "Ter akkoord"}
        </button>
      </>
    )}

<button
  onClick={closeOfferPopup}
  style={buttonPrimary("#2f7dd1")}
>
  {popupMode === "preview" ? "Sluiten" : "Opslaan/Sluiten"}
</button>

{popupMode !== "preview" && (
  <button
    onClick={annuleerOfferPopup}
    style={buttonStyle}
  >
    Annuleren
  </button>
)}
  </div>
</div>

</div>

</div>
</div>
)}

{aiOpen && (
  <div
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.35)",
      zIndex: 100000,
      padding: isMobile ? 10 : 24,
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <div
      style={{
        width: isMobile ? "100%" : 900,
        height: isMobile ? "92vh" : "82vh",
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 20px 70px rgba(0,0,0,0.30)",
        padding: 18,
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 12,
        }}
      >
<h2 style={{ margin: 0 }}>
  {aiSource === "popup" ? "AI Opdracht (Offerte)" : "AI Opdrachten (Offertes)"}
</h2>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={printAiDocument} disabled={aiBusy} style={aiBusy ? buttonDisabledStyle : buttonStyle}>
            Printen
          </button>
          <button
            onClick={startAiSpeak}
            disabled={!voice.supported || voice.listening || aiBusy}
            style={!voice.supported || voice.listening || aiBusy ? buttonDisabledStyle : buttonStyle}
          >
            {voice.listening ? "🎤 Luistert…" : "🎤 Spreek"}
          </button>
          <button onClick={closeAiPopup} disabled={aiBusy} style={aiBusy ? buttonDisabledStyle : buttonStyle}>
            Sluiten
          </button>
        </div>
      </div>

      <div style={{ marginBottom: 10, fontWeight: 700 }}>Kies een actie:</div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr 1fr",
          gap: 8,
          marginBottom: 14,
        }}
      >
        <button onClick={runAiOrganisationData} disabled={aiBusy} style={aiBusy ? buttonDisabledStyle : buttonStyle}>
          Relatie gegevens
        </button>

        {aiSource === "popup" ? (
          <button onClick={runAiOfferCheck} disabled={aiBusy} style={aiBusy ? buttonDisabledStyle : buttonStyle}>
            Opdracht (Offerte) controleren
          </button>
        ) : (
          <button onClick={runAiContactData} disabled={aiBusy} style={aiBusy ? buttonDisabledStyle : buttonStyle}>
            Contactpersoon gegevens
          </button>
        )}

        <button onClick={runAiDossierOverview} disabled={aiBusy} style={aiBusy ? buttonDisabledStyle : buttonStyle}>
          Dossier overzicht
        </button>

        <button onClick={runAiChat} disabled={aiBusy} style={aiBusy ? buttonDisabledStyle : buttonStyle}>
          Chat met AI
        </button>
      </div>

      <div style={{ marginBottom: 8, fontWeight: 700 }}>AI venster</div>

      <textarea
        ref={aiTextRef}
        value={aiText}
        onChange={(e) => setAiText(e.target.value)}
        placeholder="Klik op een AI-keuze, of typ hier je vraag voor Chat met AI."
        style={{
          width: "100%",
          flex: 1,
          minHeight: 0,
          padding: 12,
          border: "1px solid #ccc",
          borderRadius: 8,
          fontFamily: "inherit",
          fontSize: 15,
          lineHeight: 1.45,
          boxSizing: "border-box",
          resize: "none",
          whiteSpace: "pre-wrap",
          overflowY: "auto",
        }}
      />
    </div>
  </div>
)}
</>
);
}